/**
 * Textos — `OD-0017 D-07`.
 *
 * V1 es sólo español (`URS §140`), pero los textos viven EXTERNALIZADOS desde
 * el primer día: retrofitar i18n sobre cadenas incrustadas se paga entero más
 * tarde. Ningún componente escribe texto visible; todos leen de aquí.
 *
 * `scripts/check-web-guardrails.mjs` comprueba que sigue siendo así.
 */

/** Un porcentaje de la API, "30,00", como se dice: "30"; "33,30" -> "33,3". Texto, no aritmética. */
function sinCeros(porcentaje: string): string {
  return porcentaje.replace(/,00$/, "").replace(/(,\d)0$/, "$1");
}

// Un recuento con punto de miles: 5000 -> "5.000". Sólo cuentas de filas y
// facturas, nunca importes (esos llegan escritos de la API).
function miles(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export const t = {
  app: {
    // `titulo: "Facturación"` se fue con el <h1> de las pantallas sin sesión:
    // era la CATEGORÍA donde debía ir la MARCA, y la marca ya estaba aquí
    // debajo sin que nadie la usara fuera del panel.
    marca: "Faktelio",
    saltarAContenido: "Saltar al contenido",
    entornoStaging: "Entorno de pruebas",
  },
  conexion: {
    sinConexion: "Sin conexión",
    sinConexionDetalle:
      "No hay red. La aplicación abre, pero no puede consultar ni guardar nada hasta que vuelva la conexión.",
  },
  comprobando: {
    titulo: "Comprobando tu sesión",
    detalle: "Un momento.",
  },
  login: {
    titulo: "Entrar",
    email: "Email",
    password: "Contraseña",
    entrar: "Entrar",
    entrando: "Entrando…",
    faltaEmail: "Escribe tu email.",
    faltaPassword: "Escribe tu contraseña.",
    credencialesIncorrectas: "Email o contraseña incorrectos.",
    demasiadosIntentos: "Demasiados intentos. Prueba de nuevo más tarde.",
    errorGenerico: "No se ha podido completar la operación. Inténtalo de nuevo.",
    sinRed: "Sin conexión: no se puede entrar ahora.",
    crearCuenta: "Crear una cuenta",
    olvidePassword: "He olvidado mi contraseña",
    emailVerificado: "Email verificado. Ya puedes entrar.",
    passwordCambiada: "Contraseña cambiada. Se han cerrado todas tus sesiones: entra con la nueva.",
    // Auditoría del onboarding (2026-10-29, hallazgo 6). Sale con CUALQUIER
    // «incorrectos», exista la cuenta o no: decirlo sólo de las pendientes de
    // confirmar sería un oráculo de qué emails tienen cuenta (OD-0026 D-04,
    // OD-0027 D-04). Por eso pregunta en vez de afirmar.
    sinConfirmarTitulo: "¿Acabas de crear la cuenta? Confirma tu email",
    sinConfirmarDetalle:
      "Hasta que abras el enlace que te enviamos no podrás entrar. Si no lo encuentras, mira en spam o pide otro.",
    reenviarEnlace: "Reenviar enlace",
  },
  // Mínimo de 12 caracteres: OD-0027 D-01 (MIN_PASSWORD_LENGTH en la API). Si
  // la API lo cambia, su mensaje de error manda sobre esta ayuda.
  contrasenaNueva: {
    ayuda: "Mínimo 12 caracteres. No hacen falta mayúsculas ni símbolos: lo que protege es la longitud.",
    faltaPassword: "Escribe una contraseña.",
    // Hallazgo 12: la misma regla que la API, antes de enviar. Si la API la
    // cambia, su mensaje manda (ver ui/contrasena.ts).
    corta: (minimo: number) => `Tiene que tener al menos ${minimo} caracteres.`,
  },
  // Hallazgo 12: ver lo escrito en un campo de contraseña.
  verContrasena: { mostrar: "Mostrar", ocultar: "Ocultar" },
  registro: {
    // Auditoria de conversion (2026-10-27): el alta recuerda lo que se consigue.
    titulo: "Crea tu cuenta gratis",
    // OD-0064 D-11: «30 días» siempre con «sin tarjeta»; nunca «Business gratis» a secas.
    ventajas: [
      "30 días de Business gratis, sin tarjeta.",
      "Después sigues en Free, sin caducidad, o eliges plan.",
      // Auditoría del onboarding (hallazgo 8): al entrar sale el asistente,
      // no el presupuesto. Se cuenta tal cual, con la salida que tiene.
      "Al entrar te guiamos para poder facturar. Si prefieres, pulsa «Seguir más tarde» y prepara antes tu primer presupuesto.",
    ],
    crear: "Crear cuenta",
    creando: "Creando…",
    // OD-0027 D-04: el mismo texto exista o no la cuenta. No se puede saber
    // desde aquí, y no se debe insinuar.
    enviadoTitulo: "Revisa tu correo",
    // Hallazgo 15: a qué dirección. Es la que ha ESCRITO la persona, así que
    // sigue sin decir si había cuenta (OD-0027 D-04), y deja ver una errata.
    enviadoDetalle: (email: string) =>
      `Si ${email} es correcto, te hemos enviado un enlace para confirmarlo. Caduca en 7 días. Mira también en spam.`,
    // Ayuda para el salto al buzon, que es donde mas altas se pierden. Mismo
    // texto exista o no la cuenta: no dice nada que OD-0027 D-04 no diga ya.
    abrirBuzon: "Abre tu correo:",
    buzones: [
      { nombre: "Gmail", url: "https://mail.google.com/" },
      { nombre: "Outlook", url: "https://outlook.live.com/mail/" },
    ],
    noLlega: "¿No te llega en unos minutos? Mira en spam o en promociones, o vuelve a pedirlo: el enlace anterior deja de valer.",
    volverAPedir: "Volver a pedir el enlace",
    // Llega desde «Reenviar enlace» del login: el email ya va escrito.
    reenvioTitulo: "Pide otro enlace",
    reenvioDetalle: "Escribe la contraseña que quieras usar y te mandamos otro enlace. El anterior deja de valer.",
    yaTengoCuenta: "Ya tengo cuenta",
    emailNoValido: "Ese email no parece válido.",
    // El plan elegido en faktelio.com (enlaces/plan-elegido.ts). Cuenta lo que
    // va a pasar, en orden, sin decir que ya se ha pagado nada.
    planElegidoDetalle: "Crea tu cuenta y confirma tu correo. Al entrar irás directamente a contratar tu plan.",
    planElegidoDespues: "Cuando confirmes el correo y entres, irás directamente a contratar el plan que has elegido.",
    // OD-0063 D-08: el enlace de una gestoria. No se dice su nombre: la API no
    // lo cuenta a quien no tiene cuenta, para no dejar probar codigos.
    deAsesoriaTitulo: "Vienes de parte de tu asesoría",
    deAsesoriaDetalle:
      "Crea tu cuenta y tu empresa. Después te preguntaremos si quieres darle acceso de lectura: no se le da nada sin que tú lo digas.",
    // EL DEBER DE INFORMACION ES EN EL PUNTO DE RECOGIDA, no en una pagina
    // a la que haya que llegar: este formulario pide email y contraseña, y
    // aqui se dice que se acepta y donde leerlo.
    //
    // LAS DOS RUTAS TIENEN QUE ESTAR PUBLICADAS para poder enlazarlas, y lo
    // estan desde el 2026-10-13. El invariante I1 de
    // `tests/adversarial/site-alta.mjs` prohibe enlazar desde el punto de
    // recogida un texto que el propio proyecto declara en `draft` -seria
    // mandar a quien se registra a un documento que no esta-, y el I2 exige
    // las DOS el dia que el alta publica se abra.
    legalAntes: "Al crear la cuenta aceptas las",
    legalCondiciones: "condiciones de uso y contratación",
    legalCondicionesUrl: "https://www.faktelio.com/legal/condiciones/",
    legalEntre: "y confirmas haber leído la",
    legalPrivacidad: "política de privacidad",
    legalPrivacidadUrl: "https://www.faktelio.com/legal/privacidad/",
    legalDespues: "de Faktelio.",
  },
  verificar: {
    titulo: "Confirmar tu email",
    // Botón y no petición automática: los filtros de correo abren enlaces para
    // analizarlos, y confirmar tiene que ser un acto de la persona.
    detalle: "Pulsa el botón para confirmar tu email y activar la cuenta.",
    confirmar: "Confirmar mi email",
    confirmando: "Confirmando…",
  },
  recuperar: {
    titulo: "Recuperar la contraseña",
    detalle: "Te enviaremos un enlace para elegir una contraseña nueva.",
    enviar: "Enviar enlace",
    enviando: "Enviando…",
    enviadoTitulo: "Revisa tu correo",
    enviadoDetalle:
      "Si hay una cuenta con ese email, te hemos enviado un enlace. Caduca en 1 hora. Mira también en spam.",
    faltaEmail: "Escribe tu email.",
    volver: "Volver a entrar",
  },
  nuevaContrasena: {
    titulo: "Elegir una contraseña nueva",
    detalle: "Al cambiarla se cerrarán todas tus sesiones abiertas, en todos tus dispositivos.",
    password: "Contraseña nueva",
    guardar: "Guardar contraseña",
    guardando: "Guardando…",
  },
  enlace: {
    noValido: "El enlace no es válido, ya se ha usado o ha caducado. Pide uno nuevo.",
    pedirOtroRegistro: "Volver a crear la cuenta",
    pedirOtraRecuperacion: "Pedir otro enlace",
  },
  comun: {
    demasiadosIntentos: "Demasiados intentos. Prueba de nuevo más tarde.",
    errorGenerico: "No se ha podido completar la operación. Inténtalo de nuevo.",
    sinRed: "Sin conexión: no se puede hacer ahora.",
    volver: "Volver",
    // RC-0009: los planes de pago no tienen tope de unidades. Donde iba un
    // número va esto, y la barra de progreso desaparece: una barra sin máximo
    // no significa nada.
    sinLimite: "Sin límite",
  },
  mfa: {
    titulo: "Verificación en dos pasos",
    detalle: "Escribe el código de tu aplicación de autenticación.",
    codigo: "Código",
    verificar: "Verificar",
    verificando: "Verificando…",
    faltaCodigo: "Escribe el código.",
    codigoIncorrecto: "El código no es correcto.",
    caducado: "El inicio de sesión ha caducado. Vuelve a introducir tu contraseña.",
    volver: "Volver",
  },
  sesion: {
    titulo: "Tu sesión",
    organizacion: "Organización",
    usuario: "Usuario",
    capacidades: "Permisos",
    salir: "Salir",
    saliendo: "Saliendo…",
    cargando: "Cargando tu sesión…",
    error: "No se ha podido cargar tu sesión.",
    reintentar: "Reintentar",
    sinPermiso: "Tu usuario no tiene permiso para ver esto. Pídeselo a tu administrador.",
    limite: "Has alcanzado un límite de tu plan.",
    vacio: "Todavía no hay nada que mostrar aquí.",
  },
  buscador: {
    etiqueta: "Buscar clientes, productos, presupuestos y facturas",
    marcador: "Buscar cliente, producto, presupuesto o factura…",
    atajo: "Ctrl+K",
    cargando: "Buscando…",
    sinResultados: "No hay nada que coincida.",
    encontrados: (n: number) => (n === 1 ? "1 resultado" : `${n} resultados`),
    tipos: { cliente: "Cliente", producto: "Producto", presupuesto: "Presupuesto", factura: "Factura" },
  },
  panel: {
    navegacion: "Navegación principal",
    inicio: "Inicio",
    emisor: "Datos fiscales",
    autorizacion: "Autorización AEAT",
    clientes: "Clientes",
    productos: "Productos",
    // CRM V1 de Business (OD-0048 D-07).
    oportunidades: "Oportunidades",
    // Tareas y actividades del CRM de Business (OD-0050 D-07).
    tareas: "Tareas",
    // Informes fiscales de Business (OD-0041).
    informes: "Informes fiscales",
    // OD-0051: submenu de Facturas en el lateral.
    cobrosPendientes: "Cobros pendientes",
    presupuestos: "Presupuestos",
    seguridad: "Seguridad",
    configuracion: "Configuración",
    cuenta: "Mi cuenta",
    // OD-0060: quien entra en la empresa. Solo con users.manage (OWNER y ADMIN).
    equipo: "Equipo",
    plan: "Plan y pago",
    // OD-0063: el programa para gestorías. Solo sale con el programa encendido.
    asesoria: "Asesoría",
    // OD-0063 D-06: el panel de la gestoria, en su propio grupo del menu.
    empresasCliente: "Empresas cliente",
    grupoAsesoria: "Asesoría",
    grupoMenu: "Menú",
    grupoPreferencias: "Preferencias",
    primerosPasos: "Primeros pasos",
    configuracionSecciones: "Secciones de configuración",
    tuEmpresa: "Tu empresa",
    navegacionMovil: "Navegación en el móvil",
    nuevoPresupuesto: "Nuevo presupuesto",
    // La etiqueta del boton grande del movil. Es `aria-label`: el boton solo
    // enseña un «+», y quien usa lector de pantalla necesita saber QUE crea.
    nuevaFactura: "Nueva factura",
    mas: "Más",
    facturas: "Facturas",
    // Facturas de compra y proveedores (OD-0054).
    compras: "Compras",
    soporte: "Soporte",
    // Nombre visible del plan por el prefijo de su versión (FREE_V1 -> FREE).
    planes: {
      FREE: "Plan Gratis",
      FACTURA_PLUS: "Plan Factura+",
      BUSINESS: "Plan Business",
    },
    // Cambio de plan a la vista (propietario, 2026-10-19). Sin precios: el
    // importe lo enseña la pantalla de pago de Stripe, que es la única que lo
    // tiene de verdad (ver PantallaPlan). Las frases son las de faktelio.com.
    mejora: {
      titulo: "Mejora tu plan",
      region: "Cambiar de plan",
      nombre: {
        FACTURA_PLUS: "Factura+",
        BUSINESS: "Business",
      },
      pasarA: {
        FACTURA_PLUS: "Pasar a Factura+",
        BUSINESS: "Pasar a Business",
      },
      lema: {
        FACTURA_PLUS: "Sin límites de volumen y con tu logo.",
        BUSINESS: "Oportunidades, cobros, gestoría e informes.",
      },
    },
    /*
      OD-0064 D-07: la franja de la prueba, todos los dias. Nunca "Business
      gratis" a secas ni nada que suene a que se cobra al final (D-11): no se
      cobra nada, y si no elige, sigue en Free.
    */
    prueba: {
      region: "Prueba de Business",
      /*
        Auditoría del onboarding (hallazgo 9): el día del alta decía «Te quedan
        31 días» junto a un alta que prometía 30. Las dos cosas eran ciertas
        -OD-0064 D-03: el día del alta no cuenta, y `diasRestantes` incluye
        hoy-, pero leídas juntas parecían un error. Se cuenta hoy aparte.
      */
      quedan: (dias: number, ultimoDia: string) =>
        dias <= 1
          ? `Estás probando Business. Hoy es el último día (${ultimoDia}).`
          : dias === 2
            ? `Estás probando Business. Te queda hoy y 1 día más (hasta el ${ultimoDia}).`
            : `Estás probando Business. Te quedan hoy y ${dias - 1} días más (hasta el ${ultimoDia}).`,
      despues: "Si no eliges plan, sigues en Free, gratis y sin caducidad. No se te cobra nada.",
      elegir: (clientes: number, limite: number) =>
        `Tienes ${clientes} clientes y Free admite ${limite}. Si pasas a Free, elige cuáles se quedan; los demás se archivan y vuelven si contratas un plan.`,
      elegirPlan: "Elegir plan",
    },
  },
  avisos: {
    titulo: "Avisos",
    boton: (n: number) => (n === 0 ? "Avisos: ninguno" : n === 1 ? "Avisos: 1 pendiente" : `Avisos: ${n} pendientes`),
    cargando: "Comprobando…",
    ninguno: "Todo en orden. No tienes avisos pendientes.",
    emisorSinDatos: "Faltan los datos fiscales de tu empresa. Sin ellos no podrás emitir facturas.",
    emisorSinConfirmar: "Tus datos fiscales están sin confirmar. Revísalos antes de emitir facturas.",
    // OD-0035: confirmados de antes, pero sin la respuesta del SII.
    emisorSinSii: "Falta que nos digas si llevas el SII. Sin esa respuesta no podrás emitir facturas.",
    plazasLlenas: (total: number) => `Has ocupado tus ${total} plazas de cliente. Para añadir otro, libera una plaza.`,
    cuotaLlena: (nombre: string, limite: number, reinicio: string | null) =>
      `Has llegado al máximo de ${nombre} (${limite})${reinicio === null ? "." : `. Se reinicia el ${reinicio}.`}`,
    cuotaCerca: (nombre: string, usados: number, limite: number, reinicio: string | null) =>
      `Llevas ${usados} de ${limite} ${nombre}${reinicio === null ? "." : `. Se reinicia el ${reinicio}.`}`,
    nombres: {
      presupuestosMes: "presupuestos este mes",
      presupuestosAbiertos: "presupuestos abiertos",
      productos: "productos",
    },
    remisionPendientes: (n: number) =>
      n === 1
        ? "Queda 1 factura por enviar a la Agencia Tributaria. Se envía sola en unos minutos."
        : `Quedan ${n} facturas por enviar a la Agencia Tributaria. Se envían solas en unos minutos.`,
    remisionIncidencia: (n: number) =>
      n === 1
        ? "No hemos podido enviar 1 factura a la Agencia Tributaria. Lo reintentamos solos; tus facturas siguen siendo válidas y puedes seguir facturando."
        : `No hemos podido enviar ${n} facturas a la Agencia Tributaria. Lo reintentamos solos; tus facturas siguen siendo válidas y puedes seguir facturando.`,
    // OD-0053 D-07, pieza 5: el IVA de un anticipo se devenga al cobrarlo.
    anticiposSinEmitir: (n: number, desde: string | null) =>
      n === 1
        ? `Tienes un anticipo sin facturar${desde === null ? "" : ` desde el ${desde}`}. Emítelo ya: el IVA se devenga el día que cobras.`
        : `Tienes ${n} anticipos sin facturar${desde === null ? "" : `, el más antiguo desde el ${desde}`}. Emítelos ya: el IVA se devenga el día que cobras.`,
    remisionRechazos: (n: number) =>
      n === 1
        ? "La Agencia Tributaria ha rechazado 1 factura. Escríbenos y lo resolvemos contigo."
        : `La Agencia Tributaria ha rechazado ${n} facturas. Escríbenos y lo resolvemos contigo.`,
    mfaSinActivar: "Protege tu cuenta: activa la verificación en dos pasos.",
    // OD-0054 D-05: el anticipo pagado al proveedor sin su factura todavía.
    // OD-0048-ENMIENDA-02 F-02: se cuenta al abrir la aplicación, sin correo ni avisos push.
    tareasVencidas: (n: number) => (n === 1 ? "Tienes 1 tarea vencida." : `Tienes ${n} tareas vencidas.`),
    // OD-0062 E-11: también las de hoy y las facturas vencidas, al abrir la aplicación.
    tareasHoy: (n: number) => (n === 1 ? "Tienes 1 tarea para hoy." : `Tienes ${n} tareas para hoy.`),
    facturasVencidas: (n: number, importe: string) =>
      n === 1 ? `1 factura vencida: ${importe} € por cobrar.` : `${n} facturas vencidas: ${importe} € por cobrar.`,
    pagosSinFactura: (n: number, tarde: number) =>
      (n === 1 ? "Tienes 1 pago a un proveedor sin su factura. Sin ella no puedes deducir el IVA." : `Tienes ${n} pagos a proveedores sin su factura. Sin ella no puedes deducir el IVA.`) +
      (tarde === 0 ? "" : tarde === 1 ? " Uno ya va tarde." : ` ${tarde} ya van tarde.`),
    /**
     * El recordatorio que sustituye al muro (OD-0039 E-11): la puerta se
     * puede cerrar, así que lo que falta tiene que seguir viéndose en algún
     * sitio. Aquí, con el resto de lo pendiente.
     */
    autorizacionPendiente: "Autorízanos ante la Agencia Tributaria. Hasta entonces no podrás emitir facturas.",
    // OD-0039 E-14: ya lo has hecho, y la AEAT lo esta comprobando. No es «Hecho».
    autorizacionComprobando:
      "Estamos comprobando tu autorización ante la Agencia Tributaria. Suele quedar lista antes del final del siguiente día laborable.",
    pocosCodigos: (n: number) =>
      n === 1 ? "Te queda 1 código de recuperación. Genera códigos nuevos." : `Te quedan ${n} códigos de recuperación. Genera códigos nuevos.`,
    enviados: (n: number) =>
      n === 1 ? "Tienes 1 presupuesto enviado esperando respuesta." : `Tienes ${n} presupuestos enviados esperando respuesta.`,
    ir: {
      emisor: "Revisar",
      autorizacion: "Ver los pasos",
      seguridad: "Ir a Seguridad",
      presupuestos: "Ver presupuestos",
      clientes: "Ver clientes",
      productos: "Ver productos",
      facturas: "Ver facturas",
      compras: "Ver pagos",
      tareas: "Ver tareas",
      cobros: "Ver vencidas",
    },
  },
  /** `OD-0036`: la exportación para la gestoría (Business). */
  exportacion: {
    titulo: "Para tu gestoría",
    boton: "Exportar para la gestoría",
    cerrar: "Cerrar",
    // OD-0054-ENMIENDA-01: lo que dice la cabecera según dónde se abre el panel.
    detalles: {
      ventas:
        "Descarga las facturas emitidas de un periodo para mandárselas a tu gestoría: el listado en una hoja de cálculo, o el listado con el PDF de cada factura. Las anuladas también salen, con su estado.",
      compras:
        "Descarga las facturas de proveedores que has anotado en un periodo para mandárselas a tu gestoría, en una hoja de cálculo.",
      todo: "Descarga para tu gestoría las facturas de un periodo: las emitidas a tus clientes y las de tus proveedores.",
    },
    sinPlan: "Tu plan no incluye la exportación para la gestoría. Está disponible en Business. Cada factura se sigue descargando una a una desde su ficha.",
    sinPlanCompras: "Tu plan no incluye la exportación para la gestoría. Está disponible en Business.",
    juntas: "Ventas y compras, juntas",
    completo: "Descargar todo en un Excel",
    ayudaCompleto: "Un solo Excel con dos pestañas: «Facturas», con las que has emitido, y «Recibidas», con las de tus proveedores. Sale siempre en Excel, aunque hayas elegido otro formato.",
    ventas: "Facturas emitidas (ventas)",
    compras: "Facturas de proveedores (compras)",
    recibidas: "Descargar facturas de proveedores",
    ayudaRecibidas: "Las que has anotado en el periodo, por fecha de anotación. Las anuladas no salen.",
    paqueteRecibidas: "Descargar con los adjuntos (ZIP)",
    ayudaPaqueteRecibidas: (max: number, mb: number) =>
      `El paquete lleva el listado en Excel y en CSV y el adjunto de cada compra que lo tenga. Admite hasta ${max} compras y ${mb} MB de adjuntos; si tienes más, descárgalo por meses.`,
    recibidasSinAeat: "El libro de la AEAT de facturas recibidas todavía no está. Para las de proveedores, elige Excel o CSV.",
    verPlanes: "Ver planes",
    periodo: "Periodo",
    porTrimestre: "Por trimestre",
    porFechas: "Entre dos fechas",
    trimestre: "Trimestre",
    // "2027-T1" -> "1.er trimestre de 2027". La API manda el código; aquí sólo se escribe.
    nombreTrimestre: (codigo: string) => {
      const [anio, n] = codigo.split("-T");
      return `${n === "1" ? "1.er" : n === "3" ? "3.er" : `${n}.º`} trimestre de ${anio}`;
    },
    desde: "Desde",
    hasta: "Hasta (incluido)",
    ayudaRango: (dias: number) => `Por fecha de expedición, hasta ${dias} días.`,
    formato: "Formato",
    formatos: {
      xlsx: "Excel (hoja de cálculo)",
      csv: "CSV (texto separado por punto y coma)",
      aeat: "Libro registro de la AEAT (Excel)",
    },
    /*
      Para qué sirve cada uno, en una frase. Lo que dice cada una está
      comprobado contra la documentación de cada programa y de la AEAT
      (OD-0036, revisión 2026-10-18).
    */
    ayudaFormato: {
      xlsx: "Para importar con una plantilla de Excel, como la del importador de a3: tu gestoría la configura una vez y le sirve todos los trimestres.",
      csv: "El mismo listado en texto, para programas que no leen Excel.",
      aeat: "El formato oficial del libro de facturas expedidas: el que se presenta ante un requerimiento y el que importa el servicio Pre303 de la AEAT. Un trimestre sale desde el 1 de enero, como pide la AEAT. Las columnas de actividad (epígrafe del IAE) las completa tu gestoría.",
    },
    listado: "Descargar listado",
    paquete: "Descargar con los PDF (ZIP)",
    preparando: "Preparando…",
    ayudaPaquete: (max: number) => `El paquete con los PDF admite hasta ${max} facturas. Si tienes más, descárgalo por meses.`,
    // OD-0063 D-10f: los libros de IVA para ContaSOL.
    contasol: "Para ContaSOL",
    contasolDigitos: "Dígitos de las cuentas",
    contasolPrefijoClientes: "Prefijo de clientes",
    contasolPrefijoProveedores: "Prefijo de proveedores",
    contasolPrimerCodigo: "Primer código de registro",
    contasolDescargar: "Descargar para ContaSOL (ZIP)",
    ayudaContasol:
      "Los libros de IVA de ventas y compras en el formato de importación de ContaSOL. Cada cliente y proveedor tiene siempre la misma cuenta. Lo que no se puede pasar sin revisarlo va en una lista dentro del ZIP.",
    error: "No se ha podido descargar. Vuelve a intentarlo.",
  },
  /**
   * Lo que el cliente le dice a su gestoría (`OD-0067` D-02 y D-03): una nota y
   * una etiqueta por factura o compra, y un comentario por trimestre. Viajan en
   * el Excel de la exportación; la factura no se toca.
   */
  notaAsesoria: {
    titulo: "Nota para la asesoría",
    explicacion:
      "Lo que quieras decirle a tu gestoría de este documento. Sale en el Excel y el CSV que le mandas, en las columnas «Etiqueta» y «Nota para la asesoría». No cambia la factura ni llega a la AEAT.",
    etiqueta: "Etiqueta",
    ayudaEtiqueta: "Una palabra para filtrar en la hoja de cálculo, como «efectivo» u «otra actividad». Hasta 40 caracteres.",
    nota: "Nota",
    ayudaNota: "Hasta 1.000 caracteres.",
    guardar: "Guardar la nota",
    guardando: "Guardando…",
    guardada: "Nota guardada. Saldrá en la próxima exportación.",
    borrada: "Nota borrada.",
    soloLectura: "Esta nota la escribe quien lleva la facturación de la empresa.",
    sinNota: "Sin nota para la asesoría.",
    actualizada: (dia: string) => `Última vez cambiada el ${dia}.`,
    sinPlan: "Las notas para la asesoría van con la exportación para la gestoría, del plan Business.",
    error: "No se ha podido guardar. Vuelve a intentarlo.",
    errores: {
      TOO_LONG: "Es demasiado largo.",
      MULTILINE: "La etiqueta va en una sola línea.",
      NOT_TEXT: "Escribe un texto.",
    } as Readonly<Record<string, string>>,
    comentarioTitulo: "Comentario del trimestre",
    comentarioEtiqueta: "Para tu gestoría",
    comentarioAyuda: (trimestre: string) =>
      `Lo que tu gestoría tiene que saber del ${trimestre}. Va en una hoja del Excel y en el paquete ZIP, solo cuando exportas el trimestre entero. Hasta 2.000 caracteres.`,
    guardarComentario: "Guardar el comentario",
    comentarioGuardado: "Comentario guardado.",
    comentarioBorrado: "Comentario borrado.",
  },
  /** `OD-0067` D-05 y D-06: la conversación de la empresa con su gestoría. */
  conversacionAsesoria: {
    titulo: (conQuien: string | null) => (conQuien === null ? "Conversación con la asesoría" : `Conversación con ${conQuien}`),
    tituloDelDocumento: "Hablarlo con tu asesoría",
    vacia: "Todavía no hay mensajes. Escribe aquí las dudas rápidas: quedan junto a las facturas de las que habláis.",
    vaciaDelDocumento: "Nada sobre este documento todavía.",
    tu: "Tú",
    laEmpresa: "La empresa",
    tuAsesoria: "Tu asesoría",
    sobre: (tipo: "factura" | "compra", numero: string) => (tipo === "factura" ? `Sobre la factura ${numero}` : `Sobre la compra ${numero}`),
    retirado: "Mensaje retirado",
    retirar: "Retirar",
    confirmarRetirar: "¿Retirar este mensaje? Se borra lo que decía y queda «Mensaje retirado».",
    escribir: "Mensaje",
    escribirDelDocumento: (tipo: "factura" | "compra", numero: string) =>
      tipo === "factura" ? `Mensaje sobre la factura ${numero}` : `Mensaje sobre la compra ${numero}`,
    ayuda: "Solo texto, hasta 2.000 caracteres. Para mandar un fichero, usa «Mandar por correo».",
    enviar: "Enviar",
    enviando: "Enviando…",
    mandarPorCorreo: "Mandar por correo",
    ayudaCorreo: (a: string) => `Abre tu correo con ${a} ya puesto. El fichero lo adjuntas tú: Faktelio no lo ve ni lo guarda.`,
    asuntoDeLaEmpresa: (numero: string | null) => (numero === null ? "Para mi asesoría" : `Para mi asesoría: ${numero}`),
    asuntoDeLaGestoria: (empresa: string | null) => (empresa === null ? "De tu asesoría" : `De tu asesoría, para ${empresa}`),
    cuerpoDelCorreo: (origen: string) => `Te adjunto lo que hablamos en la conversación de Faktelio:\n\n${origen}\n`,
    noSeHaEnviado: "No se ha podido enviar. Vuelve a intentarlo.",
    noSeHaRetirado: "No se ha podido retirar. Vuelve a intentarlo.",
    avisoInicio: (n: number) => (n === 1 ? "Tu asesoría te ha escrito un mensaje." : `Tu asesoría te ha escrito ${n} mensajes.`),
    abrir: "Abrir la conversación",
    enElPanel: (n: number | null) => (n === null || n === 0 ? "Conversación" : `Conversación (${n} sin leer)`),
    cerrar: "Cerrar la conversación",
    errores: {
      REQUIRED: "Escribe un mensaje.",
      TOO_LONG: "Es demasiado largo: hasta 2.000 caracteres.",
      NOT_TEXT: "Escribe un texto.",
    } as Readonly<Record<string, string>>,
  },
  /** Registro de cobros de Business (`OD-0040`). Los importes llegan de la API ya sumados. */
  cobros: {
    titulo: "Cobros",
    sinPlan: "Apuntar lo que te pagan por cada factura y ver lo pendiente es del plan Business.",
    verPlanes: "Ver planes",
    aCobrar: "Total a cobrar",
    cobrado: "Cobrado",
    pendiente: "Pendiente",
    // La retención: lo que el cliente ingresa no es el total de la factura (OD-0040 D-02).
    ayudaACobrar: "Es lo que te ingresa el cliente: el total menos la retención, más los suplidos, si los hay.",
    estados: {
      UNPAID: "Sin cobrar",
      PARTIALLY_PAID: "Cobro parcial",
      PAID: "Cobrada",
    } as Record<string, string>,
    metodos: {
      TRANSFERENCIA: "Transferencia",
      TARJETA: "Tarjeta",
      EFECTIVO: "Efectivo",
      DOMICILIACION: "Domiciliación",
      BIZUM: "Bizum",
      OTRO: "Otro",
    } as Record<string, string>,
    bloqueos: {
      NOT_ISSUED: "Los cobros se apuntan cuando la factura está emitida.",
      INVOICE_VOIDED: "La factura está anulada: sus cobros se conservan y ya no cuenta como pendiente.",
      RECTIFICATIVE:
        "Los cobros se apuntan en la factura original. Si esta rectificativa rebaja lo que ya te pagaron, la diferencia se devuelve o se compensa fuera de Faktelio.",
      NOTHING_TO_COLLECT: "Esta factura no tiene nada que cobrar.",
      TOTALS_UNREADABLE: "No se pueden leer los importes de esta factura. Escríbenos a soporte.",
      FULLY_PAID: "Factura cobrada entera.",
    } as Record<string, string>,
    sinCobros: "Todavía no has apuntado ningún cobro.",
    lista: "Cobros apuntados",
    anulado: "Anulado",
    anuladoPor: (motivo: string) => `Anulado: ${motivo}`,
    nuevo: "Apuntar un cobro",
    importe: "Importe (€)",
    ayudaImporte: "Con coma para los céntimos, por ejemplo 1250,50 o 1.250,50.",
    fechaCobro: "Fecha en que te pagaron",
    metodo: "Forma de pago",
    nota: "Nota (opcional)",
    ayudaNota: "Por ejemplo, la referencia de la transferencia.",
    registrar: "Apuntar cobro",
    registrando: "Apuntando…",
    registrado: "Cobro apuntado.",
    anular: "Anular",
    motivoAnular: "Por qué lo anulas",
    confirmarAnular: "Anular el cobro",
    anulando: "Anulando…",
    cobroAnulado: "Cobro anulado. Sigue en la lista para que quede constancia.",
    cancelar: "Cancelar",
    sinPermiso: "Tu rol puede ver los cobros, pero no apuntarlos ni anularlos.",
    sobrepago: (pendiente: string) => `Es más de lo que queda por cobrar (${pendiente} €).`,
    yaAnulado: "Ese cobro ya estaba anulado.",
    error: "No se ha podido guardar. Vuelve a intentarlo.",
    errores: {
      REQUIRED: "Es obligatorio.",
      INVALID_NUMBER: "Escribe un importe con coma para los céntimos, como 1250,50 o 1.250,50.",
      TOO_MANY_DECIMALS: "Como mucho, dos decimales.",
      NOT_POSITIVE: "Tiene que ser mayor que cero.",
      OUT_OF_RANGE: "Es demasiado grande.",
      INVALID_DATE: "No es una fecha válida.",
      FUTURE_DATE: "Un cobro no puede tener fecha futura.",
      NOT_ALLOWED: "Elige una de la lista.",
      TOO_LONG: "Es demasiado largo.",
      INVALID_CHARACTERS: "Tiene caracteres que no se admiten.",
    } as Record<string, string>,
    // La lista de facturas.
    pendienteTotal: (importe: string, n: number) => `Pendiente de cobro: ${importe} € en ${n} ${n === 1 ? "factura" : "facturas"}`,
    cobradoMes: (importe: string) => `Cobrado este mes: ${importe} €`,
    queda: (importe: string) => `quedan ${importe} €`,
    verPendientes: "Ver cobros pendientes",
  },
  // OD-0051: la lista de cobros pendientes, dentro de Facturas.
  pendientes: {
    titulo: "Cobros pendientes",
    volver: "Volver a facturas",
    cargando: "Cargando lo pendiente…",
    error: "No se ha podido cargar lo pendiente. Vuelve a intentarlo.",
    reintentar: "Reintentar",
    sinPermiso: "Tu rol no puede ver los cobros.",
    soloBusinessTitulo: "Cobros pendientes es del plan Business",
    soloBusiness: "Con Business apuntas lo que te pagan por cada factura y ves aquí lo que te deben, con lo vencido arriba.",
    verPlanes: "Ver planes",
    aFecha: (dia: string) => `A fecha de ${dia}`,
    total: (importe: string, n: number) => `${importe} € pendientes en ${n} ${n === 1 ? "factura" : "facturas"}`,
    filtroEstado: "Qué facturas ver",
    estados: {
      PENDING: "Todo lo pendiente",
      OVERDUE: "Vencidas",
      PARTIAL: "Cobradas en parte",
    } as Record<string, string>,
    cliente: "Cliente",
    todosLosClientes: "Todos los clientes",
    desde: "Emitidas desde",
    hasta: "Emitidas hasta",
    limpiar: "Quitar filtros",
    antiguedad: "Antigüedad de lo pendiente",
    tramos: {
      NOT_DUE: "Sin vencer",
      DAYS_1_30: "1 a 30 días",
      DAYS_31_60: "31 a 60 días",
      DAYS_61_90: "61 a 90 días",
      DAYS_OVER_90: "Más de 90 días",
      NO_DUE_DATE: "Sin vencimiento",
    } as Record<string, string>,
    tramoFacturas: (n: number) => `${n} ${n === 1 ? "factura" : "facturas"}`,
    vacio: "No te debe nada nadie. Cuando emitas una factura y no esté cobrada entera, aparecerá aquí.",
    vacioFiltro: "Ninguna factura pendiente con estos filtros.",
    sinNumero: "Sin número",
    emitida: (dia: string) => `Emitida el ${dia}`,
    vence: (dia: string) => `vence el ${dia}`,
    sinVencimiento: "sin vencimiento",
    retraso: (dias: number) => `${dias} ${dias === 1 ? "día" : "días"} de retraso`,
    deTotal: (pendiente: string, total: string) => `${pendiente} € de ${total} €`,
    // D-03: sin fecha de vencimiento no se inventa un plazo.
    ayudaSinVencimiento:
      "Las facturas sin fecha de vencimiento no cuentan como vencidas. Pon el vencimiento al crear la factura, o el plazo de pago en la ficha del cliente, para verlas en su tramo.",
    // OD-0062 E-02: sin vencimiento, cuánto llevan emitidas. Lo suma la API.
    desdeEmision: (dias: number) => (dias === 1 ? "emitida hace 1 día" : `emitida hace ${dias} días`),
    sinVencimientoAntiguas: (importe: string, n: number, dias: number) =>
      `${importe} € sin vencimiento emitidos hace más de ${dias} días (${n} ${n === 1 ? "factura" : "facturas"}).`,
    // OD-0062 E-05: lo filtrado, en Excel.
    descargar: "Descargar Excel",
    descargando: "Preparando el Excel…",
    errorDescarga: "No se ha podido preparar el Excel. Vuelve a intentarlo.",
    // OD-0062 E-04: desde la fila, sin entrar en la factura.
    apuntarCobro: "Apuntar cobro",
    cerrarCobro: "Cerrar",
    recordarBoton: "Recordar",
    recordar: {
      detalle: "Se abre tu correo o WhatsApp con el recordatorio escrito, para que se lo mandes tú. Faktelio no envía nada.",
      preparando: "Preparando el recordatorio…",
      asunto: (numero: string, emisor: string) => (emisor === "" ? `Recordatorio: factura ${numero}` : `Recordatorio: factura ${numero} de ${emisor}`),
      cuerpo: (numero: string, pendiente: string, situacion: string, emisor: string) =>
        `Hola:\n\nTe escribo por la factura ${numero}${situacion === "" ? "" : `, ${situacion}`}: quedan ${pendiente} € pendientes de pago.\n\nSi ya la has pagado, no hagas caso de este mensaje.\n\nUn saludo${emisor === "" ? "" : `,\n${emisor}`}`,
      vencida: (dias: number) => (dias === 1 ? "que venció hace 1 día" : `que venció hace ${dias} días`),
      vence: (dia: string) => `que vence el ${dia}`,
      emitida: (dia: string) => `emitida el ${dia}`,
      actividad: (numero: string) => `Correo: recordatorio de ${numero}`,
      apuntado: "Queda apuntado en las tareas de la factura.",
      whatsappAl: (numero: string) => `WhatsApp va al ${numero} de su ficha.`,
      whatsappSinNumero: "Por WhatsApp eliges tú el contacto: su ficha no tiene un móvil con prefijo.",
      cerrar: "Cerrar",
    },
    errores: {
      INVALID_DATE: "No es una fecha válida.",
      BEFORE_START: "Tiene que ser posterior a la fecha de inicio.",
    } as Record<string, string>,
  },
  // OD-0054: facturas de compra, proveedores y pagos a proveedores (Business para anotar).
  compras: {
    titulo: "Compras",
    secciones: "Secciones de compras",
    pestanas: {
      recibidas: "Facturas recibidas",
      presupuestos: "Presupuestos",
      pendientes: "Pagos pendientes",
      proveedores: "Proveedores",
    },
    cargando: "Cargando…",
    error: "No se ha podido completar. Vuelve a intentarlo.",
    reintentar: "Reintentar",
    sinConexion: "Sin conexión: no se puede guardar nada hasta que vuelva.",
    sinPermiso: "Tu rol no puede ver las compras.",
    sinPermisoEscribir: "Tu rol puede ver las compras, pero no anotarlas ni cambiarlas.",
    soloLecturaRol: "Tu rol puede ver las compras, pero no anotarlas ni cambiarlas.",
    soloBusinessTitulo: "Anotar compras es del plan Business",
    soloBusiness:
      "Con Business anotas las facturas de tus proveedores, sus pagos y los anticipos, con la foto o el PDF. Lo que ya anotaste se sigue viendo en cualquier plan.",
    verPlanes: "Ver planes",
    volver: "Volver a compras",
    volverProveedores: "Volver a compras",
    cancelar: "Cancelar",
    guardar: "Guardar",
    guardando: "Guardando…",
    revisa: "Revisa los campos marcados.",
    // Lista de recibidas.
    filtroEstado: "Qué facturas ver",
    filtros: {
      ALL: "Todas",
      PENDING: "Por pagar",
      OVERDUE: "Vencidas",
      PAID: "Pagadas",
      VOIDED: "Anuladas",
    },
    buscarCompra: "Buscar por número, proveedor, NIF o referencia",
    filtroTipo: "Tipo",
    tiposFiltro: {
      ALL: "Todas",
      ORDINARY: "Ordinarias",
      ADVANCE: "De anticipo",
    },
    filtroFecha: "Fechas de",
    fechasFiltro: {
      ISSUE: "Emisión",
      DUE: "Vencimiento",
    },
    desde: "Desde",
    hasta: "Hasta",
    limpiar: "Quitar filtros",
    anotar: "Anotar factura",
    vencida: "Vencida",
    accionesDe: (numero: string) => `Acciones de ${numero}`,
    descargarFila: "Descargar adjunto",
    apuntarPagoFila: "Apuntar pago",
    // OD-0055 D-09: de qué presupuesto viene cada factura.
    dePresupuesto: (numero: string) => `presupuesto ${numero}`,
    verPresupuesto: "Ver presupuesto",
    soloDelPresupuesto: (numero: string) => `Solo las facturas del presupuesto ${numero}.`,
    verTodas: "Ver todas",
    resumenLista: (total: string, pendiente: string) => `Total: ${total} € · Por pagar: ${pendiente} €`,
    vacioTitulo: "Todavía no has anotado ninguna factura de compra",
    vacio: "Anota las facturas de tus proveedores: su IVA, lo que te queda por pagar y los anticipos, con la foto o el PDF guardados.",
    vacioFiltro: "Ninguna factura cumple estos filtros.",
    suFactura: (numero: string) => `su factura ${numero}`,
    anulada: "Anulada",
    deTotal: (pendiente: string, total: string) => `quedan ${pendiente} € de ${total} €`,
    tipos: {
      ORDINARY: "Factura ordinaria",
      ADVANCE: "Factura de anticipo",
    },
    estadosPago: {
      UNPAID: "Sin pagar",
      PARTIALLY_PAID: "Pago parcial",
      PAID: "Pagada",
    },
    // Pagos pendientes.
    aFecha: (dia: string) => `A ${dia}`,
    totalPendiente: (pendiente: string, vencido: string) => `Por pagar: ${pendiente} € · Vencido: ${vencido} €`,
    porPagarTitulo: "Facturas por pagar",
    nadaPendiente: "No tienes ninguna factura de compra por pagar.",
    vence: (dia: string) => `Vence el ${dia}`,
    sinVencimiento: "Sin vencimiento",
    retraso: (dias: number) => (dias === 1 ? "1 día vencida" : `${dias} días vencida`),
    // D-05: el pago hecho antes de tener la factura.
    sinFacturaTitulo: "Pagos sin factura",
    sinFacturaAyuda: "Pagos adelantados a un proveedor que todavía no te ha dado la factura. Sin ella no puedes deducir el IVA.",
    pagadoEl: (dia: string) => `Pagado el ${dia}`,
    facturaTarde: "Tu proveedor tenía que habértela enviado antes del día 16",
    facturaAntesDe: (dia: string) => `Factura antes del ${dia}`,
    faltaFactura: "Falta la factura de este pago. Sin ella no puedes deducir el IVA.",
    proveedorTarde: "Tu proveedor tenía que habértela enviado antes del día 16.",
    pagoSinFacturaLinea: (importe: string, dia: string) => `${importe} € pagados el ${dia}.`,
    delPresupuesto: (numero: string) => `Del presupuesto ${numero}.`,
    comoAsignar: "Cuando llegue, pulsa «Anotar factura» y elige este pago: queda asignado y el aviso se va.",
    // Proveedores.
    filtroProveedores: "Qué proveedores ver",
    activos: "Activos",
    archivados: "Archivados",
    archivado: "Archivado",
    buscarProveedor: "Buscar por nombre o NIF",
    nuevoProveedor: "Nuevo proveedor",
    crearProveedor: "Guardar proveedor",
    sinProveedores: "Todavía no tienes proveedores. Se crean solos al anotar su primera factura, o aquí.",
    sinArchivados: "No tienes proveedores archivados.",
    sinCoincidencias: "Ningún proveedor coincide con la búsqueda.",
    pendienteDe: (importe: string) => `por pagar ${importe} €`,
    pagosSinFactura: (n: number) => (n === 1 ? "1 pago sin factura" : `${n} pagos sin factura`),
    nif: "NIF del proveedor",
    ayudaNif: "El que aparece en su factura. Si ya lo tienes dado de alta, se usa ese proveedor.",
    nombre: "Nombre o razón social",
    correo: "Correo (opcional)",
    notas: "Notas (opcional)",
    nifNoCambia: "El NIF no se cambia: si es otro, da de alta otro proveedor.",
    editar: "Editar",
    archivar: "Archivar",
    recuperar: "Recuperar",
    proveedorCreado: "Proveedor guardado.",
    yaExistia: "Ya tenías un proveedor con ese NIF: es este.",
    proveedorGuardado: "Cambios guardados.",
    proveedorArchivado: "Proveedor archivado. Sus facturas y pagos se conservan.",
    proveedorRecuperado: "Proveedor recuperado.",
    proveedorNoEncontrado: "No se ha encontrado el proveedor.",
    facturasDelProveedor: "Sus facturas",
    sinFacturasProveedor: "Todavía no le has anotado ninguna factura.",
    todosLosPagos: "Todos sus pagos",
    sinFacturaCorto: "sin factura",
    conFactura: "con factura",
    presupuestoDelPago: "De qué presupuesto es (opcional)",
    ayudaPresupuestoPago: "Si es el anticipo de un presupuesto aceptado, elígelo. No se cambia después.",
    apuntarPagoSinFactura: "Apuntar pago sin factura",
    ayudaPagoSinFactura: "Para el anticipo que pagas antes de tener la factura. Cuando llegue la factura de anticipo, se lo asignas.",
    apuntarPago: "Apuntar pago",
    pagoSinFacturaAnotado: "Pago apuntado. Queda pendiente de su factura.",
    // El formulario.
    anotarTitulo: "Anotar factura de compra",
    corregirTitulo: (numero: string) => `Corregir ${numero}`,
    proveedor: "Proveedor",
    proveedorNuevo: "Proveedor nuevo",
    tieneSinFactura: (n: number) =>
      n === 1 ? "Este proveedor tiene 1 pago sin factura: puedes asignárselo abajo." : `Este proveedor tiene ${n} pagos sin factura: puedes asignárselos abajo.`,
    tipo: "Tipo de factura",
    ayudaOrdinaria: "La normal. Si es la final de un trabajo con anticipo, marca abajo el anticipo que descuenta.",
    ayudaAnticipo: "La que te hace el proveedor por un pago adelantado, antes de terminar el trabajo.",
    numero: "Número de su factura",
    emision: "Fecha de la factura",
    operacion: "Fecha de la operación (opcional)",
    ayudaOperacion: "Solo si la factura dice una distinta de la de expedición.",
    vencimiento: "Vencimiento (opcional)",
    concepto: "Concepto (opcional)",
    // OD-0055 D-04 y D-08: el presupuesto del que viene y la obra.
    presupuesto: "Presupuesto (opcional)",
    sinPresupuesto: "Sin presupuesto",
    ayudaPresupuesto: "Los presupuestos aceptados de este proveedor. Así se sabe cuánto queda por facturar de la obra.",
    presupuestoElegible: (numero: string, referencia: string | null, porFacturar: string) =>
      `${numero}${referencia === null ? "" : ` · ${referencia}`} · por facturar ${porFacturar} €`,
    referencia: "Referencia de la obra (opcional)",
    ayudaReferencia: "Para distinguir dos trabajos con el mismo proveedor. No va en la exportación para la gestoría.",
    registrada: "Anotada el",
    // En la ficha, sin el «(opcional)» del formulario.
    datos: {
      operacion: "Fecha de la operación",
      vencimiento: "Vencimiento",
      concepto: "Concepto",
      correo: "Correo",
      notas: "Notas",
      referencia: "Referencia",
      presupuesto: "Presupuesto",
    },
    lineas: "Bases e IVA",
    ayudaLineas: "Escribe las bases y cuotas tal como vienen en la factura: una línea por cada tipo de IVA.",
    tratamiento: "IVA",
    tratamientos: {
      SUJETA_NO_EXENTA: "Con IVA",
      SUJETA_EXENTA: "Exenta de IVA",
      NO_SUJETA: "No sujeta a IVA",
    },
    tipoIva: "Tipo",
    porcentaje: (tipo: string) => `${sinCeros(tipo)} %`,
    base: "Base imponible (€)",
    cuota: "Cuota de IVA (€)",
    ayudaCuota: "La de la factura. Si la dejas vacía, se calcula con la base y el tipo.",
    ayudaImporte: "Con coma para los céntimos, por ejemplo 1250,50 o 1.250,50.",
    quitarLinea: "Quitar esta línea",
    anadirLinea: "Añadir otro tipo de IVA",
    retencionTipo: "Retención de IRPF",
    sinRetencion: "Sin retención",
    retencion: "Retención (€)",
    retencionDe: (tipo: string) => `Retención (${tipo})`,
    ayudaRetencion: "La de la factura. Si la dejas vacía, se calcula sobre la base.",
    anticiposQueDescuenta: "Anticipos que descuenta",
    ayudaAnticipos: "Las facturas de anticipo de este proveedor que esta factura final descuenta.",
    enlazada: (registro: string, numero: string, total: string) => `${registro} · su factura ${numero} · ${total} €`,
    pagosQueAsignar: "Pagos que ya le hiciste",
    ayudaPagosAnticipo: "Los pagos sin factura que corresponden a este anticipo: quedan asignados y el aviso se va.",
    ayudaPagos: "Si alguno de estos pagos es de esta factura, márcalo.",
    pagoElegible: (importe: string, dia: string) => `${importe} € pagados el ${dia}`,
    baseTotal: "Base imponible",
    ivaTotal: "IVA",
    total: "Total factura",
    aPagar: "A pagar",
    guardarCorreccion: "Guardar corrección",
    guardada: (numero: string) => `Factura anotada con el número de registro ${numero}.`,
    avisos: {
      cuota: (linea: number, propuesta: string) => `Línea ${linea}: la cuota no es la de base por tipo (${propuesta} €). Se guarda la que has escrito.`,
      retencion: (propuesta: string) => `La retención no es la de base por tipo (${propuesta} €). Se guarda la que has escrito.`,
      qrTotal: (total: string) => `El QR dice que el total es ${total} €, y las líneas suman otro.`,
      qrNumero: (numero: string) => `El QR dice que el número es ${numero}.`,
      qrFecha: (dia: string) => `El QR dice que la fecha es el ${dia}.`,
      qrProveedor: (nif: string) => `El QR es de otro NIF (${nif}): comprueba el proveedor.`,
      otro: "Revisa los datos: algo no coincide.",
    },
    // D-08: el QR, leído en el navegador.
    // OD-0061: la factura se lee en el dispositivo. Antes solo se leía el QR.
    qr: {
      titulo: "Leer la factura",
      ayuda:
        "Haz una foto o elige el PDF: proponemos el proveedor, el número, las fechas, el desglose del IVA y la retención, y leemos el QR de la Agencia Tributaria si lo lleva. Se lee en tu dispositivo: la factura no sale de él para leerla. Solo rellenamos lo que está vacío.",
      leer: "Leer una foto o un PDF",
      leyendo: "Leyendo la factura… La primera vez tarda un poco más.",
      noLeida: "No hemos podido abrir ese archivo para leerlo. Escribe los datos a mano.",
      nadaLeido: "No hemos sabido leer datos de esta factura. Prueba con más luz y más cerca, o escríbelos a mano.",
      rellenado: (campos: readonly string[]) => `Hemos rellenado: ${campos.join(", ")}.`,
      sinDesglose: "El desglose del IVA no lo hemos leído con seguridad: escríbelo tú.",
      revisa: "Revísalo con la factura antes de guardar.",
      campos: {
        proveedor: "proveedor",
        nif: "NIF",
        nombre: "nombre",
        numero: "número",
        fecha: "fecha",
        vencimiento: "vencimiento",
        desglose: "desglose del IVA",
        retencion: "retención",
      },
      noEsAeat: "Ese QR no es el de la Agencia Tributaria: no lo usamos.",
      enLaCompra: "El QR se guarda con la compra.",
      total: (total: string) => `Total según el QR: ${total} €`,
      comprobar: "Comprobar en la AEAT",
      quitar: "Quitar el QR",
      verifactu: "Factura VERI*FACTU: la Agencia Tributaria la tiene registrada.",
      noVerifactu: "Factura de un sistema adaptado, no VERI*FACTU.",
    },
    // D-07: la foto o el PDF.
    adjunto: {
      titulo: "Foto o PDF de la factura",
      ayuda: "Opcional: PDF, JPEG, PNG o WebP, hasta 25 MB. Desde el móvil puedes hacer la foto con la cámara.",
      elegir: "Elegir foto o PDF",
      cambiar: "Elegir otro",
      noAdjuntar: "No adjuntar",
      elegido: (nombre: string) => `Se adjuntará: ${nombre}`,
      fotoCopia:
        "Una foto de una factura en papel es una copia para consultarla: el papel hay que guardarlo. Solo la digitalización certificada lo sustituye (Orden EHA/962/2007).",
      pdfOriginal: "El PDF que te envió el proveedor sí es el original. Se guarda con su huella SHA-256.",
      sinAntivirus: "Faktelio no pasa antivirus a los archivos: solo los ve tu organización.",
      noSubido: "La factura está anotada, pero el archivo no se ha subido: súbelo desde su ficha.",
      resumen: (nombre: string, tamano: string) => `${nombre} · ${tamano}`,
      ninguno: "Sin foto ni PDF.",
      descargar: "Descargar",
      sustituir: "Sustituir",
      quitar: "Quitar el archivo",
      subiendo: "Subiendo…",
      subido: "Archivo guardado.",
      quitado: "Archivo quitado. La compra sigue anotada.",
    },
    // La ficha.
    fichaTitulo: (numero: string) => `Compra ${numero}`,
    anuladaPor: (motivo: string) => `Anulada: ${motivo}`,
    lineaResumen: (base: string, iva: string) => `Base ${base} € · IVA ${iva} €`,
    anticiposDescontados: "Anticipos que descuenta",
    operacionCompleta: (total: string) => `Anticipos descontados + esta factura = operación completa: ${total} €`,
    ayudaOperacionCompleta:
      "Si esta cifra pasa de lo que acordaste, el proveedor ha vuelto a cobrarte el IVA del anticipo: se arregla con una factura rectificativa suya.",
    descontadoEn: "La descuenta la factura final",
    corregir: "Corregir",
    anular: "Anular",
    ayudaAnular: "Anular no borra la compra: se queda con su número y su motivo. Si tiene pagos, anúlalos antes.",
    motivoAnular: "Por qué la anulas",
    confirmarAnular: "Anular la compra",
    anuladaOk: "Compra anulada.",
    noEncontrada: "No se ha encontrado la compra.",
    // Sus pagos.
    pagosTitulo: "Pagos",
    pagado: "Pagado",
    pendiente: "Pendiente",
    pagadaEntera: "Factura pagada entera.",
    sinPagos: "Todavía no has apuntado ningún pago de esta factura.",
    anuladoPor: (motivo: string) => `Anulado: ${motivo}`,
    anularPago: "Anular",
    motivoAnularPago: "Por qué anulas el pago",
    confirmarAnularPago: "Anular el pago",
    pagoAnulado: "Pago anulado. Sigue en la lista para que quede constancia.",
    asignarTitulo: "Asignar un pago sin factura",
    asignarAyuda: "Un pago que hiciste a este proveedor antes de tener la factura.",
    pagoSinFacturaEtiqueta: "Pago",
    asignar: "Asignar a esta factura",
    pagoAsignado: "Pago asignado a esta factura.",
    nuevoPago: "Apuntar un pago",
    importe: "Importe (€)",
    fechaPago: "Fecha del pago",
    metodo: "Forma de pago",
    nota: "Nota (opcional)",
    anotarPago: "Apuntar pago",
    pagoAnotado: "Pago apuntado.",
    errores: {
      REQUIRED: "Es obligatorio.",
      TOO_LONG: "Es demasiado largo.",
      INVALID_CHARACTERS: "Tiene caracteres que no se admiten.",
      INVALID_NUMBER: "Escribe un importe con coma para los céntimos, como 1250,50 o 1.250,50.",
      TOO_MANY_DECIMALS: "Como mucho, dos decimales.",
      NEGATIVE: "No puede ser negativo.",
      NOT_POSITIVE: "Tiene que ser mayor que cero.",
      OUT_OF_RANGE: "Es demasiado grande.",
      INVALID_DATE: "No es una fecha válida.",
      FUTURE_DATE: "No puede ser una fecha futura.",
      BEFORE_ISSUE_DATE: "No puede ser anterior a la fecha de la factura.",
      NOT_ALLOWED: "Ese valor no se admite aquí.",
      INVALID_TAX_ID: "No es un NIF, NIE o CIF español válido.",
      INVALID_EMAIL: "No es un correo válido.",
      NOT_AN_AEAT_QR: "Ese QR no es el de la Agencia Tributaria: quítalo para guardar.",
      TOO_MANY: "Son demasiados.",
      DUPLICATED: "Está repetido.",
    } as Record<string, string>,
    /** Presupuestos de proveedor (`OD-0055`): lo acordado, lo facturado contra él y lo que queda. */
    presupuestos: {
      filtroEstado: "Qué presupuestos ver",
      filtros: {
        ALL: "Todos",
        PENDING: "Pendientes",
        ACCEPTED: "Aceptados",
        PARTIALLY_INVOICED: "Facturado parcial",
        COMPLETED: "Completados",
        CANCELLED: "Cancelados",
      },
      estados: {
        PENDING: "Pendiente",
        ACCEPTED: "Aceptado",
        PARTIALLY_INVOICED: "Facturado parcial",
        COMPLETED: "Completado",
        CANCELLED: "Cancelado",
      },
      buscar: "Buscar por número, proveedor, NIF o referencia",
      tarde: "Con pagos sin factura fuera de plazo",
      anotar: "Anotar presupuesto",
      vacioTitulo: "Todavía no has anotado ningún presupuesto de proveedor",
      vacio: "Anota los presupuestos que te mandan tus proveedores: al aceptarlos verás lo facturado y pagado contra cada uno, y lo que queda.",
      vacioFiltro: "Ningún presupuesto cumple estos filtros.",
      suNumero: (numero: string) => `su número ${numero}`,
      resumenFila: (total: string, pagado: string, porFacturar: string) => `Total ${total} € · pagado ${pagado} € · por facturar ${porFacturar} €`,
      pagosTarde: (n: number) => (n === 1 ? "1 pago sin factura fuera de plazo" : `${n} pagos sin factura fuera de plazo`),
      anotarFactura: "Anotar factura",
      accionesDe: (numero: string) => `Acciones de ${numero}`,
      // El formulario.
      anotarTitulo: "Anotar presupuesto de proveedor",
      corregirTitulo: (numero: string) => `Corregir ${numero}`,
      noFiscal: "Un presupuesto es una anotación para seguir la obra: no va al libro de facturas recibidas ni a la exportación para la gestoría.",
      numero: "Número del proveedor (opcional)",
      ayudaNumero: "Muchos presupuestos no lo llevan: Faktelio le pone el suyo (PP y el año).",
      fecha: "Fecha del presupuesto",
      validoHasta: "Válido hasta (opcional)",
      ayudaValido: "Solo se enseña: no cancela nada.",
      ayudaLineas: "Las bases y el IVA tal como vienen en el presupuesto: una línea por cada tipo de IVA.",
      proveedorNoCambia: "Tiene facturas o pagos enlazados: el proveedor ya no cambia.",
      guardar: "Guardar presupuesto",
      guardado: (numero: string) => `Presupuesto anotado como ${numero}. Acéptalo cuando lo confirmes con el proveedor.`,
      corregido: "Cambios guardados.",
      adjuntoTitulo: "PDF o foto del presupuesto",
      adjuntoAyuda: "Opcional: PDF, JPEG, PNG o WebP, hasta 25 MB. Se descarga, nunca se abre aquí.",
      adjuntoNoSubido: "El presupuesto está anotado, pero el archivo no se ha subido: súbelo desde su ficha.",
      adjuntoQuitado: "Archivo quitado. El presupuesto sigue anotado.",
      errores: {
        BEFORE_ISSUE_DATE: "No puede ser anterior a la fecha del presupuesto.",
      } as Record<string, string>,
      // La ficha.
      fichaTitulo: (numero: string) => `Presupuesto ${numero}`,
      suNumeroEtiqueta: "Número del proveedor",
      validez: "Válido hasta",
      total: "Total presupuestado",
      aPagar: "A pagar",
      facturado: "Facturado",
      pagado: "Pagado",
      porFacturar: "Por facturar",
      porPagar: "Por pagar",
      facturadoDeMas: "Lo facturado pasa del total del presupuesto. No se bloquea, porque manda la factura del proveedor, pero conviene revisarlo con él.",
      cerradoAyuda: "Cerrado a mano: la obra se dio por terminada aunque se facturó menos de lo presupuestado.",
      cancelado: (motivo: string) => `Cancelado: ${motivo}`,
      facturas: "Sus facturas",
      sinFacturas: "Todavía no tiene facturas enlazadas.",
      pagosSinFactura: "Pagos sin factura de este presupuesto",
      pagos: "Sus pagos",
      aceptar: "Aceptar",
      aceptado: "Presupuesto aceptado. Ya puedes enlazarle facturas y pagos.",
      cerrar: "Dar por terminado",
      ayudaCerrar: "Si la obra terminó por menos de lo presupuestado: pasa a completado. Se puede deshacer.",
      cerrado: "Presupuesto dado por terminado.",
      reabrir: "Deshacer",
      reabierto: "Vuelve a contar lo que queda por facturar.",
      cancelar: "Cancelar presupuesto",
      ayudaCancelar: "Cancelar no lo borra: conserva su número y el motivo. Solo se puede sin facturas ni pagos vivos enlazados.",
      motivoCancelar: "Por qué lo cancelas",
      confirmarCancelar: "Cancelar el presupuesto",
      volver: "Volver",
      canceladoOk: "Presupuesto cancelado.",
      noCancelable: "Tiene facturas o pagos vivos enlazados: quítales antes el presupuesto o anula esos pagos.",
      verEnRecibidas: "Ver en facturas recibidas",
      anotarFacturaDe: "Anotar factura de este presupuesto",
      noEncontrado: "No se ha encontrado el presupuesto.",
    },
  },
  /**
   * Informes fiscales (`OD-0041`, ACCEPTED). Las palabras importan más que en
   * ninguna otra pantalla (`D-02`): «estimación», «orientativo», «repercutido»;
   * NUNCA «a pagar», «a ingresar», «lo que debes» ni «resultado del 303».
   */
  informes: {
    titulo: "Informes fiscales",
    cargando: "Calculando el informe…",
    error: "No se ha podido calcular el informe. Vuelve a intentarlo.",
    reintentar: "Reintentar",
    sinPermiso: "Tu rol no puede ver las facturas, y el informe sale de ellas.",
    soloBusinessTitulo: "Los informes fiscales son del plan Business",
    soloBusiness:
      "Con Business ves el IVA repercutido de cada trimestre, las retenciones que te han hecho tus clientes y los próximos plazos de la AEAT, todo sacado de tus facturas emitidas.",
    verPlanes: "Ver planes",
    trimestre: "Trimestre",
    nombreTrimestre: (etiqueta: string) => {
      const [y, n] = etiqueta.split("-T");
      return `${n ?? ""}.º trimestre de ${y ?? ""}`;
    },
    aFecha: (dia: string) => `Calculado con tus facturas emitidas y tus compras anotadas a fecha de ${dia}.`,
    enCurso: "El trimestre aún no ha terminado: las cifras cambiarán con cada factura que emitas y cada compra que anotes.",
    // D-02 y D-12: siempre visible, encima de las cifras.
    parcialTitulo: "Estimación parcial e informativa",
    parcial:
      "Sale de las facturas que has emitido con Faktelio y de las compras que has anotado en él. No incluye nada de fuera, no es el resultado de ninguna declaración y Faktelio no presenta nada ante la AEAT. Confírmalo con tu gestoría.",
    ivaTitulo: "IVA repercutido",
    ivaAyuda:
      "El IVA que has cobrado a tus clientes por cuenta de Hacienda en las facturas emitidas en el trimestre, por su fecha de expedición.",
    ivaCifra: (importe: string) => `${importe} €`,
    ivaBase: (importe: string, n: number) => `sobre ${importe} € de base, en ${n} ${n === 1 ? "factura" : "facturas"}`,
    // OD-0056 (sustituye a OD-0041 D-05). Escrito para quien no sabe de
    // impuestos: lo pidió el propietario el 2026-10-23.
    soportadoTitulo: "IVA soportado en tus compras anotadas",
    soportadoCifra: (importe: string) => `${importe} €`,
    soportadoBase: (importe: string, n: number) => `sobre ${importe} € de base, en ${n} ${n === 1 ? "compra" : "compras"}`,
    soportadoAyuda: "El IVA que te han cobrado tus proveedores en las facturas de compra que has anotado este trimestre.",
    soportadoSinCifra: {
      SIN_COMPRAS_ANOTADAS:
        "Todavía no has anotado ninguna factura de compra. Cuando las anotes en Compras, aquí verás el IVA que te han cobrado tus proveedores.",
      NINGUNA_EN_EL_TRIMESTRE: "No has anotado ninguna factura de compra en este trimestre.",
      ROL_SIN_COMPRAS: "Tu rol no puede ver las compras, así que aquí no se muestra su IVA.",
      PLAN_SIN_COMPRAS: "Tu plan no incluye las compras, así que aquí no se muestra su IVA.",
      COMPRAS_ILEGIBLES: "No se han podido leer los importes de estas compras, así que no se muestra una cifra a medias:",
    } as Record<string, string>,
    irACompras: "Ir a Compras",
    // D-05: siempre, junto a la cifra. Lo esencial en tres frases.
    soportadoAvisoTitulo: "Esta cifra no es lo que te puedes deducir",
    soportadoAviso:
      "Suma el IVA de las facturas de compra que has anotado este trimestre, tal como lo dicen. No todo ese IVA se puede restar en tu declaración: depende de cada compra, y lo decide tu gestoría. Y si te falta alguna factura por anotar, aquí también falta.",
    // D-05: la explicación larga, plegada, para quien no sabe qué es.
    soportadoGuia: {
      titulo: "¿Qué es el IVA soportado? Explicado sin tecnicismos",
      secciones: [
        {
          titulo: "Qué es el IVA soportado",
          parrafos: [
            "Cuando compras algo para tu trabajo, el proveedor te cobra IVA en su factura. Ese IVA que pagas tú se llama IVA soportado.",
            "Un ejemplo: compras material por 100 € y la factura dice 100 € + 21 € de IVA = 121 €. Esos 21 € son IVA soportado, y son lo que suma esta cifra.",
          ],
          puntos: [],
        },
        {
          titulo: "Para qué sirve",
          parrafos: [
            "En la declaración trimestral del IVA (el modelo 303), normalmente puedes restar el IVA de tus compras del IVA que has cobrado a tus clientes, que es la cifra de «IVA repercutido».",
            "Por eso conviene anotar todas tus facturas de compra: una factura que no está anotada no aparece aquí.",
          ],
          puntos: [],
        },
        {
          titulo: "Por qué Faktelio no hace la resta",
          parrafos: ["Porque el resultado podría no ser el bueno, y en impuestos una cifra equivocada cuesta dinero:"],
          puntos: [
            "No todo el IVA de una compra se puede restar. Si la compra no es para tu trabajo, o la usas también para ti (un coche, un móvil), se resta solo una parte o nada. Eso lo decide tu gestoría.",
            "Solo suma las compras que has anotado, y Faktelio no sabe si están todas.",
            "La declaración tiene más cosas que Faktelio no conoce, como lo que te quedó por compensar de otros trimestres.",
          ],
        },
        {
          titulo: "En qué trimestre cuenta cada compra",
          parrafos: [
            "En el trimestre en que la anotaste en Faktelio. El IVA de una compra solo se puede restar cuando ya tienes su factura, así que Faktelio nunca la cuenta antes de que la tengas.",
            "Si anotas ahora una factura de un trimestre anterior, cuenta en este, y la compra lo avisa: si ya la tenías entonces, tu gestoría puede haberla llevado a aquel trimestre. Para restarla hay un plazo de cuatro años.",
            "Una excepción: si te facturan antes de la compra (por ejemplo, la factura llega en septiembre y la entrega es en octubre) y escribes esa fecha de operación, cuenta en el trimestre de la operación. Hasta que la operación no se hace, ese IVA no se puede restar.",
          ],
          puntos: [],
        },
      ],
    },
    comprasTitulo: "Compras que forman el IVA soportado",
    comprasAyuda: "La cifra del IVA soportado es la suma de estas compras. Ábrelas para ver la factura que anotaste.",
    comprasPorTipo: "IVA soportado por tipo",
    filaCompraTitulo: (registro: string, proveedor: string, numero: string) => `${registro} · ${proveedor} · su factura ${numero}`,
    filaCompra: (dia: string, base: string, cuota: string) => `Anotada el ${dia} · Base ${base} € · IVA ${cuota} €`,
    avisosCompra: {
      EXPEDIDA_EN_OTRO_TRIMESTRE:
        "La factura es de un trimestre anterior. Cuenta aquí porque la anotaste en este; si ya la tenías entonces, tu gestoría puede haberla llevado a aquel trimestre.",
      OPERACION_EN_OTRO_TRIMESTRE: "La fecha de operación es de otro trimestre: pregunta a tu gestoría en cuál va.",
      OPERACION_POSTERIOR:
        "La anotaste en un trimestre anterior, pero la fecha de operación es de este. Cuenta aquí porque hasta la operación ese IVA no se puede restar.",
      MAS_DE_CUATRO_ANOS: "Han pasado más de cuatro años desde la compra: pregunta a tu gestoría si todavía se puede deducir.",
    } as Record<string, string>,
    comprasExcluidasTitulo: "Compras que no cuentan",
    compraExcluida: (registro: string, proveedor: string) => `${registro}, ${proveedor}: anulada`,
    porTipo: "IVA repercutido por tipo",
    columnaTipo: "Tipo",
    columnaBase: "Base",
    columnaCuota: "Cuota",
    tipoSujeta: (tipo: string) => `${tipo.replace(/,00$/, "")} %`,
    tratamientos: {
      SUJETA_EXENTA: "Exenta",
      NO_SUJETA: "No sujeta",
    } as Record<string, string>,
    sinCuota: "—",
    retencionesTitulo: "Retenciones de IRPF que te han hecho",
    retencionesAyuda:
      "Lo que tus clientes han retenido en las facturas emitidas en el trimestre. Es IRPF que ellos ingresan por ti y que te descuentas en tu declaración de la renta: no es un pago tuyo ni se suma al IVA.",
    retencionesCifra: (importe: string) => `${importe} €`,
    retencionesBase: (n: number) => `en ${n} ${n === 1 ? "factura" : "facturas"}`,
    retencionTipo: (tipo: string, base: string, importe: string, n: number) =>
      `Al ${tipo.replace(/,00$/, "")} %: ${importe} € sobre ${base} € (${n} ${n === 1 ? "factura" : "facturas"})`,
    sinRetenciones: "Ninguna factura del trimestre lleva retención.",
    facturasTitulo: "Facturas que forman el IVA repercutido y las retenciones",
    facturasAyuda: "Esas dos cifras son la suma de estas filas. Ábrelas para ver el documento.",
    sinFacturas: "No hay facturas emitidas en este trimestre.",
    clases: {
      ORDINARIA: "",
      RECTIFICATIVA_DIFERENCIAS: "Rectificativa por diferencias",
      RECTIFICATIVA_SUSTITUCION: "Rectificativa por sustitución: cuenta la diferencia con la original",
    } as Record<string, string>,
    filaFactura: (base: string, cuota: string) => `Base ${base} € · IVA ${cuota} €`,
    filaRetencion: (importe: string) => ` · Retención ${importe} €`,
    expedida: (dia: string) => `Expedida el ${dia}`,
    avisos: {
      OPERACION_EN_OTRO_TRIMESTRE: "La fecha de operación es de otro trimestre: pregunta a tu gestoría en cuál se declara.",
      ORIGINAL_CON_VARIAS_RECTIFICATIVAS: "La factura original tiene más de una rectificativa: revisa esta cifra con tu gestoría.",
    } as Record<string, string>,
    excluidasTitulo: "Facturas que no cuentan",
    excluida: (numero: string, dia: string) => `${numero}, expedida el ${dia}: anulada`,
    sinDatosTitulo: "Sin datos suficientes",
    sinDatos: {
      TOTALES_ILEGIBLES: "No se han podido leer los importes de estas facturas, así que no se muestra una cifra a medias:",
      ORIGINAL_NO_DISPONIBLE: "Falta la factura original de estas rectificativas, así que no se muestra una cifra a medias:",
    } as Record<string, string>,
    declaracionTitulo: "Dónde se declara",
    calendarioTitulo: "Próximos plazos",
    calendarioAyuda: "Plazos del calendario oficial de la AEAT para quien declara por trimestres. Faktelio no sabe si ya has presentado algo.",
    sinProximos: "No hay plazos pendientes cargados.",
    modelos: {
      "303": "Modelo 303 · IVA trimestral",
      "390": "Modelo 390 · Resumen anual de IVA",
      "130": "Modelo 130 · Pago fraccionado de IRPF, si te aplica",
    } as Record<string, string>,
    periodo: (periodo: string) => (periodo.includes("-T") ? `${periodo.split("-T")[1] ?? ""}T ${periodo.split("-T")[0] ?? ""}` : `ejercicio ${periodo}`),
    plazo: (desde: string, hasta: string) => `Del ${desde} al ${hasta}`,
    domiciliacion: (dia: string) => `Si domicilias el pago, hasta el ${dia}`,
    domiciliacionCerrada: (dia: string) => `La domiciliación del pago cerró el ${dia}`,
    estados: {
      ABIERTO: "Plazo abierto",
      PROXIMO: "Próximamente",
      TERMINADO: "Plazo terminado",
    } as Record<string, string>,
    quedan: (dias: number) => (dias === 0 ? "Termina hoy" : `Quedan ${dias} ${dias === 1 ? "día" : "días"}`),
    noPublicado: (ano: number, regla: string) =>
      `La AEAT aún no ha publicado el calendario de ${ano}. Plazo general: ${regla}; si el último día es inhábil, se mueve.`,
    soloPersonas: "El 130 es de personas físicas; tu NIF es de una entidad, así que no se muestra.",
    fuente: (titulo: string, dia: string) => `Fuente: ${titulo}, comprobado el ${dia}.`,
    confirma: "Confirma siempre los plazos con la AEAT o con tu gestoría. El calendario de Faktelio es una ayuda, no la fuente.",
  },
  logo: {
    titulo: "Logotipo en tus facturas",
    detalle:
      "Aparecerá arriba a la izquierda en las facturas que emitas a partir de ahora. Cada factura emitida guarda el logotipo que tenías ese día: cambiarlo no cambia las que ya emitiste.",
    requisitos: "PNG o JPEG, hasta 500 KB.",
    sinPlan:
      "Tu plan no incluye el logotipo en la factura. Está disponible en Factura+ y Business.",
    verPlanes: "Ver planes",
    ninguno: "Todavía no has subido ningún logotipo.",
    noDisponible: "El logotipo todavía no está disponible. Lo estamos activando.",
    elegir: "Elegir imagen",
    subiendo: "Subiendo…",
    quitar: "Quitar logotipo",
    quitando: "Quitando…",
    subido: "Logotipo actualizado. Las facturas que emitas desde ahora lo llevarán.",
    quitado: "Logotipo quitado.",
    // Dos frases: lo que ha pasado, y qué hacer. URS §110.
    demasiadoGrande: "Esa imagen pasa de 500 KB. Redúcela y vuelve a intentarlo.",
    noEsImagen: "Ese archivo no es un PNG ni un JPEG.",
    error: "No se ha podido cambiar el logotipo.",
    cargando: "Cargando…",
    ocupa: (tamano: string) => `Ocupa ${tamano} de tu espacio.`,
    // No se previsualiza a proposito (decision del 2026-10-06): el logotipo no
    // se devuelve nunca por HTTP, solo sale dentro del PDF.
    dondeVerlo: "Para verlo, descarga el PDF de cualquier factura que emitas.",
    /*
      OD-0044 D-04: el aviso dice QUE pasa y QUE hacer. Sin la segunda parte, el
      logotipo desaparece de las facturas y el cliente no sabe por que ni como
      arreglarlo.

      No se le echa la culpa al cliente ni se le explica la causa tecnica: no
      sabemos si fue el almacen o un fallo nuestro, y lo que resuelve su
      problema es el mismo en los dos casos.
    */
    noCuadra:
      "No hemos podido verificar este archivo, así que de momento no sale en tus facturas. Vuelve a subirlo para arreglarlo.",
  },

  emisor: {
    titulo: "Datos fiscales de tu empresa",
    // OD-0015 D-05/D-03: la advertencia es parte del producto, no una fricción.
    detalle:
      "Son los datos que aparecerán como emisor en tus facturas. Cada factura emitida guarda una copia congelada: si después los cambias, las facturas ya emitidas no cambian.",
    cargando: "Cargando tus datos fiscales…",
    error: "No se han podido cargar tus datos fiscales.",
    reintentar: "Reintentar",
    legalName: "Razón social, o nombre y apellidos",
    taxIdType: "Tipo de identificación",
    tipos: {
      NIF_ES_PERSON: "NIF (DNI) de persona física",
      NIE_ES: "NIE",
      NIF_ES_ENTITY: "NIF de empresa o entidad",
    },
    taxIdValue: "Número de identificación",
    siiPregunta: "¿Llevas los libros de IVA en el SII (Suministro Inmediato de Información)?",
    sii: {
      SIN_RESPONDER: "Elige una respuesta",
      NOT_SII: "No",
      SII: "Sí",
    },
    ayudaSii:
      "Casi ningún autónomo ni pyme está en el SII. Si lo estás, la ley te obliga a usar un sistema del SII y Faktelio no puede emitir tus facturas.",
    enlaceSii: "https://sede.agenciatributaria.gob.es/Sede/iva/suministro-inmediato-informacion.html",
    enlaceSiiTexto: "Qué es el SII (Agencia Tributaria)",
    // OD-0063 D-10c. Los textos de codigo y tipo son los del diseno de la AEAT
    // (AEAT-REF-027, validacion 16), no una traduccion libre.
    iaeTitulo: "Tu actividad en el libro registro",
    iaeDetalle:
      "Opcional. Va en el libro registro de facturas expedidas que descarga tu gestoría para presentar el IVA. No sale en tus facturas. Si no lo tienes claro, déjalo sin indicar: tu gestoría lo completa.",
    iaeCodigo: "Código de actividad",
    iaeCodigos: {
      NINGUNO: "Sin indicar",
      A: "A · Actividad incluida en el IAE",
      B: "B · Actividad no incluida en el IAE",
      C: "C · Actividad todavía no iniciada",
      D: "D · Arrendador de inmuebles no incluido en los anteriores",
    },
    iaeTipo: "Tipo de actividad",
    iaeTipoElige: "Elige el tipo",
    iaeTipos: {
      A: {
        "01": "01 · Arrendador de bienes inmuebles",
        "02": "02 · Ganadería independiente",
        "03": "03 · Resto de actividades empresariales",
        "04": "04 · Actividad profesional de carácter artístico o deportivo",
        "05": "05 · Resto de actividades profesionales",
      },
      B: {
        "01": "01 · Actividad agrícola",
        "02": "02 · Actividad ganadera dependiente",
        "03": "03 · Actividad forestal",
        "04": "04 · Producción de mejillón en batea",
        "05": "05 · Actividad pesquera, salvo el mejillón en batea",
        "06": "06 · Otras actividades no sujetas al IAE",
      },
    } as Readonly<Record<"A" | "B", Readonly<Record<string, string>>>>,
    iaeEpigrafe: "Grupo o epígrafe del IAE",
    iaeAyudaEpigrafe: "Hasta 4 caracteres, sin el punto: el epígrafe 653.3 se escribe 6533.",
    iaeEnlace: "https://www.agenciatributaria.gob.es/static_files/common/internet/html/buscadorActividades.html",
    iaeEnlaceTexto: "Buscador de actividades (Agencia Tributaria)",
    iaeInmueble:
      "Con esta actividad, la Agencia Tributaria pide en el libro el inmueble de cada factura (su situación y su referencia catastral). Faktelio no guarda ese dato: tu gestoría tendrá que añadirlo antes de presentar el libro.",
    siiSalida:
      "Has declarado que llevas los libros en el SII. Si ya no estás, escríbenos a soporte: salir del SII tiene fechas y condiciones.",
    nifBloqueado:
      "Ya has emitido facturas con este NIF, así que no se puede cambiar. Si tu empresa tiene un NIF nuevo, escríbenos a soporte.",
    addressLine: "Dirección",
    postalCode: "Código postal",
    city: "Municipio",
    province: "Provincia",
    guardar: "Guardar",
    guardando: "Guardando…",
    guardado: "Datos guardados. Revísalos y confírmalos para poder facturar.",
    sinCambios: "No había cambios que guardar.",
    avisoControl:
      "El número tiene el formato de un NIF de empresa, pero su carácter de control no coincide con el cálculo habitual. Ese cálculo no está publicado oficialmente, así que no lo bloqueamos: compruébalo con tu tarjeta de identificación fiscal.",
    activityNature: "Naturaleza de tu actividad",
    actividades: {
      NO_INDICADA: "Prefiero no indicarlo",
      PROFESIONAL: "Profesional (secciones 2.ª o 3.ª del IAE)",
      EMPRESARIAL: "Empresarial (sección 1.ª del IAE)",
    },
    ayudaActividad:
      "Solo se usa para sugerirte la retención de IRPF al preparar un presupuesto. No aparece en las facturas y puedes cambiarlo cuando quieras. Si no lo tienes claro, déjalo sin indicar y pregunta a tu asesor.",
    // Bloques del formulario (auditoria de diseno, 2026-10-19): era una sola
    // columna de once campos sin ningun titulo.
    bloqueIdentidad: "Quién factura",
    bloqueDomicilio: "Domicilio fiscal",
    bloqueFiscalidad: "Tu fiscalidad",
    documentoTitulo: "Datos que salen en tus documentos",
    documentoDetalle:
      "Opcionales. Aparecen en tus presupuestos para que el cliente sepa cómo contactarte y cómo pagarte. Cambiarlos no te obliga a volver a confirmar los datos fiscales.",
    contactEmail: "Email de contacto",
    contactPhone: "Teléfono de contacto",
    paymentMethod: "Forma de pago habitual",
    formasPago: {
      NINGUNA: "No indicarla",
      TRANSFERENCIA: "Transferencia bancaria",
      DOMICILIACION: "Domiciliación bancaria",
      TARJETA: "Tarjeta",
      EFECTIVO: "Efectivo",
      BIZUM: "Bizum",
      OTRO: "Otra",
    },
    iban: "IBAN para recibir los pagos",
    ayudaIban: "Comprobamos que el IBAN está bien escrito (su dígito de control), no que la cuenta sea tuya.",
    documentFooter: "Pie de tus documentos",
    ayudaPie:
      "Texto libre al pie de cada presupuesto, por ejemplo la información de protección de datos. Lo escribes tú y respondes de su contenido.",
    confirmarTitulo: "Confirmar los datos",
    confirmarDetalle:
      "Confirma que la razón social, la identificación fiscal y el domicilio son correctos. Sin confirmarlos no se podrá emitir ninguna factura.",
    casilla: "He revisado que los datos son correctos",
    confirmar: "Confirmar datos fiscales",
    confirmando: "Confirmando…",
    confirmados: (fecha: string) => `Confirmados el ${fecha}. Si cambias cualquier dato tendrás que volver a confirmarlos.`,
    faltaCasilla: "Marca la casilla para confirmar.",
    formatoValido: "Formato válido. No es una comprobación con Hacienda.",
    mfaTitulo: "Necesitas la verificación en dos pasos",
    mfaDetalle: "Para editar o confirmar los datos fiscales hay que tener activada la verificación en dos pasos.",
    irASeguridad: "Ir a Seguridad",
    soloOwner: "Solo la persona propietaria de la organización puede editar estos datos.",
    errores: {
      REQUIRED: "Este dato es obligatorio.",
      TOO_LONG: "Es demasiado largo.",
      INVALID_CHARACTERS: "Contiene caracteres no permitidos.",
      TYPE_NOT_ALLOWED: "Este tipo de identificación no se admite para el emisor.",
      POSTAL_CODE_INVALID: "El código postal debe tener 5 cifras de una provincia española.",
      INVALID_OPTION: "Elige una de las opciones de la lista.",
      EMAIL_INVALID: "Ese email no parece válido.",
      PHONE_INVALID: "Escribe solo cifras, espacios y el prefijo con +.",
      IBAN_FORMAT: "Un IBAN español tiene 24 caracteres: ES y 22 cifras.",
      IBAN_CONTROL: "Los dígitos de control no cuadran: revisa que esté bien copiado.",
      TAX_ID_REQUIRED: "Escribe el número de identificación.",
      FORMAT_INVALID: "El formato no corresponde al tipo elegido.",
      CONTROL_INVALID: "La letra de control no corresponde al número. Revisa que esté bien escrito.",
      SPECIAL_NIF_NOT_SUPPORTED: "Los NIF que empiezan por K, L o M no se admiten todavía para el emisor.",
      // OD-0039 E-14: el asistente pregunta el SII en el paso 1 y no deja seguir sin respuesta.
      SII_REQUIRED: "Elige «Sí» o «No»: sin esta respuesta no podrás emitir.",
    } as Readonly<Record<string, string>>,
  },
  clientes: {
    titulo: "Clientes",
    cargando: "Cargando clientes…",
    error: "No se han podido cargar los clientes.",
    reintentar: "Reintentar",
    activos: "Activos",
    archivados: "Archivados",
    filtro: "Mostrar",
    nuevo: "Nuevo cliente",
    plazas: (usadas: number, total: number) => `${usadas} / ${total} plazas de cliente utilizadas`,
    plazasSinLimite: (usadas: number) => `${usadas} clientes · sin límite de plazas en tu plan`,
    plazasLlenas: (liberables: number) =>
      liberables > 0
        ? `Has usado todas las plazas. ${liberables === 1 ? "1 cliente no tiene facturas: puedes eliminarlo" : `${liberables} clientes no tienen facturas: puedes eliminarlos`} para liberar espacio.`
        : "Has usado todas las plazas. Los clientes con facturas emitidas ocupan plaza aunque los archives.",
    vacioActivos:
      "Todavía no tienes clientes. Añade el primero para poder preparar presupuestos y facturas. Puedes crearlo sin NIF y completarlo después.",
    vacioArchivados: "No hay clientes archivados.",
    sinNif: "Sin identificación fiscal",
    sinPermiso: "Tu rol no permite ver clientes.",
    buscar: "Buscar por nombre, NIF, email o ciudad",
    cuantos: (vistos: number, total: number) =>
      vistos === total ? (total === 1 ? "1 cliente" : `${total} clientes`) : `${vistos} de ${total} clientes`,
    sinResultados: "Ningún cliente coincide con la búsqueda.",
  },
  cliente: {
    tituloNuevo: "Nuevo cliente",
    tituloEditar: "Editar cliente",
    volver: "Volver a clientes",
    displayName: "Nombre",
    ayudaNombre: "Cómo lo reconoces: nombre comercial o de la persona.",
    legalName: "Razón social (opcional)",
    tipo: "Identificación fiscal",
    sinIdentificacion: "Sin identificación (cliente provisional)",
    tipos: {
      NIF_ES_PERSON: "NIF (DNI) de persona física",
      NIE_ES: "NIE",
      NIF_ES_ENTITY: "NIF de empresa o entidad",
      VAT_EU: "NIF-IVA de la Unión Europea",
      FOREIGN_ID: "Identificación extranjera",
      OTHER: "Otra (por ejemplo NIF que empieza por K, L o M)",
    },
    ayudaProvisional: "Sin identificación fiscal sirve para presupuestos, pero no para emitir facturas.",
    numero: "Número de identificación",
    pais: "País",
    email: "Email (opcional)",
    telefono: "Teléfono (opcional)",
    direccion: "Dirección (opcional)",
    codigoPostal: "Código postal (opcional)",
    municipio: "Municipio (opcional)",
    provincia: "Provincia (opcional)",
    notas: "Notas (opcional)",
    guardar: "Guardar",
    guardando: "Guardando…",
    creado: "Cliente creado.",
    guardado: "Cambios guardados.",
    duplicado: "Ya existe un cliente con este NIF.",
    abrirExistente: "Abrir el existente",
    avisoControl:
      "El carácter de control de este NIF de empresa no coincide con el cálculo habitual, que no está publicado oficialmente. Lo guardamos igualmente: compruébalo con el cliente.",
    archivado: "Archivado. Sigue ocupando su plaza.",
    archivar: "Archivar",
    archivarDetalle: "Archivar lo oculta de la lista de activos. Seguirá ocupando su plaza.",
    eliminar: "Eliminar",
    eliminarPregunta: "¿Eliminar este cliente? No se puede deshacer y liberará su plaza.",
    siEliminar: "Sí, eliminar",
    cancelar: "Cancelar",
    noEliminable: "Tiene facturas emitidas: no se puede eliminar, solo archivar.",
    noEncontrado: "Este cliente ya no existe.",
    sinPermiso: "Tu rol no permite modificar clientes.",
    obligacion: "¿Te practica retención de IRPF?",
    obligaciones: {
      DESCONOCIDO: "No lo sé todavía",
      SI: "Sí, es empresa o profesional obligado a retener",
      NO: "No (por ejemplo, un particular)",
    },
    ayudaObligacion:
      "Solo se usa para sugerirte la retención al preparar un presupuesto. Los particulares no practican retención; las empresas y los profesionales, sí.",
    // OD-0062 E-03: el plazo de pago, para proponer el vencimiento de sus facturas.
    plazo: "Plazo de pago (opcional)",
    plazos: {
      ninguno: "Sin plazo",
      dias: (dias: string) => (dias === "0" ? "Al contado" : `${dias} días`),
      otro: "Otro",
    },
    plazoDias: "Días (de 0 a 365)",
    ayudaPlazo:
      "Al hacerle una factura, el vencimiento se propone con este plazo, y puedes cambiarlo. Sin plazo, a una empresa se le proponen 30 días y a un particular, ninguno.",
    plazoLargo: "Entre empresas, la ley no permite pactar más de 60 días (Ley 3/2004, art. 4.3). Se guarda igualmente: revísalo con el cliente.",
    errores: {
      REQUIRED: "Este dato es obligatorio.",
      TOO_LONG: "Es demasiado largo.",
      INVALID_CHARACTERS: "Contiene caracteres no permitidos.",
      COUNTRY_INVALID: "Elige el país de la lista.",
      POSTAL_CODE_INVALID: "En España, el código postal tiene 5 cifras y empieza por el de una provincia (01 a 52).",
      EMAIL_INVALID: "Ese email no parece válido.",
      PHONE_INVALID: "Escribe solo cifras, espacios y el prefijo con +.",
      NOT_ALLOWED: "Este dato no se puede cambiar desde aquí.",
      TAX_ID_REQUIRED: "Escribe el número de identificación.",
      FORMAT_INVALID: "El formato no corresponde al tipo elegido.",
      CONTROL_INVALID: "La letra de control no corresponde al número.",
      SPECIAL_NIF_NOT_SUPPORTED: "Los NIF con K, L o M se guardan eligiendo «Otra».",
      INVALID_OPTION: "Elige una de las opciones de la lista.",
      INVALID_NUMBER: "Escribe un número de días, sin decimales.",
      OUT_OF_RANGE: "El plazo va de 0 a 365 días.",
    } as Readonly<Record<string, string>>,
  },
  productos: {
    titulo: "Productos y servicios",
    cargando: "Cargando productos…",
    error: "No se han podido cargar los productos.",
    reintentar: "Reintentar",
    nuevo: "Nuevo producto",
    activos: "Activos",
    inactivos: "Desactivados",
    filtro: "Mostrar",
    buscar: "Buscar por código o descripción",
    vacioTitulo: "Añade tus servicios habituales",
    vacio: "Guárdalos una vez y reutilízalos en tus presupuestos y facturas, con su precio y su IVA.",
    crearPrimero: "Crear mi primer producto o servicio",
    // Ilustración, NO datos: no se guardan ni se pueden pulsar (OD-0015 D-04
    // prohíbe CREAR ejemplos en una organización real; esto no crea nada).
    ejemplosTitulo: "Por ejemplo:",
    ejemplos: [
      "Servicio de limpieza · 50 €/hora · IVA 21 %",
      "Consultoría · 75 €/hora · IVA 21 %",
      "Desplazamiento · 25 € · IVA 21 %",
    ] as const,
    vacioActivos: "No hay productos activos. Los desactivados siguen en la otra pestaña.",
    vacioInactivos: "No hay productos desactivados.",
    sinResultados: "Ningún producto coincide con la búsqueda.",
    sinPermiso: "Tu rol no permite ver productos.",
    precioSinIva: (precio: string) => `${precio} € sin IVA`,
    exenta: (causa: string) => `Exenta (${causa})`,
    noSujeta: (causa: string) => `No sujeta (${causa})`,
    iva: (tipo: string) => `IVA ${tipo} %`,
  },
  // CRM V1 de Business: OD-0048 sobre RC-0016. Sencillo a proposito: tres
  // estados, sin etapas ni tablero.
  oportunidades: {
    titulo: "Oportunidades",
    cargando: "Cargando oportunidades…",
    error: "No se han podido cargar las oportunidades.",
    reintentar: "Reintentar",
    nueva: "Nueva oportunidad",
    filtro: "Mostrar",
    estados: { OPEN: "Abiertas", WON: "Ganadas", LOST: "Perdidas" },
    estado: { OPEN: "Abierta", WON: "Ganada", LOST: "Perdida" },
    // El tablero (peticion del propietario, 2026-10-18: "mira HubSpot").
    tablero: "Tablero de oportunidades por etapa",
    columnaVacia: "Ninguna en esta etapa.",
    cuantas: (n: number) => (n === 1 ? "1 oportunidad" : `${n} oportunidades`),
    cifra: (n: number, importe: string) => (n === 1 ? `1 oportunidad · ${importe} €` : `${n} oportunidades · ${importe} €`),
    cerradaEn: (etapa: string) => `Cerrada en «${etapa}»`,
    // OD-0048-ENMIENDA-01: las siete etapas, con las palabras del propietario.
    etapas: {
      IDENTIFIED: "Identificada",
      QUALIFIED: "Cualificada",
      QUOTE_REQUESTED: "Presupuesto solicitado",
      QUOTE_SENT: "Presupuesto enviado",
      QUOTE_ACCEPTED: "Presupuesto aceptado",
      WON: "Ganada",
      LOST: "Perdida",
    },
    totalAbierto: (importe: string, n: number) =>
      n === 1 ? `1 abierta · ${importe} € estimados` : `${n} abiertas · ${importe} € estimados`,
    vacioTitulo: "Apunta lo que quieres vender",
    vacio: "Una oportunidad es algo que le quieres vender a un cliente. Apúntala, mira qué tienes abierto y, cuando te digan que sí, sácale el presupuesto sin volver a escribir nada.",
    crearPrimera: "Apuntar mi primera oportunidad",
    vacioEstado: { OPEN: "No tienes oportunidades abiertas.", WON: "Todavía no has ganado ninguna.", LOST: "No hay oportunidades perdidas." },
    sinPermiso: "Tu rol no permite ver el CRM.",
    sinPermisoMover: "Tu rol no permite cambiar oportunidades.",
    // OD-0048-ENMIENDA-02 F-03: busca en lo que ya se ve; las cifras no cambian.
    buscar: "Buscar",
    buscarMarcador: "Título o cliente",
    resultados: (n: number) => (n === 1 ? "1 oportunidad coincide." : `${n} oportunidades coinciden.`),
    sinResultados: (texto: string) => `Ninguna coincide con «${texto}».`,
    sinFechaPrevista: "Sin fecha prevista",
    previstaPara: (fecha: string) => `Prevista para el ${fecha}`,
    atrasada: "Atrasada",
    // OD-0058 D-05 y D-06: la previsión ponderada y el porcentaje de cada tarjeta.
    // ENMIENDA-02 G-09: cuentan todas las que tienen importe; las que no están
    // cualificadas, con el porcentaje de su etapa.
    prevision: "Previsión",
    previsionDetalle: (cuenta: number, de: number, porEtapa: number) =>
      `Cada importe por su probabilidad. Cuentan ${cuenta} de ${de} abiertas: las que tienen importe.` +
      (porEtapa === 0 ? "" : porEtapa === 1 ? " Una sin cualificar cuenta con el porcentaje de su etapa." : ` ${porEtapa} sin cualificar cuentan con el porcentaje de su etapa.`),
    porcentaje: (n: number) => `${n} %`,
    sinCualificar: "Sin cualificar",
    // G-03: con una o dos respuestas, el número sigue siendo el de la etapa.
    provisional: "Provisional",
    aManoCorto: "A mano",
    // G-08: después de moverla en el tablero, si no está cualificada.
    movidaSinCualificar: (titulo: string, porcentaje: number) =>
      `«${titulo}» está sin cualificar: cuenta con el ${porcentaje} % de su etapa. Con tres respuestas, José lo afina.`,
    cualificarAhora: "Cualificar ahora",
    sinImporte: "Sin importe estimado",
    importe: (importe: string) => `${importe} € estimados`,
    // D-04: fuera de Business.
    soloBusinessTitulo: "El CRM es del plan Business",
    soloBusiness:
      "Con Business apuntas lo que quieres venderle a cada cliente, ves de un vistazo lo que tienes abierto y, cuando lo ganas, sacas el presupuesto en un toque.",
    verPlanes: "Ver mi plan",
    etiquetaBusiness: "Business",
    enCliente: "Oportunidades abiertas",
    // Un cliente borrado no se lleva sus oportunidades: se quedan, sin nombre.
    clienteEliminado: "Cliente eliminado",
    enClienteVacio: "Este cliente no tiene oportunidades abiertas.",
    abrirEnCrm: "Ver en Oportunidades",
  },
  // Tareas y actividades del CRM de Business (OD-0050 sobre RC-0017).
  tareas: {
    titulo: "Tareas",
    cargando: "Cargando tareas…",
    error: "No se han podido cargar las tareas.",
    reintentar: "Reintentar",
    pendientes: (n: number) => (n === 0 ? "Pendientes" : `Pendientes (${n})`),
    hechas: "Hechas",
    vistas: "Mostrar",
    // Los grupos los calcula la pantalla con el dia LOCAL (D-07).
    grupos: { vencidas: "Vencidas", hoy: "Hoy", proximas: "Próximas", sinFecha: "Sin fecha" },
    tipos: { TASK: "Tarea", CALL: "Llamada", MEETING: "Reunión", EMAIL: "Correo", NOTE: "Nota" },
    nueva: "Nueva tarea",
    apuntar: "Apuntar lo que ya has hecho",
    apuntarAyuda: "Una llamada, una reunión o una nota que ya pasó: se guarda como hecha.",
    tipo: "Tipo",
    queHay: "Qué hay que hacer",
    queHayAyuda: "Con tus palabras: «Llamar a Marta por lo del baño», «Enviar el catálogo»…",
    quePaso: "Qué ha pasado",
    quePasoAyuda: "«Dice que lo piensa y me llama el lunes»…",
    fecha: "Fecha (opcional)",
    hora: "Hora (opcional)",
    horaSinFecha: "Pon también la fecha.",
    importante: "Importante",
    cliente: "Cliente (opcional)",
    eligeCliente: "Busca un cliente",
    clienteNoElegido: "Ese cliente no está en la lista. Elige uno de los que salen al escribir, o deja el campo vacío para guardarla sin cliente.",
    fechaPasada: "Esa fecha ya ha pasado: la tarea saldrá en Vencidas. Si es para más adelante, cámbiala.",
    notas: "Notas (opcional)",
    guardar: "Guardar",
    guardando: "Guardando…",
    cancelar: "Cancelar",
    editar: "Editar",
    eliminar: "Eliminar tarea",
    confirmarEliminar: "¿Seguro? Se borra y no se puede recuperar.",
    siEliminar: "Sí, eliminar",
    marcarHecha: (titulo: string) => `Marcar como hecha: ${titulo}`,
    deshacer: (titulo: string) => `Volver a pendiente: ${titulo}`,
    hechaEl: (fecha: string) => `Hecha el ${fecha}`,
    recienHecha: (titulo: string) => `Hecha: «${titulo}». La tienes en Hechas.`,
    // OD-0062 E-09: tareas que se repiten. La siguiente la crea la API al hacerla.
    repetir: "Se repite",
    noSeRepite: "No",
    repeticiones: { WEEKLY: "Cada semana", MONTHLY: "Cada mes" },
    repetirAyuda: "Al marcarla como hecha se crea la siguiente, con su fecha. Si lo deshaces, la siguiente se quita mientras no la hayas tocado.",
    siguiente: (dia: string) => `La siguiente, el ${dia}.`,
    // OD-0062 E-10: enlazarla desde aquí a un presupuesto o una factura del cliente.
    documento: "Presupuesto o factura (opcional)",
    sinDocumento: "Ninguno",
    deshacerRecienHecha: "Deshacer",
    vacioTitulo: "Nada pendiente",
    vacio: "Apunta lo que tienes que hacer —llamar a un cliente, preparar una visita, enviar un catálogo— y aquí verás lo que vence hoy y lo que se te ha pasado.",
    vacioHechas: "No hay nada hecho en los últimos 90 días.",
    cortada: "Se muestra lo hecho en los últimos 90 días, hasta 100. Lo de cada cliente está completo en su ficha.",
    sinPermiso: "Tu rol no permite ver el CRM.",
    soloLectura: "Tu rol permite ver las tareas, no cambiarlas.",
    // D-04: fuera de Business.
    soloBusinessTitulo: "Las tareas son del plan Business",
    soloBusiness:
      "Con Business apuntas lo que tienes que hacer con cada cliente y cada oportunidad, ves lo que vence hoy y guardas el historial de llamadas y reuniones.",
    verPlanes: "Ver mi plan",
    // En las fichas de cliente y de oportunidad.
    enFicha: "Tareas y actividades",
    enFichaVacio: "Nada pendiente ni apuntado todavía.",
    historial: "Historial",
    deOportunidad: (titulo: string) => `Oportunidad: ${titulo}`,
    // OD-0050-ENMIENDA-01: enlazadas a un presupuesto o a una factura.
    dePresupuesto: (numero: string | null) => (numero === null ? "Presupuesto" : `Presupuesto ${numero}`),
    deFactura: (numero: string | null) => (numero === null ? "Factura en borrador" : `Factura ${numero}`),
    seguimiento: "Apuntar seguimiento",
    seguimientoCobro: "Apuntar seguimiento del cobro",
    seguimientoPresupuestoTitulo: (numero: string) => `Llamar por el presupuesto ${numero}`,
    seguimientoCobroTitulo: (numero: string) => `Reclamar el cobro de la factura ${numero}`,
    seguimientoApuntado: "Seguimiento apuntado: lo tienes en Tareas.",
    cobrada: (n: number) =>
      n === 1
        ? "La factura ya está cobrada y tiene una tarea pendiente."
        : `La factura ya está cobrada y tiene ${n} tareas pendientes.`,
    cerrarPendientes: (n: number) => (n === 1 ? "Marcarla como hecha" : "Marcarlas como hechas"),
    dejarPendientes: (n: number) => (n === 1 ? "Dejarla como está" : "Dejarlas como están"),
    // Al ganar o perder una oportunidad: la lista, todas marcadas.
    oportunidadCerrada: (cierre: "WON" | "LOST", n: number) =>
      `La oportunidad está ${cierre === "WON" ? "ganada" : "perdida"} y ${n === 1 ? "tiene una tarea pendiente" : `tiene ${n} tareas pendientes`}. Desmarca las que sigan haciendo falta.`,
    darPorTerminadas: "Dar por terminadas",
    errores: {
      REQUIRED: "Este dato es obligatorio.",
      TOO_LONG: "Es demasiado largo.",
      INVALID_CHARACTERS: "Tiene caracteres que no se admiten.",
      INVALID_DATE: "La fecha no es válida.",
      INVALID_TIME: "La hora no es válida.",
      TIME_WITHOUT_DATE: "Pon también la fecha.",
      NOT_FOUND: "No se encuentra.",
      ARCHIVED: "El cliente está archivado.",
      OTHER_CUSTOMER: "Es de otro cliente.",
      REPEAT_WITHOUT_DATE: "Para que se repita, pon la fecha.",
      NOT_ALLOWED: "No se puede guardar así.",
    } as Record<string, string>,
    errorGuardar: "No se ha podido guardar. Inténtalo de nuevo.",
  },
  oportunidad: {
    nueva: "Nueva oportunidad",
    volver: "Volver a oportunidades",
    titulo: "Qué quieres vender",
    tituloAyuda: "Con tus palabras: «Reforma del baño», «Mantenimiento anual»…",
    cliente: "Cliente",
    eligeCliente: "Busca un cliente",
    clienteAyuda: "Busca uno que ya tengas o escribe el nombre de uno nuevo. No hace falta el NIF.",
    seCrearaCliente: (nombre: string) => `Al guardar se crea el cliente «${nombre}», sin NIF. Sus datos fiscales puedes completarlos cuando le factures.`,
    importe: "Importe estimado (€, opcional)",
    importeAyuda: "Orientativo, sin IVA. No se copia a ningún documento.",
    fecha: "Cuándo esperas cerrarla (opcional)",
    notas: "Notas (opcional)",
    guardar: "Guardar",
    guardando: "Guardando…",
    guardada: "Oportunidad guardada.",
    ganada: "Marcar como ganada",
    etapa: "Etapa",
    etapaHecha: "(hecha)",
    // Cerrar se confirma: un toque en falso la cerraba.
    confirmarCierre: {
      WON: "¿La marcas como ganada? Para cambiarla después tendrás que reabrirla.",
      LOST: "¿La marcas como perdida? Para cambiarla después tendrás que reabrirla.",
    },
    siCerrar: { WON: "Sí, ganada", LOST: "Sí, perdida" },
    soloLectura: "Está cerrada: para cambiar algo, reábrela.",
    fechaPasada: "Esa fecha ya ha pasado. Si sigue abierta, pon cuándo esperas cerrarla ahora.",
    // Antes de crear un cliente que se parece a otro que ya tienes.
    parecidos: "Ya tienes clientes con un nombre parecido. ¿Es uno de estos?",
    esNuevo: (nombre: string) => `No, «${nombre}» es un cliente nuevo`,
    // OD-0048-ENMIENDA-02 F-01: por qué se perdió. Opcional.
    porQue: "¿Por qué la has perdido?",
    motivos: {
      PRICE: "Por precio",
      COMPETITOR: "Eligió a otro",
      POSTPONED: "Lo aplazó o no lo hará",
      NO_REPLY: "No contestó",
      OTHER: "Otro motivo",
    } as const,
    notaMotivo: "Cuál (opcional, hasta 200 caracteres)",
    motivoOpcional: "Es opcional: si no lo sabes, márcala como perdida sin elegir ninguno.",
    motivoDePerdida: (motivo: string, nota: string | null) => (nota === null ? `Motivo: ${motivo}.` : `Motivo: ${motivo}: ${nota}`),
    etapaDelPresupuesto: "Las etapas de presupuesto las marca el presupuesto enlazado: envíalo o márcalo como aceptado desde él.",
    presupuestoEnlazado: "Presupuesto enlazado",
    presupuestoAcabo: (estado: string) => `El presupuesto está ${estado}. Si no sale adelante, márcala como perdida.`,
    estadosPresupuesto: {
      DRAFT: "en borrador",
      SENT: "enviado",
      ACCEPTED: "aceptado",
      REJECTED: "rechazado",
      EXPIRED: "caducado",
      CANCELLED: "cancelado",
    } as Record<string, string>,
    abrirPresupuesto: "Abrir el presupuesto",
    hacerPresupuesto: "Crear presupuesto",
    hacerPresupuestoDetalle: "Se abre un presupuesto nuevo con el cliente puesto y, al guardarlo, queda enlazado a esta oportunidad.",
    etapaDeCierre: (etapa: string) => `Se cerró en «${etapa}».`,
    perdida: "Marcar como perdida",
    reabrir: "Reabrir",
    // Tres «Eliminar» sin más en la misma ficha no decían qué se borraba.
    eliminarTitulo: "Eliminar la oportunidad",
    eliminar: "Eliminar esta oportunidad",
    confirmarEliminar: "¿Seguro? La oportunidad se borra y no se puede recuperar.",
    siEliminar: "Sí, eliminar",
    cancelar: "Cancelar",
    // D-03: lo que se ofrece al ganarla. NUNCA una factura.
    ganadaTitulo: "¡Enhorabuena! ¿Quieres hacerle el presupuesto?",
    ganadaDetalle: "Se abre un presupuesto nuevo con el cliente puesto. No se guarda nada hasta que tú lo guardes.",
    crearPresupuesto: "Crear presupuesto",
    noCrearDocumento: "No crear documento",
    noEncontrada: "No se ha encontrado la oportunidad.",
    transicionNoPermitida: "Primero reábrela.",
    errores: {
      REQUIRED: "Es obligatorio.",
      TOO_LONG: "Es demasiado largo.",
      INVALID_CHARACTERS: "Tiene caracteres no válidos.",
      INVALID_NUMBER: "Escribe un importe, por ejemplo 3000 o 3.000,50.",
      TOO_MANY_DECIMALS: "Como mucho dos decimales.",
      OUT_OF_RANGE: "El importe es demasiado alto.",
      INVALID_DATE: "La fecha no es válida.",
      NOT_FOUND: "Ese cliente ya no existe.",
      ARCHIVED: "Ese cliente está archivado.",
      SIMILAR: "Elige uno de los parecidos o confirma que es un cliente nuevo.",
      NOT_ALLOWED: "Ese valor no se admite aquí.",
    } as Record<string, string>,
    /**
     * OD-0058 (ACCEPTED 2026-10-23): cinco preguntas y un porcentaje que se
     * calcula solo. La regla se dice en la pantalla: es una guía, no una
     * predicción.
     */
    cualificacion: {
      titulo: "Cualificación",
      detalle: "Cinco preguntas para saber cuánto te puedes fiar de esta venta. Si no lo sabes, «No lo sé» te recuerda qué preguntar la próxima vez.",
      preguntas: {
        budget: "¿Encaja con lo que quiere gastar?",
        authority: "¿Hablas con quien decide?",
        timing: "¿Quiere decidir o empezar en menos de un mes?",
        // ENMIENDA-02 G-04: «Sí» es siempre lo bueno. Antes: «¿Está pidiendo otros presupuestos?».
        competition: "¿Eres el único al que ha pedido presupuesto?",
        trust: "¿Repite o viene recomendado?",
      },
      respuestas: { YES: "Sí", NO: "No", UNKNOWN: "No lo sé" },
      probabilidad: "Probabilidad de éxito",
      sinCualificar: "Sin cualificar",
      sinCualificarDetalle: "Responde tres preguntas y Faktelio calcula el porcentaje.",
      calculada: "Calculada con tus respuestas: es una guía, no una predicción.",
      aMano: "Ajustada a mano",
      cerrada: "Oportunidad cerrada",
      ajustar: "Ajustar a mano",
      porcentaje: "Porcentaje (0 a 100)",
      guardarAjuste: "Guardar porcentaje",
      cancelarAjuste: "Cancelar",
      volverAlCalculo: "Volver al cálculo",
      ajusteInvalido: "Escribe un número entero entre 0 y 100.",
      pasarACualificada: "Pasar a «Cualificada»",
    },
  },
  /**
   * José de Faktelio — `OD-0058-ENMIENDA-01`. Habla en primera persona y de tú,
   * como un compañero con oficio: consejos cortos y concretos, nunca una
   * predicción. Las reglas que eligen cada texto están en `ui/jose.ts`.
   *
   * Los consejos son DIEZ por pregunta y por caso (E-02), y van rotando: uno
   * distinto cada día y otro al pulsar "Otro consejo". `crm-opportunities`
   * comprueba que ninguna lista baja de diez ni repite.
   */
  jose: {
    nombre: "José de Faktelio",
    foto: "José, de Faktelio",
    titulo: "Cualificación con José",
    presentacion: "Soy José. Te hago cinco preguntas rápidas sobre esta venta y te digo qué haría yo. Con tres ya te digo cómo la veo.",
    // ENMIENDA-02 G-08: al crearla o al moverla de etapa sin cualificar.
    invitacion: {
      creada: "Ya la tienes apuntada. Respóndeme tres preguntas y te digo cómo la veo.",
      movida: (etapa: string) => `La has pasado a «${etapa}». Respóndeme tres preguntas y afino el porcentaje.`,
    },
    guia: "Es una guía, no una predicción.",
    cerrada: "Está cerrada, así que ya no pregunto. Si la reabres, seguimos.",
    // OD-0048-ENMIENDA-02 F-04: lo propone; la respuesta la sigue dando el usuario.
    repite: (titulo: string) => `Ya le ganaste «${titulo}». Si es el mismo cliente de siempre, la respuesta es «Sí».`,
    tramos: {
      sinDatos: "Aún no te puedo decir nada",
      dificil: "Lo veo difícil",
      enElAire: "Está en el aire",
      buenaPinta: "Tiene buena pinta",
      casiHecha: "Está casi hecha",
    },
    /** Tras la etiqueta con el número: no se repite. */
    lectura: (tramo: string) => `${tramo}.`,
    sinRespuestas: "Respóndeme alguna pregunta y te digo cómo la veo.",
    // G-03: menos de tres respuestas. El número es el de la etapa.
    provisional: (porcentaje: number, etapa: string, faltan: number) =>
      `Por ahora cuento con el ${porcentaje} % de «${etapa}».` +
      // Sin ninguna respuesta ya lo dice la presentación (o la invitación).
      (faltan >= 3 ? "" : faltan === 1 ? " Respóndeme una pregunta más y lo calculo con lo que me digas." : ` Respóndeme ${faltan} preguntas más y lo calculo con lo que me digas.`),
    // G-06: debajo del número. La base, y qué lo sube y qué lo baja.
    explicacion: {
      base: (porcentaje: number, etapa: string) => `Parte del ${porcentaje} % de «${etapa}».`,
      sube: (que: string) => `Sube por: ${que}`,
      baja: (que: string) => `Baja por: ${que}`,
      separador: " · ",
      tope: "Como el presupuesto no salió, no pasa del 10 %.",
      motivosSube: {
        budget: "encaja con lo que quiere gastar",
        authority: "hablas con quien decide",
        timing: "tiene prisa",
        competition: "eres el único presupuesto",
        trust: "repite o viene recomendado",
      },
      motivosBaja: {
        budget: "no encaja con lo que quiere gastar",
        authority: "no hablas con quien decide",
        timing: "no tiene prisa",
        competition: "pide otros presupuestos",
        trust: "no te conoce de antes",
      },
    },
    // G-07: la pista de José. Lo que más sube el porcentaje, de dónde a dónde.
    pista: {
      titulo: "La pista de José",
      budget: (de: number, a: number) => `Si consigues que encaje con lo que quiere gastar, pasarías del ${de} % al ${a} %.`,
      authority: (de: number, a: number) => `Si consigues hablar con quien decide, pasarías del ${de} % al ${a} %.`,
      timing: (de: number, a: number) => `Si consigues que quiera empezar en menos de un mes, pasarías del ${de} % al ${a} %.`,
      competition: (de: number, a: number) => `Si consigues ser el único presupuesto, pasarías del ${de} % al ${a} %.`,
      trust: (de: number, a: number) => `Si consigues que te conozca o te recomienden, pasarías del ${de} % al ${a} %.`,
      recordarmelo: "Recordármelo",
      recordando: "Apuntando…",
      recordado: "Te lo he apuntado en las tareas de esta oportunidad.",
    },
    // G-08: el ajuste a mano, a la vista.
    ajustadoAMano: "Ajustado a mano",
    aMano: (n: number, calculado: number | null) =>
      calculado === null ? `Lo has fijado tú en el ${n} %.` : `Lo has fijado tú en el ${n} %. Con tus respuestas, yo diría ${calculado} %.`,
    volverAlCalculo: (calculado: number | null) => (calculado === null ? "Volver al cálculo" : `Volver al cálculo (${calculado} %)`),
    // G-02: la cualificación es alta y sigue en «Identificada».
    proponerCualificada: (nivel: number) => `La cualificación es alta (${nivel} %). Yo la pasaría a «Cualificada».`,
    ganada: "¡Ganada! Bien hecho.",
    perdida: "Esta no salió. Lo que respondiste te sirve para la próxima.",
    tu: "Tú",
    cambiar: "Cambiar",
    saltar: "Saltar por ahora",
    saltadas: "Has dejado para luego:",
    responder: "Responder",
    consejo: "Consejo de José",
    otroConsejo: "Otro consejo",
    // E-07 (ADR-0024): "¿Te ha servido?", debajo de cada consejo y del paso.
    util: {
      pregunta: "¿Te ha servido?",
      si: "Sí, me ha servido",
      no: "No me ha servido",
      arriba: "👍",
      abajo: "👎",
      gracias: "Gracias: así José aprende qué ayuda.",
    },
    resumenTitulo: "Cómo la veo",
    resumen: (tramo: string, riesgo: string | null, pero: boolean) =>
      riesgo === null ? `${tramo}: lo tiene todo a favor.` : pero ? `${tramo}, pero ${riesgo}.` : `${tramo}: ${riesgo}.`,
    riesgos: {
      desfavorable: {
        budget: "no encaja con lo que quiere gastar",
        authority: "no hablas con quien decide",
        timing: "no tiene prisa por empezar",
        competition: "compite con otros presupuestos",
        trust: "todavía no te conoce",
      },
      noLoSe: {
        budget: "te falta saber si encaja con lo que quiere gastar",
        authority: "te falta saber quién decide",
        timing: "te falta saber cuándo quiere empezar",
        competition: "te falta saber si pide otros presupuestos",
        trust: "te falta saber cómo te ha conocido",
      },
    },
    pasoTitulo: "Lo que haría yo ahora",
    apuntar: "Apuntar como tarea",
    apuntando: "Apuntando…",
    apuntado: "Apuntado en las tareas de esta oportunidad.",
    notaTarea: "Te lo propuso José de Faktelio.",
    /** E-05: `frase` es lo que dice José; `tarea`, el título de la tarea que se apunta. */
    pasos: {
      budget: {
        preguntar: { frase: "Llámale esta semana y pregúntale qué rango de precio tenía en mente.", tarea: "Preguntar qué rango de precio tenía en mente" },
        mejorar: { frase: "Prepárale una versión más sencilla del presupuesto, que encaje con lo que puede gastar.", tarea: "Preparar una versión más sencilla del presupuesto" },
      },
      authority: {
        preguntar: { frase: "Llámale esta semana y pregúntale quién firma.", tarea: "Preguntar quién firma" },
        mejorar: { frase: "Pídele una llamada con quien decide, o que estéis los dos en la próxima.", tarea: "Pedir una llamada con quien decide" },
      },
      timing: {
        preguntar: { frase: "Llámale esta semana y pregúntale para cuándo lo necesita.", tarea: "Preguntar para cuándo lo necesita" },
        mejorar: { frase: "No le persigas: vuelve a llamarle dentro de un mes, a ver si ya quiere empezar.", tarea: "Volver a llamar por si ya quiere empezar" },
      },
      competition: {
        preguntar: { frase: "Llámale esta semana y pregúntale si está pidiendo otros presupuestos.", tarea: "Preguntar si pide otros presupuestos" },
        mejorar: { frase: "Llámale mañana y explícale en qué te diferencias, no solo cuánto cuestas.", tarea: "Explicar en qué me diferencio de los otros presupuestos" },
      },
      trust: {
        preguntar: { frase: "Llámale esta semana y pregúntale cómo te ha conocido.", tarea: "Preguntar cómo me ha conocido" },
        mejorar: { frase: "Mándale mañana dos trabajos parecidos que hayas hecho, o el contacto de un cliente contento.", tarea: "Mandar dos trabajos parecidos que haya hecho" },
      },
      presupuesto: { frase: "Lo tiene todo a favor: prepárale el presupuesto hoy, mientras está interesado.", tarea: "Preparar el presupuesto" },
      enviar: { frase: "Termina el presupuesto y envíaselo hoy.", tarea: "Terminar y enviar el presupuesto" },
      seguimiento: { frase: "Llámale en tres días para saber qué le ha parecido el presupuesto.", tarea: "Llamar para saber qué le ha parecido el presupuesto" },
      cerrar: { frase: "Ha aceptado: fija la fecha de inicio y márcala como ganada.", tarea: "Fijar la fecha de inicio" },
      recuperar: { frase: "El presupuesto no salió: llámale, pregúntale por qué y si hay otra opción.", tarea: "Preguntar por qué no salió el presupuesto" },
    },
    consejos: {
      budget: {
        noLoSe: [
          "Pregúntale si tenía un rango en mente: así no le presupuestas algo que no puede pagar.",
          "Dale dos o tres opciones de precio, de la más sencilla a la más completa, y mira cuál elige.",
          "Pregunta «¿cuánto te gastaste la última vez en algo parecido?». Es más fácil de contestar que «¿cuál es tu presupuesto?».",
          "Antes de hacer el presupuesto, dile una horquilla orientativa y fíjate en cómo reacciona.",
          "Pregunta si lo paga de su bolsillo, con un préstamo o con una ayuda: cambia mucho lo que puede gastar.",
          "Pregúntale qué le pasa si no lo hace: si el problema le cuesta dinero, tu precio se entiende mejor.",
          "No esperes al presupuesto para hablar de dinero: una llamada de dos minutos te ahorra horas de trabajo.",
          "Pregunta si hay una cifra que no quiere pasar. Casi todos la tienen y casi nadie se la pregunta.",
          "Si te dice «lo que cueste», pídele que piense en un tope: evitarás sorpresas al final.",
          "Cuéntale cuánto suelen costar trabajos parecidos que has hecho y pregúntale si encaja.",
        ],
        desfavorable: [
          "Ofrécele una versión más sencilla: menos alcance, el mismo cuidado.",
          "Propón hacerlo por fases: la primera, dentro de lo que puede gastar ahora.",
          "No bajes el precio sin quitar nada: si lo haces, le enseñas que tu precio se podía negociar.",
          "Pregúntale qué parte le importa más y presupuesta solo esa.",
          "Si te lo puedes permitir, ofrece pagarlo en dos o tres plazos, y déjalo por escrito.",
          "Si no encaja de ninguna manera, dedícale poco tiempo y céntrate en otra oportunidad.",
          "Explica lo que incluye tu precio que otros no: garantía, plazo, materiales. A veces no es caro, es que no se ve.",
          "Pregúntale si podría encajar más adelante y apúntate llamarle dentro de unos meses.",
          "Recomiéndale a alguien que encaje con lo que quiere gastar: quedarás bien y puede que vuelva.",
          "Repasa el presupuesto por si lleva extras que no ha pedido, y quítalos.",
        ],
      },
      authority: {
        noLoSe: [
          "Pregúntalo con naturalidad: «¿quién más tiene que dar el visto bueno?».",
          "Pregúntale cómo lo decidieron la última vez que contrataron algo parecido.",
          "Pregunta si el presupuesto lo va a ver alguien más antes de decidir.",
          "Si son pareja o socios, pregunta si los dos han visto lo que necesitan.",
          "Pregunta quién firma y quién paga: a veces no es la misma persona.",
          "Antes de enviar el presupuesto, pregunta a quién se lo tienes que mandar.",
          "Pregúntale qué dudas tendrá la otra persona y respóndelas en el presupuesto.",
          "Ofrece una llamada rápida con todos los que deciden: te ahorra idas y venidas.",
          "Si es una empresa, pregunta si detrás hay un gerente, un administrador o alguien de compras.",
          "Escucha si habla en plural, «lo tenemos que mirar»: casi siempre hay alguien más.",
        ],
        desfavorable: [
          "Pide que estéis los dos en la próxima llamada o visita.",
          "Prepara un resumen de una página que tu contacto pueda enseñar a quien decide.",
          "Pregunta qué le importa a quien decide: el precio, el plazo o la calidad.",
          "Dale argumentos a tu contacto: es tu mejor vendedor dentro de esa casa.",
          "No mandes el presupuesto a ciegas: pregunta cuándo lo verá quien decide y llama ese día.",
          "Ofrécete a resolver tú las dudas de quien decide, por teléfono o en persona.",
          "Pregunta cómo y cuándo se va a tomar la decisión.",
          "Si quien decide no aparece nunca, rebaja lo que esperas de esta venta.",
          "Pide a tu contacto que te presente: basta un correo con los dos en copia.",
          "Manda el presupuesto con un mensaje que se entienda sin que tú estés delante.",
        ],
      },
      timing: {
        noLoSe: [
          "Pregunta «¿para cuándo lo necesitarías?» antes de hablar de precio.",
          "Pregunta si hay alguna fecha que le aprieta: una temporada, una apertura, otra obra.",
          "Pregúntale qué le pasa si lo retrasa un mes.",
          "Ofrécele una fecha de inicio concreta y mira si la acepta.",
          "Pregunta si tiene que esperar a algo: una ayuda, un cobro, otra obra.",
          "Si te dice «cuando puedas», pregúntale qué prefiere: este mes o el que viene.",
          "Dile cuándo tienes hueco en tu agenda: si le interesa, se mueve.",
          "Pregunta cuándo quiere decidir, no solo cuándo quiere empezar.",
          "Pregúntale si ya lo intentó antes y por qué no salió.",
          "Pon fecha de validez al presupuesto: ayuda a que se decida.",
        ],
        desfavorable: [
          "Apúntate llamarle dentro de un mes para ver si ya quiere empezar.",
          "No le persigas cada semana: un recordatorio bien puesto vale más que diez llamadas.",
          "Pregúntale qué tendría que pasar para querer empezar antes.",
          "Mientras tanto, mándale algo útil: una idea, la foto de un trabajo parecido.",
          "Guarda tu energía para las que tienen prisa: esta puede esperar.",
          "Pon fecha de validez al presupuesto y recuérdasela unos días antes de que caduque.",
          "Si tus precios van a subir, díselo con tiempo y sin presionar.",
          "Ofrécele reservarle la fecha con una señal pequeña.",
          "Pregúntale si le vendría bien empezar ahora por una parte pequeña.",
          "Cambia la fecha prevista de cierre: así la previsión no te engaña.",
        ],
      },
      competition: {
        noLoSe: [
          "Pregúntalo sin miedo: «¿estás pidiendo otros presupuestos?». Casi todos te lo dicen.",
          "Pregunta qué va a mirar para elegir: el precio, el plazo, la garantía o la confianza.",
          "Pregúntale si ya le ha llegado alguno y qué le ha parecido.",
          "Pregunta cómo te ha encontrado: si fue por una recomendación, juegas con ventaja.",
          "Pregunta cuándo piensa decidir: si espera a otros, sabrás cuándo llamar.",
          "Pregúntale qué no le gustó de quien se lo hizo la última vez.",
          "Si duda al responder, da por hecho que compara y cuida los detalles.",
          "Pregúntale qué le haría elegirte sin mirar más.",
          "Pregunta si alguien le ha recomendado a otro profesional.",
          "Contesta rápido: el primero que responde bien suele llevárselo.",
        ],
        desfavorable: [
          "Llámale y explícale en qué te diferencias, no solo cuánto cuestas.",
          "Manda tu presupuesto antes que los demás y llama al día siguiente.",
          "Desglosa bien lo que incluye: lo va a comparar línea a línea.",
          "Enséñale fotos o referencias de trabajos parecidos.",
          "Pregúntale qué tendría que tener tu presupuesto para elegirte.",
          "No entres en una guerra de precios: compite en confianza, plazo y garantía.",
          "Pon fecha de validez al presupuesto: que no se quede esperando a los otros.",
          "Ofrece algo que los demás no suelen dar: una visita, más garantía, un seguimiento.",
          "Pregúntale si hay algo de los otros presupuestos que le haya gustado.",
          "Si solo mira el precio y no te compensa, retírate a tiempo.",
        ],
      },
      trust: {
        noLoSe: [
          "Pregúntale cómo te ha conocido: es la pregunta más útil que puedes hacer.",
          "Mira si ya es cliente tuyo o si le has hecho algún trabajo antes.",
          "Pregunta si conoce a alguien que haya trabajado contigo.",
          "Pregunta si ha visto tu web, tus redes o tus reseñas.",
          "Si viene por una recomendación, pregunta de quién y dale las gracias a esa persona.",
          "Pregúntale si ha trabajado antes con alguien como tú y qué tal le fue.",
          "Apunta de dónde vienen tus clientes: sabrás qué te trae a los buenos.",
          "Pregúntale qué le hizo llamarte a ti.",
          "Si no te conoce de nada, cuenta con que necesitará más pruebas de confianza.",
          "Pregunta si alguien le ha hablado de ti: a veces viene recomendado y no lo dice.",
        ],
        desfavorable: [
          "Mándale dos o tres trabajos parecidos que hayas hecho.",
          "Ofrécele el contacto de un cliente contento que pueda hablarle de ti.",
          "Si puedes, ve a verle en persona: la confianza se gana cara a cara.",
          "Comprométete por escrito con un plazo, y cúmplelo.",
          "Explícale cómo trabajas, paso a paso: lo que se entiende da menos miedo.",
          "Ofrécele una garantía clara y fácil de entender.",
          "Contesta rápido y con buena presentación: es lo primero que juzga.",
          "Ofrécele empezar por un trabajo pequeño para que te conozca.",
          "Pide reseñas a tus clientes contentos: la próxima vez te ayudarán.",
          "Pon tus datos completos en el presupuesto: nombre, NIF, dirección y teléfono.",
        ],
      },
    },
    recomienda: {
      titulo: "José te recomienda",
      detalle: "Llama primero a estas: son las que más pueden sumar, por importe y por porcentaje.",
      vacio: "Ponles importe a tus oportunidades abiertas y te diré a cuáles llamar primero.",
      motivo: (importe: string, porcentaje: number, porEtapa: boolean) =>
        porEtapa ? `${importe} € al ${porcentaje} %, por su etapa: cualifícala` : `${importe} € al ${porcentaje} %`,
      marca: "José la recomienda",
    },
  },
  presupuestos: {
    titulo: "Presupuestos",
    cargando: "Cargando presupuestos…",
    error: "No se han podido cargar los presupuestos.",
    reintentar: "Reintentar",
    nuevo: "Nuevo presupuesto",
    sinPermiso: "Tu rol no permite ver presupuestos.",
    vacio: "Todavía no tienes presupuestos. Prepara el primero: eliges el cliente, añades líneas y la aplicación calcula el IVA.",
    vacioFiltro: "No hay presupuestos en este estado.",
    vacioPendientes: "No hay presupuestos aceptados pendientes de facturar.",
    buscar: "Buscar por número o cliente",
    sinResultados: "Ningún presupuesto de esta lista coincide con la búsqueda.",
    pendientesDeFacturar: "Pendientes de facturar",
    // OD-0062 E-08: enviados cuya validez acaba en los próximos 7 días.
    caducanPronto: "Caducan pronto",
    vacioCaducanPronto: "Ningún presupuesto enviado caduca en los próximos 7 días.",
    // OD-0062 E-06: la lista del filtro elegido, en Excel.
    descargar: "Descargar Excel",
    descargando: "Preparando el Excel…",
    errorDescarga: "No se ha podido preparar el Excel. Vuelve a intentarlo.",
    vacioTitulo: "Prepara tu primer presupuesto",
    crearPrimero: "Crear mi primer presupuesto",
    filtro: "Filtrar por estado",
    todos: "Todos",
    todosLosEstados: "Todos los estados",
    estados: {
      DRAFT: "Borrador",
      SENT: "Enviado",
      ACCEPTED: "Aceptado",
      REJECTED: "Rechazado",
      EXPIRED: "Caducado",
      CANCELLED: "Cancelado",
    },
    cuotaMes: (usados: number, total: number, reinicio: string) => `${usados} de ${total} presupuestos creados este mes · se reinicia el ${reinicio}`,
    cuotaAbiertos: (usados: number, total: number) => `${usados} de ${total} presupuestos abiertos`,
    cuotaMesSinLimite: (usados: number) => `${usados} presupuestos creados este mes`,
    cuotaAbiertosSinLimite: (usados: number) => `${usados} presupuestos abiertos`,
    avisoCuota: "Te queda poco margen en tu plan este mes.",
    // OD-FREE-004 (propietario, 2026-09-21): que no se sienta atrapado; decir
    // cómo liberar un hueco. La oferta de Factura+ se añade cuando se pueda
    // contratar (OD-0014): no se ofrece lo que aún no se puede comprar.
    cuotaLlenaAbiertos: (limite: number) =>
      `Tienes ${limite} presupuestos abiertos, el máximo de tu plan. Para preparar otro, libera un hueco: abre uno y márcalo como aceptado o rechazado, o cancela uno que ya no vaya a salir. Los caducados no cuentan.`,
    cuotaLlenaMes: (limite: number, reinicio: string) =>
      `Has creado los ${limite} presupuestos de este mes. Podrás crear más a partir del ${reinicio}.`,
    validoHasta: (fecha: string) => `Válido hasta el ${fecha}`,
    sinValidez: "Sin fecha de validez",
    total: (importe: string) => `${importe} €`,
    // Lo que la fila dice de su facturación, sin entrar en la ficha.
    // Con anticipos, la final no es la única factura del presupuesto: se nombran todas.
    facturado: (numero: string | null, anticipos: readonly string[]) =>
      numero === null
        ? "Facturado"
        : anticipos.length === 0
          ? `Facturado en ${numero}`
          : `Facturado en ${numero}, con ${anticipos.length === 1 ? "el anticipo" : "los anticipos"} ${anticipos.join(", ")}`,
    facturaEnBorrador: "Factura en borrador",
    facturaAnulada: (numero: string | null) => (numero === null ? "Factura anulada" : `Factura ${numero} anulada`),
    anticipos: (porcentaje: string) => `Anticipos: ${sinCeros(porcentaje)} % facturado`,
    anticipoEnBorrador: "anticipo en borrador sin emitir",
  },
  /**
   * Botón «Enviar» de presupuestos y facturas (2026-10-22). El correo sale de
   * la cuenta del usuario: Faktelio no envía nada, y eso se dice.
   */
  enviarDocumento: {
    titulo: "Enviar al cliente",
    boton: "Enviar",
    detalle: "Se abre tu correo o WhatsApp con el PDF para que se lo mandes al cliente desde tu propia cuenta. Faktelio no envía nada por ti.",
    preparando: "Preparando el PDF…",
    errorPdf: "No hemos podido preparar el PDF. Inténtalo de nuevo.",
    reintentar: "Reintentar",
    para: "Email del cliente",
    ayudaPara: "Si no lo tienes guardado, escríbelo aquí. No se guarda en su ficha.",
    compartir: "Compartir el PDF (WhatsApp, Telegram, correo…)",
    ayudaCompartir: "Se abre la ventana de compartir de tu móvil u ordenador con el PDF adjunto: salen las apps que tengas instaladas. Si eliges el correo y no aparece el email del cliente, escríbelo.",
    desdeNavegador: "O ábrelo en tu correo: el PDF se descarga para que lo adjuntes.",
    gmail: "Gmail",
    outlook: "Outlook / Hotmail",
    local: "Programa de correo",
    whatsapp: "WhatsApp",
    whatsappAl: (numero: string) => `O por WhatsApp, al ${numero} de su ficha: el PDF se descarga para que lo adjuntes.`,
    whatsappSinNumero: "O por WhatsApp: eliges el contacto y el PDF se descarga para que lo adjuntes.",
    descargado: (nombre: string) =>
      `Se ha descargado ${nombre}. Adjúntalo en el correo que se ha abierto: arrástralo desde Descargas o usa el clip.`,
    descargadoChat: (nombre: string) =>
      `Se ha descargado ${nombre}. Adjúntalo en el chat de WhatsApp con el clip (en el ordenador, también arrastrándolo).`,
    asuntoPresupuesto: (numero: string, emisor: string) => `Presupuesto ${numero} de ${emisor}`,
    cuerpoPresupuesto: (numero: string, emisor: string) =>
      `Hola:\n\nTe adjunto el presupuesto ${numero}.\n\nUn saludo,\n${emisor}`,
    asuntoFactura: (numero: string, emisor: string) => `Factura ${numero} de ${emisor}`,
    cuerpoFactura: (numero: string, emisor: string) => `Hola:\n\nTe adjunto la factura ${numero}.\n\nUn saludo,\n${emisor}`,
    borrador:
      "Para enviarlo, márcalo antes como enviado: así el cliente recibe la copia congelada, la misma que queda guardada en Faktelio.",
    marcarYPreparar: "Marcar como enviado y preparar el correo",
    despuesDeMarcar: "Después podrás enviarlo por correo con el PDF adjunto.",
  },
  presupuesto: {
    // OD-0058 D-06: la oportunidad de la que salió, con su probabilidad.
    deOportunidad: (titulo: string) => `Oportunidad: ${titulo}`,
    verOportunidad: "Ver la oportunidad",
    // OD-0048-ENMIENDA-01: el presupuesto se guardo, pero no quedo enlazado.
    sinEnlaceOportunidad: "El presupuesto se ha guardado, pero no se ha podido enlazar a la oportunidad. Vuelve a intentarlo desde la oportunidad.",
    tituloNuevo: "Nuevo presupuesto",
    titulo: (numero: string) => `Presupuesto ${numero}`,
    volver: "Volver a presupuestos",
    cliente: "Cliente",
    eligeCliente: "Escribe para buscar un cliente",
    ayudaCliente: "Escribe parte del nombre o del NIF y elige de la lista.",
    clienteNoElegido: "Eso no corresponde a un solo cliente. Elige uno de la lista que sale al escribir: puedes buscar por nombre o por NIF.",
    sinClientes: "Primero necesitas un cliente. Créalo en la sección Clientes.",
    // OD-0062 E-01: en Presupuestos el cliente se puede crear escribiendo su nombre.
    sinClientesCrear: "Todavía no tienes clientes. Escribe su nombre aquí y créalo, sin NIF: sus datos fiscales los completas cuando le factures.",
    parecidos: "Ya tienes clientes con un nombre parecido. ¿Es uno de estos?",
    yaExiste: "Ya tienes un cliente con ese nombre:",
    crearCliente: (nombre: string) => `Crear el cliente «${nombre}»`,
    creandoCliente: "Creando el cliente…",
    crearClienteAyuda: "Se crea sin NIF y ocupa su plaza de cliente. Para facturarle tendrás que completar sus datos fiscales.",
    clienteCreado: (nombre: string) => `Cliente «${nombre}» creado y elegido.`,
    sinPermisoCliente: "Tu rol no permite crear clientes.",
    // OD-0062 E-07: los totales mientras se escribe, calculados por la API.
    calculando: "Calculando…",
    totalIncompleto: "Completa las líneas para ver el total.",
    validUntil: "Válido hasta (opcional)",
    ayudaValidUntil: "Si lo dejas vacío, el presupuesto no caduca solo.",
    notes: "Notas para el cliente (opcional)",
    lineas: "Líneas",
    anadirLinea: "Añadir línea",
    quitarLinea: "Quitar",
    sinLineas: "Añade al menos una línea para poder enviarlo.",
    producto: "Producto del catálogo (opcional)",
    buscaProducto: "Escribe para buscar un producto",
    ayudaProducto: "Si lo dejas vacío, escribes tú la descripción y el precio.",
    lineaLibre: "Línea libre (escribo yo el texto)",
    quitarProducto: "Quitar el producto de esta línea",
    descripcion: "Descripción",
    cantidad: "Cantidad",
    precio: "Precio unitario sin IVA (€)",
    descuento: "Descuento",
    descuentos: { NONE: "Sin descuento", AMOUNT: "Importe (€)", PERCENT: "Porcentaje (%)" },
    valorDescuento: "Valor del descuento",
    iva: "IVA",
    tipoIva: "Tipo",
    causaIva: "Causa (código AEAT)",
    lineaRetiene: "Esta línea lleva retención de IRPF",
    retencionTitulo: "Retención de IRPF",
    retencion: "Retención aplicada",
    retenciones: { NINGUNA: "Sin retención", "1500": "15 %", "700": "7 %" },
    motivoRetencion: "Motivo del 7 %",
    motivos: {
      INICIO_ACTIVIDAD_COMUNICADO: "Inicio de actividad, comunicado al pagador",
      EPIGRAFE_IAE_ART_95_1: "Actividad del artículo 95.1 del reglamento de IRPF",
    },
    sugerencia: "Por tus datos y los del cliente, lo normal sería aplicar el 15 %. Decídelo tú: la aplicación no lo aplica sola.",
    aplicarSugerencia: "Aplicar el 15 %",
    pendienteRetencion: "Hay líneas con retención pero no has elegido el tipo. Elígelo para ver los totales y poder enviarlo.",
    totalesTitulo: "Totales",
    baseTotal: "Base",
    ivaTotal: "IVA",
    retencionImporte: (tipo: string) => `Retención de IRPF (${tipo} %)`,
    totalFactura: "TOTAL",
    totalACobrar: "A cobrar",
    // Lo que el cliente ingresa de verdad cuando hay retención (OD-0029 D-06).
    // En el documento que recibe el cliente sigue siendo "A cobrar".
    clientePaga: "El cliente paga",
    explicacionTotales: "El total es base más IVA. La retención no cambia ese total: se descuenta de lo que te paga el cliente.",
    ayudaClientePaga: (iva: string, retencion: string) =>
      `Incluye ${iva} € de IVA y descuenta ${retencion} € de retención de IRPF.`,
    desgloseVer: "Ver desglose de impuestos",
    desglose: "Desglose por tipo de IVA",
    desgloseFila: (etiqueta: string, base: string, cuota: string | null) =>
      cuota === null ? `${etiqueta} · base ${base} €` : `${etiqueta} · base ${base} € · cuota ${cuota} €`,
    sinTotales: "Guarda el presupuesto para ver los totales.",
    lineaImporte: (importe: string) => `Importe de esta línea, sin IVA: ${importe} €`,
    lineaConDescuento: (bruto: string, descuento: string, importe: string) =>
      `${bruto} € − ${descuento} € de descuento = ${importe} € sin IVA`,
    guardar: "Guardar borrador",
    guardando: "Guardando…",
    revisar: "Guardar y revisar el documento",
    ayudaRevisar: "Verás el presupuesto tal como lo recibirá el cliente, con los importes ya calculados. Sigue siendo un borrador: puedes volver y cambiarlo.",
    ayudaNotas: "Condiciones, plazos, forma de pago… Lo que escribas aquí sale en el documento del cliente.",
    creado: "Borrador creado.",
    guardado: "Borrador guardado.",
    enviar: "Marcar como enviado",
    enviarDetalle: "Al marcarlo como enviado se congelan los importes y tus datos y los del cliente, tal como se los entregas. Después ya no se puede editar: se duplica.",
    enviado: "Marcado como enviado.",
    aceptar: "El cliente lo acepta",
    rechazar: "El cliente lo rechaza",
    aceptado: "Presupuesto aceptado.",
    rechazado: "Presupuesto rechazado.",
    cancelar: "Cancelar el presupuesto",
    motivoCancelar: "Motivo de la cancelación",
    confirmarCancelar: "Cancelar el presupuesto",
    cancelado: "Presupuesto cancelado.",
    duplicar: "Duplicar en un borrador nuevo",
    facturarTitulo: "Facturar",
    facturarDetalle: "Crea un borrador de factura con el cliente, las líneas y la retención de este presupuesto. Cada presupuesto se factura una sola vez.",
    facturar: "Crear factura",
    duplicado: "Se ha creado un borrador nuevo a partir de este.",
    imprimir: "Imprimir o guardar en PDF",
    volverAlEditor: "Volver al presupuesto",
    vistaImprimible: "Vista para el cliente",
    copiaCongelada: "Esto es la copia que se congeló al enviarlo.",
    emisorFalta: "Antes de enviar un presupuesto rellena tus datos fiscales.",
    irAEmisor: "Ir a Datos fiscales",
    noEditable: "Un presupuesto enviado no se edita. Duplícalo para preparar otro.",
    caducado: "Este presupuesto ha caducado.",
    noEncontrado: "Este presupuesto ya no existe.",
    sinPermiso: "Tu rol no permite modificar presupuestos.",
    conflicto: "Alguien lo ha cambiado a la vez. Vuelve a abrirlo.",
    cancelarAccion: "Cancelar",
    documento: {
      tipo: "Presupuesto",
      numeroEtiqueta: "Número",
      fechaEtiqueta: "Fecha",
      validezEtiqueta: "Válido hasta",
      formaPago: "Forma de pago",
      iban: (iban: string) => `IBAN: ${iban}`,
      baseImponible: "Base imponible",
      irpf: (tipo: string) => `Retención IRPF (${tipo} %)`,
      numero: (numero: string) => `Presupuesto ${numero}`,
      fecha: (fecha: string) => `Fecha: ${fecha}`,
      validez: (fecha: string) => `Válido hasta: ${fecha}`,
      emisor: "Emisor",
      cliente: "Cliente",
      concepto: "Concepto",
      cantidad: "Cant.",
      precio: "Precio",
      descuento: "Descuento",
      base: "Importe",
      ayudaImporte: "Importe = cantidad × precio − descuento, sin IVA.",
      nota: "Este documento es un presupuesto: no es una factura.",
      borrador: "Borrador: los datos de tu empresa y del cliente se congelarán cuando lo marques como enviado.",
    },
    errores: {
      REQUIRED: "Este dato es obligatorio.",
      TOO_LONG: "Es demasiado largo.",
      INVALID_CHARACTERS: "Contiene caracteres no permitidos.",
      INVALID: "Revisa este dato.",
      INVALID_NUMBER: "Escribe un número como 1,5.",
      TOO_MANY_DECIMALS: "Tiene demasiados decimales.",
      OUT_OF_RANGE: "El valor está fuera de lo admitido.",
      DATE_INVALID: "Escribe una fecha válida.",
      DATE_IN_PAST: "La fecha ya ha pasado.",
      TOO_MANY_LINES: "Demasiadas líneas para tu plan.",
      NO_LINES: "Añade al menos una línea.",
      VAT_RATE_NOT_IN_CATALOG: "Ese tipo de IVA no existe.",
      CAUSE_REQUIRED: "Elige la causa.",
      CAUSE_NOT_IN_CATALOG: "Esa causa no existe.",
      CUSTOMER_NOT_FOUND: "Ese cliente ya no existe.",
      CUSTOMER_ARCHIVED: "Ese cliente está archivado.",
      PRODUCT_NOT_FOUND: "Ese producto ya no existe.",
      DISCOUNT_EXCEEDS_AMOUNT: "El descuento no puede superar el importe de la línea.",
      DISCOUNT_PERCENT_OUT_OF_RANGE: "El porcentaje de descuento no es válido.",
      WITHHOLDING_RATE_MISSING: "Elige el tipo de retención.",
      WITHHOLDING_WITHOUT_BASE: "Ninguna línea lleva retención.",
      WITHHOLDING_RATE_NOT_IN_CATALOG: "Ese tipo de retención no existe.",
      REASON_REQUIRED: "Elige el motivo del 7 %.",
      NOT_ALLOWED: "Este dato no se puede cambiar desde aquí.",
      CATALOG_VERSION_UNKNOWN: "No hay tipos de IVA para esta fecha.",
    } as Readonly<Record<string, string>>,
  },
  producto: {
    tituloNuevo: "Nuevo producto o servicio",
    tituloEditar: "Editar producto o servicio",
    volver: "Volver a productos",
    code: "Código",
    ayudaCode: "Corto y único: letras, cifras y . _ - /. Por ejemplo HORA-01.",
    description: "Descripción",
    ayudaDescription: "Es el texto que aparecerá en la línea del presupuesto.",
    unitPrice: "Precio unitario sin IVA (€)",
    ayudaPrecio: "Hasta 4 decimales, con coma. Por ejemplo 45,50.",
    tratamiento: "IVA",
    tratamientos: {
      SUJETA_NO_EXENTA: "Con IVA",
      SUJETA_EXENTA: "Exenta de IVA",
      NO_SUJETA: "No sujeta a IVA",
    },
    tipo: "Tipo de IVA",
    tipoOpcion: (tipo: string) => `${tipo} %`,
    causa: "Causa (código AEAT)",
    // DF-04: los textos oficiales de E1..E6 y N1/N2 aún no están registrados; no se escriben de memoria.
    ayudaCausa: "Elige el código según el diseño de registro de la AEAT. Si tienes dudas, consúltalo con tu asesor.",
    ayudaExenta: "Úsalo solo si la operación está exenta o no sujeta por ley. La responsabilidad de elegirlo es tuya.",
    retencion: "Normalmente lleva retención de IRPF",
    ayudaRetencion: "Solo es una sugerencia al preparar presupuestos: se decide en cada documento.",
    guardar: "Guardar",
    guardando: "Guardando…",
    creado: "Producto creado.",
    guardado: "Cambios guardados. Los presupuestos ya hechos no cambian.",
    duplicado: "Ya tienes un producto con ese código.",
    abrirExistente: "Abrir el producto existente",
    desactivar: "Desactivar",
    activar: "Volver a activar",
    desactivarDetalle: "Un producto desactivado no aparece al preparar presupuestos. Sigue contando para el máximo de tu plan.",
    desactivado: "Producto desactivado.",
    activado: "Producto activado.",
    eliminar: "Eliminar",
    eliminarPregunta: "¿Eliminar este producto? No se puede deshacer.",
    siEliminar: "Sí, eliminar",
    cancelar: "Cancelar",
    enUso: "Está en algún presupuesto: no se puede eliminar, solo desactivar.",
    noEncontrado: "Este producto ya no existe.",
    sinPermiso: "Tu rol no permite modificar productos.",
    sinCatalogo: "No se ha podido cargar la lista de tipos de IVA. Vuelve a intentarlo.",
    errores: {
      REQUIRED: "Este dato es obligatorio.",
      TOO_LONG: "Es demasiado largo.",
      INVALID_CHARACTERS: "Contiene caracteres no permitidos.",
      INVALID_NUMBER: "Escribe un importe como 45,50.",
      TOO_MANY_DECIMALS: "Como mucho 4 decimales.",
      OUT_OF_RANGE: "El precio tiene que estar entre 0 y 99.999.999,9999 €.",
      VAT_RATE_NOT_IN_CATALOG: "Ese tipo de IVA no existe.",
      CAUSE_REQUIRED: "Elige la causa.",
      CAUSE_NOT_IN_CATALOG: "Esa causa no existe.",
      NOT_ALLOWED: "Este dato no se puede cambiar desde aquí.",
    } as Readonly<Record<string, string>>,
  },
  aeat: {
    // El usuario responde de los datos de sus clientes; la app no afirma identidad (URS §10).
    explicacion:
      "Aquí solo comprobamos que el formato es correcto. Para confirmar que el número corresponde a ese nombre, compruébalo en la Sede de la Agencia Tributaria con tu Cl@ve, certificado o DNI electrónico.",
    comprobarNif: "Comprobar NIF y nombre en la AEAT",
    comprobarAlta: "Comprobar si la empresa está de alta",
  },
  seguridad: {
    titulo: "Verificación en dos pasos",
    cargando: "Comprobando tu seguridad…",
    error: "No se ha podido cargar el estado de tu seguridad.",
    reintentar: "Reintentar",
    activa: "Activada",
    activaDetalle: (n: number) =>
      `Al entrar te pediremos un código de tu app. Te quedan ${n} códigos de recuperación sin usar.`,
    inactiva:
      "Añade un código de tu móvil al entrar. Es obligatoria para editar los datos fiscales de tu empresa, porque se copian en cada factura que emitas.",
    activar: "Activar",
    activando: "Preparando…",
    paso1: "1. Abre tu app de autenticación (Google Authenticator, Microsoft Authenticator u otra), pulsa «+» y escanea este código.",
    qr: "Código QR para añadir la cuenta a tu app de autenticación",
    sinApp: "¿Aún no tienes la app? Descarga Google Authenticator, es gratuita:",
    descargarEn: "Descargar en",
    googlePlay: "Google Play",
    appStore: "App Store",
    abrirEnApp: "Si estás en el móvil: abrir directamente en la app",
    claveManual: "¿No puedes escanearlo? Escribe esta clave a mano:",
    copiar: "Copiar clave",
    copiada: "Clave copiada",
    paso2: "2. Escribe el código de 6 cifras que muestra la app.",
    codigo: "Código de la app",
    faltaCodigo: "Escribe el código de 6 cifras.",
    confirmar: "Confirmar y activar",
    confirmando: "Comprobando…",
    cancelar: "Cancelar",
    codigosTitulo: "Guarda tus códigos de recuperación",
    codigosDetalle:
      "Sirven para entrar si pierdes el móvil. Cada uno vale una sola vez y no volverás a verlos: apúntalos o guárdalos en un gestor de contraseñas.",
    codigosAviso: "Por seguridad hemos cerrado tus demás sesiones abiertas en otros dispositivos.",
    guardados: "Ya los he guardado",
    yaActiva: "La verificación ya estaba activada.",
  },
  inicio: {
    titulo: "Primeros pasos",
    // OD-0015 D-06: qué es esto, por qué está vacío y cuál es la siguiente acción.
    detalle:
      "Para emitir tu primera factura hacen falta estos pasos. Los datos fiscales se congelan en cada factura emitida, así que conviene revisarlos con calma.",
    progreso: (hechos: number, total: number) => `${hechos} de ${total} completados`,
    hecho: "Hecho",
    pendiente: "Pendiente",
    // La serie no se configura: la crea la emisión (ensureSeriesAndChain). Decir
    // "aún no disponible" era falso desde que existe la emisión.
    automatica: "La crea Faktelio al emitir",
    irAlPaso: "Completar",
    irAFacturas: "Ir a facturas",
    guardadoSinConfirmar: "Guardado, falta confirmar",
    // OD-0039 E-14: los mismos estados que el asistente, de la misma lista.
    faltaSii: "Falta la pregunta del SII",
    enComprobacion: "En comprobación",
    conSii: "Llevas el SII: Faktelio no puede emitir tus facturas",
    // Lo que no se ha podido leer (red, permiso): no se afirma que falte.
    sinSaber: "Comprobando…",
    pasos: {
      cuenta: "Crear tu cuenta",
      email: "Confirmar tu email",
      organizacion: "Crear tu organización",
      fiscal: "Datos fiscales del emisor (NIF, razón social y domicilio)",
      autorizacion: "Autorizarnos ante la Agencia Tributaria",
      mfa: "Activar la verificación en dos pasos",
      serie: "Serie de facturación",
      cliente: "Tu primer cliente",
      factura: "Emitir tu primera factura",
    },
    tareasTitulo: "Tareas pendientes",
    tareasVacio: "Todo al día.",
    usoTitulo: "Uso del plan",
    usoDetalle: "Lo que llevas usado de los límites de tu plan.",
    // El estado vacío que pide OD-0015 D-06: qué es esto, por qué está vacío y
    // cuál es la siguiente acción. Antes aquí había cinco contadores a cero,
    // que no dicen ninguna de las tres cosas.
    usoSinNada:
      "Todavía no has usado nada de tu plan. En cuanto crees tu primer presupuesto o emitas tu primera factura, aquí verás cuánto te queda de cada cosa.",
    usoVerDetalle: "Ver los límites de tu plan",
    // Business: las oportunidades abiertas en vez de los límites (2026-10-18).
    // OD-0041 D-02: tampoco aqui «a pagar» ni «a ingresar».
    impuestos: {
      titulo: (periodo: string) => `Impuestos del ${periodo}`,
      parcial: "Estimación parcial e informativa: sale de tus facturas emitidas, sin tus gastos.",
      parcialConCompras: "Estimación parcial e informativa: sale de tus facturas emitidas y de las compras que has anotado.",
      // Peticion del propietario (2026-10-20): decir para quien es ese IVA, sin
      // confundirlo con la cuota que sale del modelo, que es otra cifra (D-02).
      ivaAyuda: "El IVA repercutido es IVA cobrado por cuenta de Hacienda. Lo que ingresas en el 303 es esta cifra menos el IVA de tus gastos.",
      retenciones: "Retenciones de IRPF",
      // OD-0056, enmienda de D-07 (2026-10-25): el soportado en Inicio, con su
      // aviso corto al lado. El largo, y la guia, siguen en Informes fiscales.
      soportado: "IVA soportado en tus compras anotadas",
      soportadoAviso: "Es el IVA que te han cobrado en las compras que has anotado. No es lo que te puedes deducir: eso lo decide tu gestoría.",
      soportadoSinCifra: {
        SIN_COMPRAS_ANOTADAS: "IVA soportado: aún no has anotado facturas de compra.",
        NINGUNA_EN_EL_TRIMESTRE: "IVA soportado: no has anotado ninguna compra en este trimestre.",
        COMPRAS_ILEGIBLES: "IVA soportado: sin datos suficientes para dar una cifra. Mira el detalle en Informes fiscales.",
      } as Record<string, string>,
      sinFacturas: "Aún no has emitido facturas en este trimestre.",
      sinDatos: "Sin datos suficientes para dar una cifra. Mira el detalle en Informes fiscales.",
      seDeclara: "Es el trimestre que se declara ahora.",
      proximoPlazo: "Próximo plazo",
      proximosPlazos: "Próximos plazos",
      verInforme: "Ver informes fiscales",
    },
    oportunidades: {
      titulo: "Oportunidades abiertas",
      abiertas: (n: number) => (n === 1 ? "1 oportunidad abierta" : `${n} oportunidades abiertas`),
      estimados: "Importes estimados, sin IVA. No son ingresos: eso lo dirán las facturas y los cobros.",
      verTodas: (n: number) => `Ver las ${n}`,
      vacio: "No tienes oportunidades abiertas. Apunta lo que quieres venderle a un cliente y síguelo desde aquí.",
    },
    resumen: {
      titulo: "Resumen del negocio",
      deLimite: (usados: number, limite: number) => `${usados} / ${limite}`,
      sinLimite: (usados: number) => `${usados} · sin límite`,
      creadosMes: "Presupuestos creados este mes",
      reinicio: (fecha: string) => `Se reinicia el ${fecha}`,
      abiertos: "Presupuestos abiertos",
      productos: "Productos",
      clientes: "Clientes",
      facturasMes: "Facturas emitidas este mes",
      facturasAnio: (usadas: number, total: number) => `${usadas} de ${total} este año`,
      facturasAnioSinLimite: (usadas: number) => `${usadas} este año`,
      esteMes: "Este mes",
      enviado: (n: number) => (n === 1 ? "1 presupuesto enviado" : `${n} presupuestos enviados`),
      aceptado: (n: number) => (n === 1 ? "1 presupuesto aceptado" : `${n} presupuestos aceptados`),
      noSonIngresos: "Son importes presupuestados, IVA incluido. No son ingresos: eso lo dirán las facturas y los cobros.",
      porEstado: "Presupuestos por estado",
      ultimos: "Últimos presupuestos",
      numero: "Número",
      cliente: "Cliente",
      estado: "Estado",
      total: "Total",
      crear: "Crear presupuesto",
      verTodos: "Ver todos",
      vacioTitulo: "Todavía no hay presupuestos",
      vacioDetalle: "Cuando prepares el primero, aquí verás cuántos tienes en cada estado y lo enviado y aceptado cada mes.",
    },
    // `faltanDetalle` se fue con la fila que contaba los pasos: el numero nunca
    // cuadraba con las filas de debajo porque contaba OTRA cosa. Ahora cada
    // tarea sale una vez y no hay nada que contar.
    verPasos: "Ver los pasos en Configuración",
    siguienteTitulo: "Qué viene ahora",
    siguienteDetalle:
      "No verás datos de ejemplo en ninguna pantalla: en un producto de facturación, un dato inventado puede acabar tomándose por real.",
  },
  cuenta: {
    titulo: "Mi cuenta",
    organizacion: "Organización",
    usuario: "Usuario",
    empresa: "Empresa",
    plan: "Plan",
    // Los ids internos no son datos del usuario: van plegados, para soporte.
    soporte: "Datos para soporte",
    soporteDetalle: "Si escribes a soporte, copia estos datos: nos ayudan a encontrar tu cuenta.",
    permisos: "Lo que puedes hacer",
    sinPermisos: "Tu usuario no tiene permisos en esta organización.",
    salir: "Cerrar sesión",
    saliendo: "Cerrando sesión…",
    // OD-0065 (2026-10-25): la misma web, instalada como aplicación. Los pasos
    // son los de la tarea «Instalar Faktelio en el ordenador» de /ayuda/.
    instalar: {
      titulo: "Faktelio como aplicación",
      detalle:
        "Instálala y se abre en su propia ventana, con su icono en el escritorio y en la barra de tareas. Es la misma Faktelio: tus datos siguen en tu cuenta y se actualiza sola.",
      boton: "Instalar Faktelio",
      instalando: "Instalando…",
      instalada: "Ya estás usando Faktelio como aplicación.",
      safariMac: "En Safari, abre el menú «Archivo» y elige «Añadir al Dock».",
      ios: "En Safari, pulsa el botón de compartir y elige «Añadir a pantalla de inicio».",
      otro:
        "En Microsoft Edge o Google Chrome, pulsa el icono de instalar que aparece a la derecha de la barra de direcciones. Si usas otro navegador, abre app.faktelio.com en uno de esos dos.",
    },
  },
  // OD-0063 D-01, D-03 y D-04: el acceso de la asesoría, desde las dos puntas.
  asesoria: {
    // El cliente.
    tuAsesoria: "Tu asesoría",
    explicacion:
      "Dale a tu asesoría acceso de lectura: verá y podrá descargar tus facturas, cobros, compras y clientes. No podrá emitir ni cambiar nada.",
    soloBusiness: "Dar acceso a tu asesoría está incluido en el plan Business.",
    codigo: "Código de tu asesoría",
    codigoAyuda: "Son 8 letras y números. Te lo da tu asesoría.",
    darAcceso: "Dar acceso",
    dandoAcceso: "Dando acceso…",
    avisoPermanente: (asesoria: string) =>
      `${asesoria} puede ver tus facturas, cobros, compras y clientes, y descargarlos. No puede emitir ni cambiar nada.`,
    desde: (fecha: string) => `Desde el ${fecha}.`,
    personas: "Quién de tu asesoría lleva tu empresa",
    sinPersonas: "Tu asesoría todavía no ha elegido a nadie.",
    retirar: "Retirar el acceso",
    retirando: "Retirando…",
    confirmarRetirar: "Tu asesoría dejará de ver tus datos ahora mismo. ¿Retirar el acceso?",
    sustituirTitulo: "Tu empresa ya ha dado acceso a otra asesoría",
    sustituirTexto: (anterior: string) => `Si das acceso a esta, ${anterior} lo pierde y su gente deja de entrar.`,
    sustituir: "Dar acceso a esta y quitárselo a la anterior",
    // La gestoría.
    programa: "Programa para asesorías",
    programaExplicacion:
      "Si eres una asesoría o gestoría, activa el perfil: tus clientes te darán acceso de lectura con tu código, y repartirás sus empresas entre tu equipo, hasta 3 personas por empresa.",
    aceptoCondiciones: "Acepto las condiciones del programa para asesorías",
    condicionesBorrador: (version: string) => `Versión ${version}. Es un borrador pendiente de revisión legal.`,
    activar: "Activar el perfil de asesoría",
    activando: "Activando…",
    tuCodigo: "Tu código",
    tuCodigoAyuda: "Dáselo a tus clientes: lo escriben en Configuración > Asesoría.",
    suspendido: "El perfil de asesoría de tu empresa está suspendido. Escribe a soporte.",
    clientes: "Empresas que te han dado acceso",
    sinClientes: "Todavía no te ha dado acceso ninguna empresa.",
    sinBusiness: "Sin acceso: necesita el plan Business.",
    llevan: "La llevan",
    nadie: "Nadie todavía",
    anadir: "Añadir",
    anadirA: (empresa: string) => `Persona de tu equipo para ${empresa}`,
    elegir: "Elige a alguien",
    quitar: (email: string) => `Quitar a ${email}`,
    renunciar: "Dejar de llevar esta empresa",
    confirmarRenunciar: (empresa: string) => `Nadie de tu asesoría podrá entrar en ${empresa}. ¿Dejar de llevarla?`,
    limite: "Cada empresa puede tener hasta 3 personas de tu asesoría.",
    error: "No se ha podido completar. Inténtalo otra vez.",
    cancelar: "Cancelar",
    // D-08: la pregunta al cliente que llego por el enlace. Nada marcado de antemano (D-11).
    invitacionTitulo: (asesoria: string) => `Te ha traído ${asesoria}`,
    invitacionTexto: (asesoria: string) =>
      `¿Le das acceso de lectura? ${asesoria} verá y podrá descargar tus facturas, cobros, compras y clientes. No podrá emitir ni cambiar nada, y se lo puedes retirar cuando quieras en Configuración > Asesoría.`,
    invitacionTuAsesoria: "tu asesoría",
    ahoraNo: "Ahora no",
    accesoDado: "Hecho. Se lo puedes retirar cuando quieras en Configuración > Asesoría.",
    // La gestoria: su enlace de alta.
    tuEnlace: "Tu enlace de alta",
    tuEnlaceAyuda:
      "Las empresas que se den de alta con este enlace quedan como clientes que has traído tú. Para ver sus datos, cada una tiene que darte acceso: se lo preguntamos al crearla.",
    // D-08 INVITAR DESDE EL PANEL: una invitacion y un recordatorio por direccion.
    invitar: "Invitar a un cliente",
    invitarAyuda: "Le mandamos un correo de tu parte con tu enlace de alta. Una vez, y un recordatorio si lo vuelves a pedir. No guardamos su dirección.",
    correoCliente: "Correo de tu cliente",
    enviarInvitacion: "Mandar invitación",
    enviandoInvitacion: "Mandando…",
    invitacionEnviada: (correo: string) => `Invitación enviada a ${correo}.`,
    recordatorioEnviado: (correo: string) => `Recordatorio enviado a ${correo}. Ya no se le puede mandar otro.`,
    // D-04 (enmienda 2026-10-28): el cliente invita a su gestoria desde SU correo.
    invitarMiAsesoria: "Invitar a mi asesoría",
    invitarMiAsesoriaAyuda:
      "¿Tu asesoría todavía no usa Faktelio? Se abrirá tu correo con la invitación ya escrita: solo tienes que poner su dirección. Cuando entre, te avisaremos aquí y le darás acceso con un clic.",
    abriendoCorreo: "Abriendo tu correo…",
    siNoSeAbre: "Si no se ha abierto tu correo, copia este enlace y mándaselo a tu asesoría:",
    correoAsunto: (empresa: string) => `${empresa} te invita a Faktelio como su asesoría`,
    correoCuerpo: (empresa: string, enlace: string, dias: number) =>
      [
        "Hola:",
        "",
        `En ${empresa} llevamos la facturación con Faktelio, y queremos que, como nuestra asesoría, puedas ver y descargar nuestras facturas, cobros, compras y clientes para la contabilidad.`,
        "",
        "Abre este enlace y date de alta, o entra si ya tienes cuenta:",
        "",
        enlace,
        "",
        `Después activa el perfil de asesoría en Configuración > Asesoría. En cuanto lo hagas, nos aparecerá tu nombre y te daremos acceso con un clic. El enlace sirve solo para esta invitación, durante ${dias} días.`,
        "",
        "Un saludo,",
        empresa,
      ].join("\n"),
    tuEmpresa: "Mi empresa",
    yaEsta: (asesoria: string) => `${asesoria} ya está en Faktelio`,
    yaEstaTexto: (asesoria: string) =>
      `Ha entrado con la invitación que le mandaste. ¿Le das acceso de lectura? ${asesoria} verá y podrá descargar tus facturas, cobros, compras y clientes. No podrá emitir ni cambiar nada, y se lo puedes retirar cuando quieras en Configuración > Asesoría.`,
    // La gestoria que entra por el enlace de su cliente.
    teHaInvitado: (cliente: string) => `${cliente} te ha invitado como su asesoría`,
    unCliente: "Un cliente",
    teHaInvitadoActiva: "Activa tu perfil de asesoría en Configuración > Asesoría y podrá darte acceso con un clic.",
    teHaInvitadoListo: "Ya puede darte acceso con un clic. Cuando lo haga, la verás en tus empresas cliente.",
    entendido: "Entendido",
    teEsperan: (clientes: string) => `Te ha invitado ${clientes}. Activa el perfil y podrá darte acceso con un clic.`,
    teEsperanActivo: (clientes: string) => `Te ha invitado ${clientes}: ya puede darte acceso con un clic.`,
  },
  // OD-0065, ampliacion del 2026-10-25: /instalar, a donde lleva el boton
  // «Descarga la aplicacion de escritorio» de faktelio.com. Sin sesion.
  instalar: {
    titulo: "Instala Faktelio en tu ordenador",
    despuesTitulo: "Después de instalar",
    despues: [
      "Faktelio se abre en su propia ventana. Pulsa «Crear una cuenta», o entra si ya tienes una.",
      "El correo de confirmación se abre en el navegador, no en la aplicación: confirma tu email allí y vuelve a la ventana de Faktelio para entrar.",
      "Para tenerla siempre a mano, en Edge marca el acceso directo en el escritorio y anclarla a la barra de tareas en la ventana que aparece al instalar.",
    ],
    sinInstalar: "También puedes usar Faktelio en el navegador, sin instalar nada.",
    crearEnNavegador: "Crear cuenta en el navegador",
    yaTengoCuenta: "Ya tengo cuenta",
  },
  capacidades: {
    "customer.read": "Ver clientes",
    "customer.write": "Crear y editar clientes",
    "quote.read": "Ver presupuestos",
    "quote.write": "Crear y editar presupuestos",
    "order.read": "Ver pedidos",
    "order.write": "Crear y editar pedidos",
    "invoice.read": "Ver facturas",
    "invoice.create_draft": "Preparar borradores de factura",
    "invoice.issue": "Emitir facturas",
    "invoice.rectify": "Rectificar facturas",
    "crm.read": "Ver el CRM",
    "crm.write": "Trabajar en el CRM",
    // OD-0024: billing.manage es la facturacion de la organizacion A SUS
    // CLIENTES. Decia «Gestionar la suscripción», que es organization.manage
    // (OD-0014 A-10): un ADMIN leia que podia pagar el plan y no podia.
    "billing.manage": "Gestionar la facturación a clientes",
    // OD-0040: el registro de cobros. Faltaban, y Mi cuenta los ensenaba en
    // crudo («payment.read»), que es lo que se veia en la captura del 2026-10-19.
    "payment.read": "Ver cobros",
    "payment.write": "Registrar cobros",
    "purchase.read": "Ver compras y proveedores",
    "purchase.write": "Anotar compras y pagos a proveedores",
    "users.manage": "Gestionar usuarios",
    "organization.manage": "Gestionar la organización",
    "fiscal_integrity.resolve": "Resolver incidencias de integridad fiscal",
    // RC-0025: la conversacion con la gestoria (OD-0067 D-05).
    "advisor_conversation.write": "Hablar con la asesoría",
  } as Readonly<Record<string, string>>,
  // OD-0060 D-04: quien está en varias empresas elige en cuál trabaja.
  selectorEmpresa: {
    etiqueta: "Empresa",
    cambiar: "Entrar en esta empresa",
    cambiando: "Cambiando…",
    noDisponible: "Ya no puedes entrar en esa empresa. Pide a quien la gestiona que te dé acceso.",
  },
  // OD-0063 D-05: la empresa activa es de la SESION, no de la pestana.
  cambioDeEmpresa: {
    otraPestanaTitulo: "Tienes Faktelio abierto en otra pestaña",
    otraPestanaTexto:
      "La empresa en la que estás es la misma en todas tus pestañas. Si cambias aquí, la otra pestaña pasará también a esta empresa, y se lo avisaremos para que la recargues.",
    cambiarIgualmente: "Cambiar igualmente",
    noCambiar: "No cambiar",
    cambiadaTitulo: "Has cambiado de empresa en otra pestaña",
    cambiadaTexto:
      "Esta pestaña sigue enseñando la empresa anterior, pero lo que hicieras aquí iría a la nueva. Recárgala para seguir.",
    recargar: "Recargar",
  },
  // OD-0063 D-06: el panel de la gestoria. Las cifras llegan ya formateadas.
  panelAsesoria: {
    titulo: "Empresas cliente",
    explicacion:
      "Las empresas que han dado acceso a tu asesoría. Las cifras de cada una son las de su pantalla de Informes fiscales para el trimestre que elijas.",
    trimestre: "Trimestre",
    conRechazos: "Con envíos rechazados por la AEAT",
    sinDescargar: "Sin descargar este trimestre",
    persona: "Quién la lleva",
    todasLasPersonas: "Todas las personas",
    actualizar: "Actualizar las cifras",
    notaCache:
      "Las cifras se guardan cinco minutos en esta pestaña para no volver a leer cada empresa al entrar y salir. «Actualizar las cifras» las vuelve a leer.",
    sinClientes: "Todavía no te ha dado acceso ninguna empresa.",
    sinCoincidencias: "Ninguna empresa cumple los filtros.",
    sinNombre: "Empresa sin nombre",
    nif: (nif: string) => `NIF ${nif}`,
    rechazados: (n: number) => (n === 1 ? "1 envío rechazado" : `${n} envíos rechazados`),
    noLaLlevas: "No la llevas tú: sus cifras las ve quien la lleva. Puedes repartirla en Configuración > Asesoría.",
    leyendo: "Leyendo sus cifras…",
    errorFila: "No se han podido leer las cifras de esta empresa.",
    yaNoDisponible: "Esta empresa ya no está disponible para tu asesoría: puede haber dejado Business o retirado el acceso.",
    emitidas: (n: number, base: string, iva: string) =>
      `${n === 1 ? "1 factura emitida" : `${n} facturas emitidas`} · base ${base} € · IVA repercutido ${iva} €`,
    soportado: (n: number, iva: string) => `IVA soportado ${iva} € (${n === 1 ? "1 compra" : `${n} compras`})`,
    aeatAlDia: "Envíos a la AEAT al día",
    aeat: (rechazados: number, pendientes: number) =>
      `Envíos a la AEAT: ${rechazados === 1 ? "1 rechazado" : `${rechazados} rechazados`}, ${pendientes === 1 ? "1 pendiente" : `${pendientes} pendientes`}`,
    sinDescargarAun: "Nadie lo ha descargado este trimestre",
    descargadoPor: (quien: string, dia: string) => `Descargado por ${quien} el ${dia}`,
    // OD-0067 D-03: lo que el cliente le dice de este trimestre.
    comentario: "Comentario del cliente",
    ti: "ti",
    laEmpresa: "la propia empresa",
    entrar: "Entrar",
    descargarExcel: "Descargar el Excel completo",
    descargarZip: "Descargar el ZIP con los PDF",
    descargando: "Descargando…",
    descargarTodas: (n: number) => (n === 1 ? "Descargar la de la lista" : `Descargar las ${n} de la lista`),
    descargarTanda: (desde: number, hasta: number, total: number) => `Descargar de la ${desde} a la ${hasta} (de ${total})`,
    descargandoTanda: (hechas: number, total: number) => `Descargando ${hechas} de ${total}…`,
    tandaHecha: (n: number) => (n === 1 ? "Descargada 1 empresa." : `Descargadas ${n} empresas.`),
    tandaConFallos: (bien: number, mal: number) =>
      `Descargadas ${bien}; ${mal === 1 ? "1 no ha salido" : `${mal} no han salido`}. El ZIP lleva un LEEME.txt con cuáles y por qué.`,
    nombreDelZip: (trimestre: string) => `empresas-${trimestre}.zip`,
    // El LEEME del ZIP de «Descargar todas»: texto plano, una empresa por linea.
    leeme: (trimestre: string, fallos: ReadonlyArray<{ readonly empresa: string; readonly motivo: string }>) =>
      [
        `Estas empresas no están en el ZIP del ${trimestre}:`,
        "",
        ...fallos.map((f) => `- ${f.empresa}: ${f.motivo}`),
        "",
        "Puedes volver a descargarlas una a una desde el panel.",
        "",
      ].join("\r\n"),
    franjaRegion: "Empresa en la que estás como asesoría",
    franja: (empresa: string) => `Estás en ${empresa} como asesoría`,
    volver: "Volver al panel",
    volviendo: "Volviendo…",
  },
  // Quien tiene sesión pero no puede entrar en su empresa: cerrada, o desactivado al pasar a Free.
  sinAcceso: {
    titulo: "No puedes entrar en esta empresa",
  },
  sinOrganizacion: {
    titulo: "Crea tu organización",
    detalle:
      "Tu cuenta está lista. El siguiente paso es crear la organización con la que vas a facturar.",
    nombre: "Nombre de la organización",
    // OD-0028 D-02: el nombre NO es la razón social y no sale en las facturas.
    ayudaNombre:
      "Cómo quieres llamar a tu espacio de trabajo, por ejemplo el nombre de tu negocio. No aparece en las facturas: los datos fiscales se piden después.",
    faltaNombre: "Escribe el nombre de la organización.",
    crear: "Crear organización",
    creando: "Creando…",
    // OD-0060: quien se registra para entrar en el equipo de otra empresa no
    // tiene que crear la suya.
    invitado: "¿Te han invitado al equipo de una empresa? Abre el enlace del correo de la invitación.",
  },
  // OD-0060 D-03 a D-06 y la enmienda E-01 de TDS-FREE-DOWNGRADE §3.
  equipo: {
    titulo: "Equipo",
    cargando: "Cargando el equipo…",
    noSePuedeVer: "No se ha podido cargar el equipo. Inténtalo de nuevo.",
    reintentar: "Reintentar",
    asientos: (ocupados: number, maximo: number): string =>
      maximo === 1 ? `${ocupados} de 1 usuario` : `${ocupados} de ${maximo} usuarios`,
    asientosDetalle: "Cuentan las personas con acceso y las invitaciones pendientes. Quien se quedó fuera al pasar a Free no ocupa.",
    libres: (n: number): string => (n === 1 ? "Te queda 1 usuario libre." : `Te quedan ${n} usuarios libres.`),
    sinLibres: "No te queda ningún usuario libre.",
    enElTecho: (techo: number): string => `Tu equipo tiene el máximo de ${techo} usuarios.`,
    soloUnaPersona:
      "Tu plan es para una persona. En Factura+ y Business puedes añadir usuarios a tu equipo desde «Plan y pago».",
    irAPlan: "Ver planes",
    roles: {
      OWNER: "Propietario",
      ADMIN: "Administración",
      SALES: "Ventas",
      BILLING: "Facturación",
      READ_ONLY: "Solo lectura",
    } as Readonly<Record<string, string>>,
    // Lo que cada rol puede hacer, en una línea (matriz de OD-0024).
    rolesAyuda: {
      ADMIN: "Todo menos el plan y el pago, e invita y gestiona al equipo.",
      SALES: "Clientes, presupuestos y CRM. Prepara facturas, pero no las emite.",
      BILLING: "Emite y rectifica facturas, registra cobros y anota compras.",
      READ_ONLY: "Lo ve todo y no cambia nada.",
    } as Readonly<Record<string, string>>,
    miembros: "Personas con acceso",
    tu: "Tú",
    sinCorreo: "Usuario sin correo visible",
    desactivado: "Sin acceso desde que la empresa pasó a Free",
    rolDe: (quien: string): string => `Rol de ${quien}`,
    cambiarRol: "Cambiar rol",
    cambiandoRol: "Cambiando…",
    quitar: "Quitar del equipo",
    confirmarQuitar: (quien: string): string => `Sí, quitar a ${quien}`,
    quitarDetalle:
      "Dejará de poder entrar en esta empresa. No se borra nada: sigue apareciendo como autor de lo que hizo, y su asiento queda libre.",
    quitando: "Quitando…",
    cancelar: "Cancelar",
    reactivar: "Devolver el acceso",
    reactivando: "Devolviendo…",
    reactivarSinAsiento: "Para devolverle el acceso necesitas un usuario libre.",
    invitaciones: "Invitaciones pendientes",
    sinInvitaciones: "No hay invitaciones pendientes.",
    caduca: (dia: string): string => `Caduca el ${dia}`,
    reenviar: "Reenviar",
    reenviando: "Reenviando…",
    yaReenviada: "Ya reenviada una vez",
    revocar: "Revocar",
    revocando: "Revocando…",
    invitar: "Invitar a alguien",
    invitarDetalle:
      "Le llega un correo con un enlace que caduca a los 7 días. Tiene que entrar con ese mismo correo; si aún no tiene cuenta, la crea con él.",
    email: "Correo",
    rol: "Rol",
    enviarInvitacion: "Enviar invitación",
    enviando: "Enviando…",
    faltaEmail: "Escribe el correo de la persona que quieres invitar.",
    invitada: (email: string): string => `Invitación enviada a ${email}.`,
    correoNoSalio:
      "La invitación está creada, pero el correo no ha salido. Pulsa «Reenviar» en la lista de pendientes.",
    reenviada: "Invitación reenviada, con un enlace nuevo. El anterior ya no sirve.",
    revocada: "Invitación revocada. Su asiento queda libre.",
    rolCambiado: "Rol cambiado.",
    quitado: "Esa persona ya no está en el equipo.",
    reactivado: "Acceso devuelto.",
    // D-05: comprar un usuario adicional. El precio lo da Stripe por la API.
    comprar: "Añadir un usuario a tu plan",
    consultandoPrecio: "Consultando el precio…",
    oferta: (precio: string): string => `Un usuario más cuesta ${precio} € al año, IVA incluido, y se renueva con tu plan.`,
    ofertaHoy: (dia: string | null): string =>
      dia === null
        ? "Hoy solo se cobra la parte que queda hasta la renovación, con la tarjeta de tu plan."
        : `Hoy solo se cobra la parte que queda hasta la renovación del ${dia}, con la tarjeta de tu plan.`,
    confirmarCompra: "Sí, añadir y pagar",
    comprando: "Añadiendo…",
    comprado: "Pago en marcha. El usuario nuevo quedará libre en cuanto se confirme, normalmente en unos segundos.",
    pagoPendiente:
      "Tu banco tiene que confirmar el pago. Hazlo desde «Plan y pago»: el usuario nuevo quedará libre cuando se complete.",
    // Enmienda de D-05 (2026-10-26): quitar un usuario libre, en el acto y sin abono.
    quitarAsiento: "Quitar un usuario libre de tu plan",
    quitarAsientoAviso: (quedan: number): string =>
      `Tu plan pasará a ${quedan === 1 ? "1 usuario" : `${quedan} usuarios`}. No se devuelve nada de lo ya pagado: la renovación cobrará uno menos.`,
    confirmarQuitarAsiento: "Sí, quitarlo",
    quitandoAsiento: "Quitando…",
    asientoQuitado: "Usuario quitado de tu plan. La renovación cobrará uno menos.",
  },
  // OD-0060 D-04: lo que ve quien abre el enlace del correo E-18.
  invitacion: {
    titulo: "Invitación al equipo",
    aceptando: "Aceptando la invitación…",
    rota: "El enlace de la invitación no está completo. Ábrelo otra vez desde el correo.",
    entrarPrimero: "Para aceptar la invitación, entra con el correo al que te ha llegado.",
    sinCuenta:
      "Si todavía no tienes cuenta, créala con ese mismo correo. Cuando la confirmes, vuelve a abrir el enlace de la invitación.",
    entrar: "Entrar",
    crearCuenta: "Crear cuenta",
    otraCuenta: "Salir y entrar con otra cuenta",
    saliendo: "Saliendo…",
    continuar: "Ir a Faktelio",
    reintentar: "Reintentar",
  },
  plan: {
    titulo: "Tu plan",
    planActual: "Plan actual",
    cargando: "Comprobando tu plan…",
    error: "No hemos podido comprobar tu plan. Vuelve a intentarlo.",
    reintentar: "Reintentar",
    sinPermiso: "Solo quien gestiona la organización puede contratar o cambiar el plan.",
    limite: "Has hecho muchas peticiones seguidas. Espera un momento y vuelve a intentarlo.",
    sinSuscripcion: "No tienes ningún plan de pago contratado.",
    // OD-0064 D-07 y D-05: en la prueba se puede elegir cualquiera de los dos, y el año empieza al pagar.
    prueba: {
      titulo: "Estás probando Business",
      detalle: (ultimoDia: string) =>
        `Tienes Business hasta el ${ultimoDia}, sin coste. Si eliges Factura+ o Business, tu suscripción empieza el día que pagas y la prueba termina. Si no eliges nada, pasas a Free, gratis y sin caducidad, y no se te cobra nada.`,
    },
    // La lista de planes la da el servidor; vacia = nada por encima con precio.
    nadaQueContratar: "Ahora mismo no hay ningún plan por encima del tuyo que puedas contratar desde aquí.",
    elegido: {
      FACTURA_PLUS: "Has elegido Factura+",
      BUSINESS: "Has elegido Business",
    },
    elegidoDetalle: "Guarda tus datos de facturación y pulsa el botón de tu plan para ir al pago.",
    // D3-b: sin datos fiscales no hay checkout. El texto NO promete nada
    // fiscal: dice lo que podemos hacer hoy, que es lo unico que sabemos.
    datosFiscales: {
      titulo: "Tus datos de facturación",
      intro: "Los necesitamos para emitirte la factura de la suscripción. No se cobra nada por guardarlos.",
      cargando: "Comprobando tus datos de facturación…",
      error: "No hemos podido comprobar tus datos de facturación. Vuelve a intentarlo.",
      guardar: "Guardar mis datos",
      guardando: "Guardando…",
      guardado: "Datos guardados.",
      errorGuardar: "No hemos podido guardar los datos. Revisa los campos y vuelve a intentarlo.",
      revisa: "Revisa los datos marcados.",
      editar: "Cambiar mis datos",
      // El territorio NO es el pais del domicilio, y la etiqueta lo dice:
      // confundirlos es el error que la Decision 3 corrige.
      territorio: "¿Dónde está establecida tu actividad?",
      territorioAyuda: "No es la dirección a la que quieres recibir el correo: es dónde estás establecido a efectos de impuestos.",
      territorios: {
        ES_PENINSULA_BALEARES: "Península o Baleares",
        ES_CANARIAS: "Canarias",
        ES_CEUTA_MELILLA: "Ceuta o Melilla",
        OTRO_UE: "Otro país de la Unión Europea",
        FUERA_UE: "Fuera de la Unión Europea",
      },
      condicion: "¿Contratas como…?",
      condiciones: {
        EMPRESARIO: "Empresa o autónomo",
        PARTICULAR: "Particular",
      },
      razonSocial: "Nombre o razón social",
      tipoId: "Tipo de identificación fiscal",
      tipos: {
        NIF_ES_PERSON: "NIF de persona física (España)",
        NIE_ES: "NIE (España)",
        NIF_ES_ENTITY: "NIF de empresa (España)",
        VAT_EU: "NIF-IVA de otro país de la UE",
        FOREIGN_ID: "Identificación fiscal extranjera",
        OTHER: "Otra",
      },
      numeroId: "Número",
      paisId: "País de la identificación fiscal",
      paisIdAyuda: "Dos letras, por ejemplo ES o DE. Tener un NIF español no significa estar establecido en España.",
      direccion: "Dirección",
      codigoPostal: "Código postal",
      ciudad: "Ciudad",
      provincia: "Provincia (si la hay)",
      pais: "País del domicilio",
      // Los motivos los nombra el SERVIDOR; aqui solo se traducen.
      motivos: {
        SIN_DATOS_FISCALES: "Rellena tus datos de facturación para poder contratar.",
        TERRITORIO_DESCONOCIDO: "Falta indicar dónde está establecida tu actividad.",
        CONDICION_DESCONOCIDA: "Falta indicar si contratas como empresa o como particular.",
        TERRITORIO_FUERA_DE_V1: "Todavía no podemos facturar en Canarias, Ceuta ni Melilla. Escríbenos y lo vemos.",
        OPERACION_FUERA_DE_V1: "Todavía no podemos facturar fuera de España. Escríbenos y lo vemos.",
      },
      noSePuedeContratar: "Guarda tus datos de facturación antes de contratar.",
      // En la tarjeta del plan, que desde el 2026-10-19 va ENCIMA del formulario.
      rellenaDebajo: "Antes de contratar, guarda tus datos de facturación aquí debajo.",
    },
    dondeEstaElPrecio: "Verás el precio y el IVA en la pantalla de pago antes de confirmar nada.",
    contratar: {
      FACTURA_PLUS: "Contratar Factura+",
      BUSINESS: "Contratar Business",
    },
    abriendoPago: "Abriendo el pago…",
    gestionar: "Gestionar el pago",
    subirDetalle: "Puedes pasarte a un plan superior cuando quieras. Se te cobrará solo la diferencia que queda de periodo.",
    subirA: {
      FACTURA_PLUS: "Pasar a Factura+",
      BUSINESS: "Pasar a Business",
    },
    cambiando: "Cambiando de plan…",
    terminada: "Tu suscripción ya no está activa. Puedes volver a contratar cuando quieras.",
    estadoSuscripcion: "Estado",
    hastaCuando: "Cubierto hasta",
    estados: {
      ACTIVE: "Al día",
      PAST_DUE: "Pago pendiente",
      CANCELED: "Cancelada, activa hasta el final del periodo",
      GRACE_EXPIRED: "Caducada por falta de pago",
      ENDED: "Terminada",
    },
    // La vuelta de Stripe NO confirma el cobro: lo confirma el webhook, que
    // llega por su cuenta. El texto no puede decir "ya está": tiene que decir
    // la verdad, que es que se está comprobando.
    confirmandoTitulo: "Estamos confirmando tu pago",
    confirmando: "Suele tardar menos de un minuto. Esta pantalla se actualiza sola.",
    confirmandoTarda: "Está tardando más de lo normal. Si no aparece en unos minutos, escríbenos y lo miramos.",
    cancelado: "No has completado el pago. No se ha cobrado nada.",
    errorAccion: "No hemos podido abrir la pantalla de pago. Vuelve a intentarlo.",
    sinConexion: "Sin conexión: vuelve a intentarlo cuando tengas red.",
    // TDS-FREE-DOWNGRADE §14: la bajada a Free. Los textos son los de
    // OD-FREE-002 §19, LITERALES: {limite} y {fecha} los rellena la pantalla
    // con lo que dice el servidor, nunca con un 10 escrito aqui.
    bajada: {
      titulo: "Tu cambio a Free",
      tituloDespues: "Tus clientes",
      cargando: "Comprobando tu cambio de plan…",
      error: "No hemos podido comprobar tu cambio de plan. Vuelve a intentarlo.",
      // 19.1 Antes del cutoff
      antes: "Tu plan cambiará a Free el {fecha}.",
      permite: "Free permite hasta {limite} clientes operativos.",
      selecciona: "Selecciona qué clientes quieres mantener operativos.",
      losDemas: "Los demás permanecerán guardados y accesibles, pero quedarán archivados por el plan.",
      puedesCambiar: "Puedes cambiar esta selección hasta que el downgrade sea efectivo.",
      // 19.2 Aviso de no rotación
      noRotacion:
        "Después del cambio a Free, los clientes archivados por el plan no podrán intercambiarse por otros mientras permanezcas en Free.",
      // 19.3 Aviso de fallback
      fallback:
        "Si no haces una selección antes del {fecha}, elegiremos automáticamente hasta {limite} clientes según tu actividad reciente. No eliminaremos ningún cliente ni documento.",
      // 19.4 Después del fallback
      despuesDelFallback:
        "Tu cambio a Free se ha completado. Como no realizaste una selección, hemos aplicado la selección automática. Hasta {limite} clientes permanecen operativos. Los demás continúan guardados como archivados por el plan.",
      // 19.5 Después del upgrade
      despuesDelUpgrade:
        "Tu nuevo plan ya está activo. Los clientes que quedaron archivados exclusivamente por el Plan Free han recuperado automáticamente el estado que tenían antes del downgrade.",
      // Lo que no dice la OD y la pantalla necesita.
      caben: "Tienes {activos} clientes operativos: caben todos en Free y no tienes que elegir nada.",
      elegidos: "{n} de {limite} elegidos",
      sinActividad: "sin actividad",
      ultimaActividad: "Última actividad: {fecha}",
      guardar: "Guardar mi selección",
      guardando: "Guardando…",
      guardada: "Selección guardada. Puedes cambiarla hasta el {fecha}.",
      demasiados: "Free permite hasta {limite} clientes operativos. Quita alguno de la selección.",
      errorGuardar: "No hemos podido guardar la selección. Vuelve a intentarlo.",
      congelada: "El cambio a Free ya está en curso y la selección no se puede cambiar.",
    },
  },
  facturas: {
    titulo: "Facturas",
    cargando: "Cargando tus facturas…",
    error: "No se han podido cargar tus facturas.",
    sinPermiso: "Tu rol no permite ver facturas.",
    vacioTitulo: "Prepara tu primera factura",
    vacioDetalle: "Empieza un borrador o conviértelo desde un presupuesto aceptado: se copian el cliente, las líneas y la retención.",
    nueva: "Nuevo borrador",
    desdePresupuesto: "Facturar un presupuesto",
    filtro: "Filtrar por estado",
    todas: "Todas",
    estados: {
      DRAFT: "Borrador",
      ISSUED: "Emitida",
      VOIDED: "Anulada",
      DISCARDED: "Descartado",
      // URS §28: derivado, no una columna. La factura sigue emitida y con su
      // numero; lo que dice es que existe una rectificativa suya ya emitida.
      RECTIFIED: "Rectificada",
    } as Readonly<Record<string, string>>,
    vacioFiltro: "No hay facturas en este estado.",
    sinNumero: "Sin número",
    sinTotal: "sin total todavía",
    deUnPresupuesto: "desde un presupuesto",
    cuotaBorradores: (usados: number, total: number) => `${usados} de ${total} borradores de factura`,
    cuotaBorradoresSinLimite: (usados: number) => `${usados} borradores de factura`,
    cuotaLlena: "Has llegado al máximo de borradores de tu plan. Emite o descarta alguno para preparar otro.",
  },
  // OD-0052: suplidos. Dinero pagado en nombre y por cuenta del cliente.
  suplidos: {
    titulo: "Suplidos",
    ayuda:
      "Lo que has pagado en nombre y por cuenta de tu cliente, como una tasa o un certificado. No lleva IVA ni retención y no se envía a la Agencia Tributaria: se suma a lo que te paga.",
    // La guía de la propia pantalla (OD-0052 D-01): quien marca la casilla es
    // el usuario, así que tiene que saber qué está afirmando antes de marcarla.
    guia: {
      titulo: "¿Qué es un suplido y cuándo puedo usarlo?",
      secciones: [
        {
          titulo: "Qué es",
          parrafos: [
            "Dinero que has pagado en nombre y por cuenta de tu cliente, porque él te lo encargó, y que le repercutes por el importe exacto, sin ganar nada con ello.",
          ],
          puntos: [
            "Tasas del Registro Mercantil o de la Propiedad, tasas judiciales o administrativas.",
            "Honorarios de notaría o certificados pedidos a nombre de tu cliente.",
            "Impuestos de tu cliente que pagas tú por él, como el ITP o el AJD.",
            "Un burofax o un envío que haces en su nombre.",
          ],
        },
        {
          titulo: "Qué NO es un suplido",
          parrafos: [
            "Los gastos que tienes tú para hacer tu trabajo, aunque se los cobres aparte: desplazamientos, kilometraje, dietas, fotocopias, mensajería contratada por ti o tu material. Eso forma parte de tu servicio y va como una línea normal, con IVA y, si la hay, con retención.",
          ],
          puntos: [],
        },
        {
          titulo: "Requisitos (art. 78.Tres.3.º de la Ley del IVA)",
          parrafos: [],
          puntos: [
            "Lo pagas en nombre y por cuenta de tu cliente, con su encargo expreso.",
            "Puedes justificar el importe exacto: guarda la factura o el justificante, que lo normal es que esté a nombre de tu cliente.",
            "No te deduces el IVA que pudiera llevar ese gasto: no es un gasto tuyo.",
          ],
        },
        {
          titulo: "Qué efecto tiene en tu factura",
          parrafos: [],
          puntos: [
            "No es base imponible: no lleva IVA ni retención de IRPF.",
            "No se envía a la Agencia Tributaria. El registro VERI*FACTU y el QR dicen el total de la factura sin suplidos.",
            "Se suma a lo que te paga tu cliente: en la factura sale debajo del total, con el aviso legal, y al final el «Total a pagar».",
            "Por lo general tampoco cuenta como ingreso ni como gasto de tu actividad.",
          ],
        },
        {
          titulo: "Si tienes dudas",
          parrafos: [
            "Pregunta a tu gestoría antes de marcarlo. Si algo que no cumple los requisitos se factura como suplido, Hacienda puede considerarlo parte de la base y reclamar el IVA y la retención que faltaron.",
          ],
          puntos: [],
        },
      ],
    },
    leyenda: (n: number) => `Suplido ${n}`,
    concepto: "Concepto",
    importe: "Importe (€)",
    pagadoA: "A quién se pagó (opcional)",
    referencia: "Referencia (opcional)",
    ayudaReferencia: "El número del justificante o de la liquidación.",
    confirmacion: "Lo pagué en nombre y por cuenta de mi cliente, y tengo el justificante.",
    quitar: "Quitar suplido",
    anadir: "Añadir suplido",
    totalEtiqueta: "Suplidos",
    ayudaTotales: (importe: string) => `Suma ${importe} € de suplidos, sin IVA.`,
    lectura: (concepto: string, importe: string) => `${concepto} · ${importe} €`,
    soloBusiness: "Los suplidos son del plan Business.",
    errores: {
      REQUIRED: "Es obligatorio.",
      OUT_OF_RANGE: "Escribe un importe mayor que cero.",
      TOO_MANY: "Como mucho, 20 suplidos por factura.",
      INVALID: "Revisa este suplido.",
      NOT_ALLOWED_IN_RECTIFICATION: "Una factura rectificativa no lleva suplidos.",
      DISBURSEMENT_NOT_POSITIVE: "Un suplido tiene que ser mayor que cero.",
      DISBURSEMENT_NOT_ALLOWED_IN_RECTIFICATION: "Una factura rectificativa no lleva suplidos.",
    } as Readonly<Record<string, string>>,
  },
  // OD-0053: anticipos de un presupuesto aceptado, y la factura final que los descuenta.
  anticipos: {
    titulo: "Anticipos",
    invitacion: "¿Te han pagado una parte por adelantado? Hacienda pide la factura al cobrarla.",
    boton: "Facturar un anticipo",
    otro: "Facturar otro anticipo",
    porPorcentaje: "Un porcentaje del presupuesto",
    porImporte: "Lo que te han pagado",
    porcentaje: "Porcentaje (%)",
    importe: "Importe cobrado, IVA incluido (€)",
    ayudaImporte: "Lo convertimos en un porcentaje del total a cobrar del presupuesto. El borrador enseña el total exacto antes de emitirlo: puede diferir en algún céntimo por el redondeo.",
    fechaCobro: "Fecha del cobro",
    ayudaFechaCobro: "El día que te pagaron. Es la fecha de la operación de esta factura, y no puede ser futura.",
    crear: "Preparar el borrador del anticipo",
    creando: "Preparando…",
    cancelar: "Cancelar",
    // La guía plegada (D-07, pieza 2), como la de suplidos.
    guia: {
      titulo: "¿Qué es un anticipo y cuándo hay que facturarlo?",
      secciones: [
        {
          titulo: "Qué es",
          parrafos: [
            "Una parte del precio que tu cliente te paga antes de que termines el trabajo. Se factura como una factura normal, con su IVA y, si la hay, su retención, por la parte cobrada.",
          ],
          puntos: [],
        },
        {
          titulo: "Cuándo hay que facturarlo",
          parrafos: [],
          puntos: [
            "El IVA de un anticipo se devenga el día que lo cobras (art. 75.Dos de la Ley del IVA), y hay que expedir factura por él (art. 2.1 del Reglamento de facturación).",
            "Si tu cliente es una empresa o un profesional, como tarde el día 15 del mes siguiente al cobro (art. 11.1 del Reglamento de facturación).",
            "La factura lleva la fecha del cobro si es distinta de la de expedición.",
          ],
        },
        {
          titulo: "Cómo se descuenta después",
          parrafos: [
            "Cuando termines, «Facturar el resto» prepara la factura final con todas las líneas del presupuesto y descuenta de cada tipo de IVA la base que ya facturaste en los anticipos. Así el IVA de la operación se repercute una sola vez.",
          ],
          puntos: [],
        },
        {
          titulo: "Qué NO es",
          parrafos: [],
          puntos: [
            "No es un suplido: un suplido es lo que pagas tú en nombre de tu cliente, sin IVA.",
            "No es el cobro de una factura ya emitida: eso se anota en la propia factura, en «Cobros».",
          ],
        },
      ],
    },
    estado: (resumen: string, pendiente: string) => `Anticipos: ${resumen}. Pendiente de facturar: ${sinCeros(pendiente)} %.`,
    fila: (porcentaje: string, base: string | null, numero: string | null) =>
      `${sinCeros(porcentaje)} %${base === null ? "" : ` (${base} €)`}, ${numero ?? "borrador sin emitir"}`,
    resto: "Facturar el resto",
    restoDetalle: "Prepara la factura final con todas las líneas del presupuesto y descuenta lo ya facturado en anticipos.",
    abrir: "Abrir",
    bloqueos: {
      ADVANCE_DRAFT_PENDING: "Hay un anticipo sin emitir. Emítelo o descártalo antes de facturar el resto.",
      ADVANCE_RECTIFIED: "Un anticipo de este presupuesto tiene una rectificativa. En esta versión, la factura final se hace a mano.",
      FULLY_INVOICED_BY_ADVANCES: "Los anticipos ya facturan el 100 % del presupuesto: no hay factura final.",
      ALREADY_INVOICED: "Este presupuesto ya tiene su factura final.",
      PLAN_FEATURE_REQUIRED: "Este presupuesto tiene anticipos facturados. La factura final que los descuenta es del plan Business.",
    } as Readonly<Record<string, string>>,
    abrirFinal: "Abrir la factura final",
    // D-07, pieza 3: en el borrador de un anticipo.
    borradorTitulo: (porcentaje: string, presupuesto: string | null) =>
      `Anticipo del ${sinCeros(porcentaje)} %${presupuesto === null ? "" : ` del presupuesto ${presupuesto}`}`,
    emitirYa: "Emítela ya: el IVA se devenga el día que cobras.",
    lineasBloqueadas:
      "Las líneas, el cliente y la retención salen del presupuesto y no se cambian aquí. Para otro porcentaje, descarta este borrador y haz otro anticipo desde el presupuesto.",
    descuentosTitulo: "Anticipos descontados",
    descuento: (numero: string | null, base: string) => `${numero ?? "Anticipo"}: −${base} € de base`,
    totalEtiqueta: "Anticipos ya facturados",
    ayudaTotales: (importe: string) => `La base ya descuenta ${importe} € facturados en anticipos.`,
    errores: {
      REQUIRED: "Escribe un porcentaje o un importe.",
      ONE_OF_PERCENT_OR_AMOUNT: "Escribe un porcentaje o un importe, no los dos.",
      ADVANCE_PERCENT_OUT_OF_RANGE: "El porcentaje tiene que ser mayor que 0 y como mucho 100.",
      ADVANCE_EXCEEDS_TOTAL: "Con los anticipos que ya tiene, el presupuesto pasaría del 100 %.",
      ADVANCE_TOO_SMALL: "Con este porcentaje el anticipo sale a 0,00 €.",
      OUT_OF_RANGE: "Escribe un importe mayor que cero.",
      DATE_IN_FUTURE: "La fecha del cobro no puede ser futura.",
      DATE_INVALID: "Revisa la fecha.",
      INVALID_NUMBER: "Escribe un número con coma decimal, como 30 o 33,33.",
      ADVANCE_LINES_LOCKED: "Las líneas de un anticipo no se cambian: descártalo y haz otro.",
      ADVANCE_CUSTOMER_LOCKED: "El cliente de un anticipo es el del presupuesto.",
      ADVANCE_WITHHOLDING_LOCKED: "La retención de un anticipo es la del presupuesto.",
      NOT_ALLOWED_IN_ADVANCE: "Un anticipo no lleva suplidos: van en la factura final.",
    } as Readonly<Record<string, string>>,
    soloBusiness: "Los anticipos son del plan Business.",
  },
  factura: {
    tituloNuevo: "Nuevo borrador de factura",
    tituloBorrador: "Borrador de factura",
    tituloDescartado: "Borrador descartado",
    titulo: (numero: string) => `Factura ${numero}`,
    volver: "Volver a facturas",
    noEncontrada: "Esta factura no existe.",
    sinPermiso: "Tu rol no permite preparar facturas.",
    noEditable: "Esta factura ya no es un borrador y no se puede cambiar.",
    conflicto: "Alguien ha cambiado esta factura a la vez. Recárgala y vuelve a intentarlo.",
    sinEmisionTodavia: "Borrador: todavía sin número y sin enviar a la Agencia Tributaria.",
    desdePresupuesto: (numero: string) => `Creada desde el presupuesto ${numero}.`,
    ayudaCliente: "La factura se emitirá a nombre de este cliente. Para emitirla hará falta su NIF.",
    descripcion: "Descripción de la operación",
    ayudaDescripcion: "Qué has vendido o qué trabajo has hecho. Sale en la factura y se envía a la Agencia Tributaria.",
    propuesta: (texto: string) => `Propuesta a partir de las líneas: «${texto}»`,
    usarPropuesta: "Usar esta descripción",
    operationDate: "Fecha de la operación",
    ayudaOperationDate: "Solo si el trabajo o la venta fue en otra fecha distinta a la de la factura.",
    dueDate: "Vencimiento",
    ayudaDueDate: "Fecha en la que esperas cobrarla.",
    ayudaDueDatePropuesto:
      "Propuesto a 30 días, el plazo legal entre empresas cuando no se ha pactado otro. Puedes cambiarlo o dejarlo en blanco.",
    ayudaDueDatePlazo: (dias: number) =>
      dias === 0
        ? "Propuesto para hoy: en su ficha consta que paga al contado. Puedes cambiarlo o dejarlo en blanco."
        : `Propuesto a ${dias} días, el plazo de pago de su ficha. Puedes cambiarlo o dejarlo en blanco.`,
    guardar: "Guardar borrador",
    guardado: "Borrador guardado.",
    descartar: "Descartar borrador",
    descartarDetalle: "El borrador deja de estar activo y no cuenta en tu plan. No se borra: se conserva, como exige la Agencia Tributaria para los documentos previos a una factura.",
    confirmarDescartar: "Sí, descartarlo",
    descartado: "Borrador descartado.",
    descartadoDetalle: "Este borrador está descartado. Se conserva, pero ya no se puede editar ni emitir.",
    lineaLectura: (descripcion: string, cantidad: string) => `${descripcion} × ${cantidad}`,
    emitir: "Emitir factura",
    emitirDetalle: "Al emitirla recibe su número definitivo y se envía a la Agencia Tributaria. Después no se puede cambiar: los errores se corrigen con una factura rectificativa.",
    confirmarEmitir: "Sí, emitirla",
    emitiendo: "Emitiendo…",
    emitida: "Factura emitida.",
    fechaExpedicion: (fecha: string) => `Fecha de expedición: ${fecha}`,
    pendienteEnvio: "Pendiente de envío a la Agencia Tributaria.",
    /**
     * Lo que de verdad pasó con el registro. El CSV es el justificante: es lo
     * que hay que poder enseñar si alguien pregunta, así que se ve, no se
     * esconde detrás de un «enviada» a secas.
     */
    enviadaAeat: (csv: string) => `Registrada en la Agencia Tributaria. Justificante: ${csv}`,
    /*
      Una factura anulada tiene DOS registros ante la AEAT, y hay que poder
      ver los dos por separado: mientras la anulación no salga, la Agencia
      sigue teniendo esa factura como válida.
    */
    envioAlta: "El alta de la factura",
    envioAnulacion: "La anulación",
    enviadaAeatSinCsv: "Registrada en la Agencia Tributaria.",
    /**
     * «Aceptado con errores» (art. 16.4): la AEAT la anotó, pero con un
     * defecto que hay que subsanar. Decir sólo «registrada» sería esconderlo.
     */
    aceptadaConErrores: (csv: string) =>
      `Registrada en la Agencia Tributaria CON UN DEFECTO que hay que subsanar. Justificante: ${csv}`,
    rechazadaAeat: "La Agencia Tributaria ha rechazado este registro. Escribe a soporte: hay que subsanarlo.",
    /** Sin fila en la cola: no se sabe, y eso no es «enviada». */
    envioDesconocido: "No consta el estado de envío de este registro. Escribe a soporte.",
    verificacionTitulo: "Verificación en la Agencia Tributaria",
    masAcciones: "Más acciones",
    cabeceraClientePaga: (importe: string) => `El cliente paga ${importe} €`,
    qrTitulo: "QR tributario:",
    verDocumento: "Ver la factura en PDF",
    // TDS-FISCAL Parte 2 DQ-02 (decidida A): la frase larga bajo el QR.
    qrFrase: "Factura verificable en la sede electrónica de la AEAT",
    qrEtiqueta: "Código QR para comprobar la factura en la Agencia Tributaria",
    paraEmitirTitulo: "Qué falta para emitirla",
    paraEmitirDetalle: "Lo comprobamos antes de emitir para no gastar un número de factura en algo que no se puede enviar.",
    irADatosFiscales: "Ir a Datos fiscales",
    abrirCliente: "Abrir el cliente",
    motivos: {
      ISSUE_NOT_ALLOWED_FOR_ROLE: "Tu rol no puede emitir facturas. Pídeselo a quien administra la cuenta.",
      NOT_DRAFT: "Solo se emite un borrador.",
      ISSUER_MISSING: "Rellena los datos fiscales de tu empresa.",
      ISSUER_NOT_CONFIRMED: "Revisa y confirma los datos fiscales de tu empresa.",
      REPRESENTATION_REQUIRED:
        "Autorízanos a enviar tus facturas a la Agencia Tributaria en tu nombre, en Configuración › Autorización AEAT.",
      CUSTOMER_ARCHIVED: "El cliente está archivado. Elige otro o reactívalo.",
      CUSTOMER_TAX_ID_REQUIRED: "Añade el NIF del cliente en su ficha.",
      // D3-a: este cliente SI tiene identificacion fiscal. Decirle «añade el
      // NIF» le invita a poner uno que no le corresponde, y ademas no
      // arreglaria nada: el limite es nuestro, no suyo. El texto lo dice, no
      // promete fecha, y manda a soporte, que es lo unico que puede hacer.
      CUSTOMER_TAX_ID_NOT_SPANISH:
        "Este cliente tiene identificación fiscal de otro país. Todavía no podemos emitirle facturas: escríbenos y lo vemos.",
      DESCRIPTION_REQUIRED: "Escribe la descripción de la operación.",
      NO_LINES: "Añade al menos una línea.",
      WITHHOLDING_DECISION_REQUIRED: "Decide si aplicas retención de IRPF.",
      TOTALS_INVALID: "Revisa las líneas: con estos datos no se pueden calcular los importes.",
      SERIES_AMBIGUOUS: "Hay más de una serie de numeración activa. Escribe a soporte para resolverlo.",
      SII_ANSWER_REQUIRED: "Responde en Datos fiscales si llevas los libros de IVA en el SII.",
      SII_REGISTERED:
        "Has declarado que llevas los libros en el SII: la ley te obliga a usar un sistema del SII, así que Faktelio no emite tus facturas.",
      SIF_DECLARATION_MISSING:
        "Estamos terminando la certificación de esta versión de Faktelio. En cuanto esté, podrás emitir.",
    } as Readonly<Record<string, string>>,
    /** Abre la puerta del alta, que es donde están los tres pasos. */
    verQueFalta: "Ver qué falta",
    cuotaMes: (limite: number) => `Has emitido las ${limite} facturas de este mes que incluye tu plan.`,
    cuotaAnio: (limite: number) => `Has emitido las ${limite} facturas de este año que incluye tu plan.`,
    validacionFiscal: {
      INVALID_NIF: "El NIF del cliente o el de tu empresa no tiene el formato que admite la Agencia Tributaria.",
      INVALID_NAME: "El nombre del cliente o el de tu empresa es demasiado largo para la Agencia Tributaria (máximo 120 caracteres).",
      INVALID_DESCRIPTION: "La descripción de la operación no es válida (entre 1 y 500 caracteres).",
      EMPTY_BREAKDOWN: "La factura no tiene importes que declarar.",
      TOO_MANY_BREAKDOWN_LINES: "Hay más de 12 combinaciones distintas de IVA: la Agencia Tributaria no admite tantas en una factura.",
      EXEMPTION_CAUSE_MISSING: "Falta la causa de exención en alguna línea.",
      NON_SUBJECT_CAUSE_MISSING: "Falta la causa de no sujeción en alguna línea.",
      SURCHARGE_NOT_SUPPORTED: "El recargo de equivalencia todavía no está disponible.",
    } as Readonly<Record<string, string>>,
    validacionFiscalOtra: "Hay un dato que la Agencia Tributaria no admitiría. Revisa la factura.",
    // TDS-FISCAL Parte 4 §3: anular no es corregir. Se dice con todas las
    // letras, porque confundirlas es el error caro.
    anular: "Anular la factura",
    anularDetalle:
      "Anula una factura que no debió emitirse: la operación no existió o la emitiste por error. Si lo que está mal es el importe, no se anula: se hace una factura rectificativa.",
    anularEfecto:
      "La factura no se borra ni se edita: conserva su número, sigue en tus listados y se envía a la Agencia Tributaria un registro de anulación enlazado con ella.",
    anularSinSaber: "Comprobando si esta factura se puede anular…",
    anularMotivo: "Motivo de la anulación",
    anularMotivoAyuda: "Para qué sirve: queda guardado con la anulación, para ti y para una inspección. Máximo 500 caracteres.",
    confirmarAnular: "Sí, anularla",
    anulando: "Anulando…",
    anulada: "Factura anulada.",
    anuladaDetalle: (fecha: string, motivo: string) => `Anulada el ${fecha}. Motivo: ${motivo}`,
    // --- Rectificativa (TDS-FISCAL Parte 4 §2, DR4-08) ---
    //
    // El motivo lo ELIGE la persona y no se deduce del importe (URS §35), asi
    // que cada opcion se explica por lo que PASO, no por su clave legal.
    rectificar: "Rectificar",
    rectificarDetalle:
      "Rectificar no cambia esta factura: se emite otra, enlazada con ella, que corrige lo que estaba mal. Es lo que exige la norma, y es lo que verá la Agencia Tributaria.",
    rectificarMotivo: "¿Qué pasó?",
    rectificarMotivos: {
      R1: "Había un error en la factura (datos, importes o impuestos mal calculados)",
      R2: "El cliente ha entrado en concurso de acreedores",
      R3: "La deuda es incobrable y se ha seguido el procedimiento de la Agencia Tributaria",
      R4: "Otro motivo (descuento posterior, devolución, anulación parcial…)",
    } as Readonly<Record<string, string>>,
    rectificarModalidad: "¿Cómo quieres corregirla?",
    rectificarModalidades: {
      I: "Por diferencias: escribo solo lo que cambia (por ejemplo, −200 €)",
      S: "Por sustitución: escribo la factura correcta entera",
    } as Readonly<Record<string, string>>,
    rectificarModalidadAyuda:
      "Por diferencias es lo normal cuando sabes cuánto sobra o falta. Por sustitución, cuando es más fácil volver a escribir la factura como tendría que haber sido.",
    rectificarAccion: "Preparar la rectificativa",
    rectificando: "Preparando…",
    rectificativaDe: (numero: string) => `Rectificativa de la factura ${numero}`,
    rectificadaTitulo: "Esta factura ya tiene rectificativa",
    rectificadaDetalle:
      "Sigue siendo válida y conserva su número: lo que la corrige es otra factura, enlazada con ella. Rectificativas emitidas:",
    rectificativaAyudaI:
      "Escribe SOLO la diferencia, con su signo: −200 si bajas la base 200 €, +50 si la subes 50 €. Eso es exactamente lo que se envía a la Agencia Tributaria.",
    rectificativaAyudaS:
      "Escribe la factura correcta entera. Los importes que se corrigen los ponemos nosotros, tomados de la factura original.",
    fueraDePlazoTitulo: "Han pasado más de cuatro años",
    fueraDePlazoDetalle:
      "Desde que se expidió esta factura han pasado más de cuatro años. La norma da ese plazo desde el devengo, que no siempre es la fecha de la factura, así que no te lo bloqueamos: confírmalo si sabes que estás en plazo.",
    fueraDePlazoConfirmar: "Lo confirmo, rectificar igualmente",
    motivosRectificar: {
      RECTIFICATION_NOT_ALLOWED_FOR_ROLE: "Tu rol no puede preparar facturas. Pídeselo a quien administra la cuenta.",
      ORIGINAL_NOT_ISSUED: "Solo se rectifica una factura emitida.",
      ORIGINAL_VOIDED: "Esta factura está anulada: no hay nada que rectificar.",
      RECTIFICATIVE_INVOICE: "Una rectificativa no se rectifica desde aquí. Escríbenos y lo vemos contigo.",
      FISCAL_REVIEW_REQUIRED: "La Agencia Tributaria no aceptó esta factura. Escríbenos: hay que corregir el envío, no rectificarla.",
      RECTIFICATION_TYPE_INVALID: "Elige qué pasó.",
      RECTIFICATION_KIND_INVALID: "Elige cómo quieres corregirla.",
      OUT_OF_TIME_CONFIRMATION_REQUIRED: "Han pasado más de cuatro años: confírmalo para seguir.",
    } as Readonly<Record<string, string>>,
    motivosAnular: {
      VOID_NOT_ALLOWED_FOR_ROLE: "Tu rol no puede anular facturas. Pídeselo a quien administra la cuenta.",
      NOT_ISSUED: "Solo se anula una factura emitida.",
      REASON_REQUIRED: "Escribe el motivo de la anulación (entre 1 y 500 caracteres).",
      REPRESENTATION_REQUIRED:
        "Autorízanos a enviar tus facturas a la Agencia Tributaria en tu nombre, en Configuración › Autorización AEAT.",
      SIF_DECLARATION_MISSING: "Estamos terminando la certificación de esta versión de Faktelio. En cuanto esté, podrás anularla.",
      SUBMISSION_PENDING: "Esta factura todavía no ha llegado a la Agencia Tributaria. En cuanto llegue, podrás anularla.",
      FISCAL_REVIEW_REQUIRED: "La Agencia Tributaria no aceptó esta factura. Escríbenos: hay que corregir el envío, no anularla.",
      RECTIFICATIVE_INVOICE: "Una factura rectificativa no se anula.",
      HAS_RECTIFICATIONS: "Esta factura tiene rectificativas. Resuélvelas primero.",
    } as Readonly<Record<string, string>>,
    errores: {
      CUSTOMER_ARCHIVED: "Este cliente está archivado. Elige otro o reactívalo.",
      DATE_INVALID: "Escribe una fecha válida.",
      TOO_LONG: "Es demasiado largo: como mucho 500 caracteres.",
    } as Readonly<Record<string, string>>,
  },
  soporte: {
    titulo: "Soporte",
    detalle: "Si no encuentras lo que buscas en la ayuda, o la duda es de tu cuenta, escríbenos.",
    escribenos: "Escríbenos a",
    // Centro de ayuda: el texto es el de faktelio.com/ayuda/ (ayuda/buscar.ts).
    ayudaTitulo: "Ayuda paso a paso",
    ayudaCargando: "Cargando la ayuda…",
    ayudaSinCargar: "No se ha podido cargar la ayuda. También la tienes en",
    ayudaEntradilla: "Todas las funciones de Faktelio, explicadas paso a paso. Busca lo que quieres hacer o abre la sección que te interese.",
    buscarEtiqueta: "¿Qué quieres hacer?",
    buscarAyuda: "Por ejemplo: rectificar una factura, logo, gestoría.",
    sinResultados: "No hay nada con esas palabras. Prueba con otras, o escríbenos.",
    resultados: (n: number) => (n === 1 ? "1 resultado." : `${n} resultados.`),
    preguntaFrecuente: "Pregunta frecuente",
    verEnLaWeb: "Ver en faktelio.com",
    ayudaWebUrl: "https://www.faktelio.com/ayuda/",
    sinEmail: "Muy pronto encontrarás aquí cómo contactar con nosotros.",
    // Email de soporte. Vacío = no se muestra ninguno (no se inventa).
    email: "soporte@faktelio.com",
  },
  // TDS-CORE Parte 4 §3.1 y OD-0020 D-02: herramientas internas del operador.
  operador: {
    titulo: "Autorizaciones por comprobar",
    cargando: "Cargando…",
    reintentar: "Reintentar",
    noEncontradoTitulo: "No se ha encontrado",
    noEncontrado: "La página que buscas no existe.",
    sinMfa: "Activa la verificación en dos pasos en tu cuenta para usar las herramientas internas.",
    detalle:
      "Empresas que dicen haber hecho el apoderamiento en la Sede. Compruébalo antes de validar: activar sin comprobarlo deja emitir facturas que la Agencia Tributaria rechazará.",
    enlaceSede: "https://sede.agenciatributaria.gob.es/Sede/colaborar-agencia-tributaria/registro-apoderamientos.html",
    enlaceSedeTexto: "Abrir el Registro de apoderamientos (con el certificado de Jominicol)",
    vacio: "No hay ninguna autorización esperando comprobación.",
    nif: (nif: string) => `NIF ${nif}`,
    declarado: (fecha: string) => `nos lo dijo el ${fecha}`,
    validar: "Validar",
    rechazar: "Rechazar",
    motivoEtiqueta: "Motivo del rechazo",
    confirmarRechazo: "Confirmar el rechazo",
    comprobarTitulo: "Antes de validar, comprueba en la Sede:",
    comprobar1: (nif: string) => `que el poderdante es ${nif};`,
    comprobar2: "que el apoderado es JOMINICOL SPAIN SL (NIF B37582483);",
    comprobar3: "que el poder es general, o el específico de Sistemas Informáticos de Facturación y VERI*FACTU;",
    comprobar4: "y que está vigente.",
    validada: (empresa: string) => `Autorización de ${empresa} validada. Ya puede emitir facturas.`,
    rechazada: (empresa: string) => `Autorización de ${empresa} rechazada. Se lo explicamos en su pantalla.`,
    errorDecision: "No se ha podido guardar la decisión. Vuelve a cargar la lista.",
    motivos: {
      NOT_FOUND: "No aparece ningún poder con ese NIF",
      GRANTOR_MISMATCH: "El poderdante no es esa empresa",
      WRONG_PROCEDURE: "El poder es de otros trámites",
      WRONG_REPRESENTATIVE: "El apoderado no es Jominicol Spain SL",
      NOT_IN_FORCE: "Existe pero no está vigente",
      OTHER: "Otro motivo",
    },
  },
  // TDS-CORE Parte 4 §5 y OD-0031: la autorización para remitir a la AEAT.
  /**
   * La puerta del alta (OD-0039). Textos propios sólo para lo que la puerta
   * añade: los pasos, el enlace y el apoderado salen de `autorizacion`, que
   * es la misma fuente que la pantalla de Configuración.
   */
  /**
   * La guia del apoderamiento (`OD-0039` E-10). Se abre en otra pestana desde
   * la puerta, en /como-autorizar.
   *
   * El tramite se identifica por su DESCRIPCION y no por su codigo: los
   * codigos son de la AEAT y pueden cambiar sin avisar; lo que dicen, no.
   */
  comoAutorizar: {
    titulo: "Cómo autorizarnos en la Agencia Tributaria",
    entradilla:
      "Es gratis, se hace una vez y tarda un par de minutos. Necesitas Cl@ve, DNI electrónico o un certificado digital. Ten esta pestaña a mano: te va diciendo qué poner en cada pantalla.",
    datosTitulo: "Los datos que vas a necesitar",
    paso1Titulo: "1. Abre el alta de poder",
    paso1:
      "Se hace en el Registro de apoderamientos de la Sede electrónica, en «Alta de poder mediante identificación electrónica».",
    abrirSede: "Abrir el alta de poder en la Agencia Tributaria",
    paso2Titulo: "2. Identifícate",
    paso2: "Con tu Cl@ve, tu DNI electrónico o tu certificado digital. Entra la persona titular de la empresa.",
    paso3Titulo: "3. Rellena los datos del poder",
    paso3:
      "En «NIF del Apoderado» pon el nuestro. La fecha de fin la eliges tú: la Agencia Tributaria admite hasta cinco años. Y en «Apoderamiento por absorbente» marca No, salvo que te lo hayan pedido por una fusión.",
    fechaEjemplo: "27/09/2031",
    /**
     * Las etiquetas de la Sede que aparecen en los dibujos. Van aqui como todo
     * lo visible (`OD-0017 D-07`), y ademas asi, el dia que la AEAT renombre un
     * campo, se cambia en un sitio.
     */
    campos: {
      nifApoderado: "NIF del Apoderado",
      fechaFin: "Fecha fin de la autorización",
      absorbente: "Apoderamiento por absorbente",
      no: "No",
      si: "Sí",
      busqueda: "Búsqueda por nombre de trámite",
      terminoBusqueda: "FACTU",
      buscar: "Buscar",
      nif: "NIF",
    },
    /**
     * Lo que sale al buscar FACTU. Se marcan los CUATRO (decisión del
     * propietario, 2026-09-28): son el bloque de Sistemas Informáticos de
     * Facturación, y así el poder queda dado de una vez.
     *
     * El IMPRESCINDIBLE es el primero: sin `IZ860` no podemos remitir nada,
     * por bien que estén los otros tres.
     */
    tramites: [
      { codigo: "IZ860", texto: "Remisión y consulta de registros de facturación por servicio web", marcar: true },
      { codigo: "IZ861", texto: "Consulta de registros de facturación", marcar: true },
      { codigo: "IZ862", texto: "Emisión de facturas mediante aplicación de facturación de la AEAT", marcar: true },
      { codigo: "IZ863", texto: "Consulta de facturas emitidas por la aplicación de facturación de la AEAT", marcar: true },
    ] as ReadonlyArray<{ readonly codigo: string; readonly texto: string; readonly marcar: boolean }>,
    dibujoDatosAlt: "Dibujo del formulario: NIF del apoderado, fecha fin y apoderamiento por absorbente.",
    dibujoDatosNota: "Dibujo orientativo de la pantalla de la Sede.",
    paso4Titulo: "4. Elige el trámite: el de servicio web",
    paso4:
      "En «Búsqueda por nombre de trámite» escribe FACTU y pulsa Buscar. Deja los desplegables de procedimiento en blanco. En la lista salen los trámites de facturación: marca las casillas de la izquierda de los cuatro.",
    paso4Aceptar:
      "Y pulsa «Aceptar», arriba a la izquierda de la tabla. Ese botón es el que los fija en el documento: si no lo pulsas, las casillas se pierden.",
    dibujoTramitesAlt: "Dibujo de la búsqueda de trámites, con las cuatro casillas marcadas y el botón Aceptar.",
    aceptar: "Aceptar",
    ojoTitulo: "El que no puede faltar es el primero",
    ojoDetalle:
      "IZ860, el de REMISIÓN por servicio web, es el que usamos para enviar tus facturas: sin él, el poder queda dado y aun así fallarían todas. Los otros tres son del mismo bloque y se marcan a la vez. Si los códigos no te cuadran, fíjate en la descripción y no en el número: los códigos son de la Agencia Tributaria y puede cambiarlos.",
    paso5Titulo: "5. Firma y envía",
    paso5: "Revisa los datos, marca la casilla de conformidad y pulsa «Firmar y Enviar». La Sede te da un justificante con su CSV.",
    despuesTitulo: "Y después, ¿qué?",
    despues:
      "Dínoslo aquí mismo, sin volver atrás. Nosotros aceptamos el poder por nuestra parte y lo comprobamos en la Sede; en cuanto esté, podrás emitir.",
    volver: "Volver a Faktelio",
    hechoTitulo: "Anotado",
    hechoDetalle:
      "Lo comprobamos en la Sede y te avisamos en cuanto esté. Ya puedes cerrar esta pestaña; al volver a Faktelio verás el estado actualizado.",
    yaAnotadaTitulo: "Ya nos consta",
    yaAnotadaDetalle:
      "Lo estamos comprobando en la Sede. En cuanto la Agencia Tributaria nos dé el poder por bueno, podrás emitir.",
    yaActivaTitulo: "Ya está autorizado",
    yaActivaDetalle: "La Agencia Tributaria ya nos tiene apoderados para enviar tus facturas. No hay que hacer nada más.",
    faltanDatosTitulo: "Primero, los datos de tu empresa",
    faltanDatosDetalle:
      "Sin la razón social, el NIF y el domicilio fiscal no podemos anotar la autorización. Entra en Faktelio y rellénalos: es el paso 3.",
    sinSesionTitulo: "Entra primero en Faktelio",
    sinSesionDetalle:
      "Para anotarlo hace falta tu sesión, y aquí no la tenemos. Entra en Faktelio y pulsa «Ya lo he hecho»: es este mismo botón.",
    noSePudoTitulo: "No hemos podido anotarlo desde aquí",
    noSePudoDetalle:
      "Puede que ya estuviera anotada, o que sea cosa de un momento. Vuelve a Faktelio y mira en qué punto está tu autorización: si no consta, inténtalo desde allí.",
    pie: "Los dibujos son orientativos: las pantallas de la Sede pueden cambiar. Lo que no cambia es qué trámite hace falta.",
  },
  /**
   * El asistente del alta: tres pantallas seguidas, a pantalla completa
   * (`OD-0039` E-13, propuesta del 2026-10-20). El orden es el de las
   * dependencias: sin datos fiscales no hay a quién representar, y el segundo
   * factor no depende de nada, así que va el último.
   */
  puerta: {
    etiqueta: "Configura tu cuenta",
    titulo: "Tres pasos antes de empezar",
    cerrar: "Cerrar",
    /** El texto del botón de salida de la cabecera. El aspa sola se ve poco. */
    seguirLuego: "Seguir más tarde",
    /**
     * Lo que se lee al cerrar. Importa que diga las dos cosas: que se puede
     * seguir trabajando y que esto no ha quedado hecho por cerrarlo.
     */
    cerrarAyuda: "Puedes cerrar esto y seguir trabajando. Hasta que lo termines no podrás emitir facturas.",
    /** Hallazgo 8: lo que prometía el alta, recordado donde se decide. */
    antesUnPresupuesto: "¿Prefieres probar antes? Pulsa «Seguir más tarde» y prepara tu primer presupuesto: esto te espera en Inicio.",
    pasoDeTotal: (n: number, total: number) => `Paso ${n} de ${total}`,
    // Los nombres cortos del indicador de arriba, en el orden del recorrido.
    nombrePasoDatos: "Datos fiscales",
    nombrePasoAutorizacion: "Autorización",
    nombrePasoMfa: "Verificación en dos pasos",
    pendiente: "Pendiente",
    enCurso: "En curso",
    hecho: "Hecho",
    /**
     * E-14: lo que está hecho por tu parte y todavía no te deja facturar. Va
     * en ámbar y con el reloj, nunca con el ✓ de «Hecho».
     */
    faltaConfirmar: "Guardado, falta confirmar",
    faltaSii: "Falta la pregunta del SII",
    conSii: "Llevas el SII",
    enComprobacion: "En comprobación",
    atras: "Atrás",
    continuar: "Continuar",
    tarda: (minutos: string) => `Tarda ${minutos}.`,

    // 1) Los datos fiscales.
    fiscalTitulo: "Los datos fiscales de tu empresa",
    fiscalEntradilla:
      "Empezamos por quién factura. Son los datos que saldrán como emisor en cada factura, y los que necesita la Agencia Tributaria para saber quién nos autoriza en el paso siguiente.",
    fiscalTarda: "un minuto",
    fiscalGuardar: "Guardar y continuar",
    fiscalGuardando: "Guardando…",
    fiscalResto:
      "La forma de pago, el IBAN, el pie de factura o el logotipo no hacen falta ahora: los completas cuando quieras en Configuración.",
    fiscalVerTodo: "Completar el resto de datos ahora",
    fiscalPorQueTitulo: "Por qué empezamos por aquí",
    fiscalPorQue: [
      "Sin estos datos no hay a quién representar: la autorización del paso 2 la da tu NIF, así que tiene que estar antes.",
      "Cada factura emitida guarda una copia congelada de estos datos. Si después los cambias, las facturas que ya emitiste no cambian.",
    ],
    fiscalALaManoTitulo: "Tenlo a mano",
    fiscalALaMano: [
      "Tu NIF, o el de tu empresa, tal como figura en Hacienda.",
      "La dirección de tu domicilio fiscal. Si dudas, viene en tu tarjeta de identificación fiscal o en el certificado de situación censal.",
    ],

    // 2) El apoderamiento.
    autorizacionTitulo: "Autorízanos ante la Agencia Tributaria",
    /**
     * Partida en tres porque GRATIS va en negrita y en mayúscula: es lo
     * primero que se pregunta todo el mundo, y leerlo de un vistazo evita la
     * duda de si esto se paga.
     */
    entradillaAntes:
      "Para enviar tus facturas a la Agencia Tributaria en tu nombre necesitamos que nos autorices en su Sede. Es ",
    entradillaGratis: "GRATIS",
    entradillaDespues: ", se hace una vez y tarda un par de minutos.",
    porQue:
      "No hay atajo: la Agencia Tributaria no admite que esto se acepte con una casilla. Tiene que autorizarlo la persona titular, identificándose en su Sede.",
    autorizacionTarda: "un par de minutos",
    recomendada: "Recomendada",
    autorizarAhora: "Empezar la autorización",
    opcionSedeTitulo: "Autorízanos en la Sede",
    opcionSedeDetalle:
      "Entras una vez en la Agencia Tributaria con Cl@ve, DNI electrónico o certificado, y nos apoderas. Nosotros enviamos tus facturas con nuestro certificado: el tuyo no sale de tu ordenador.",
    opcionCertificadoTitulo: "Con mi propio certificado digital",
    opcionCertificadoDetalle:
      "Subirnos tu certificado para que enviemos con él. No lo ofrecemos: tu certificado sirve para firmar ante cualquier administración, no sólo para facturar, y preferimos no custodiarlo. Con la opción de al lado no hace falta.",
    opcionCertificadoEstado: "No disponible",
    volverAEmpezar: "Volver a empezar",
    /**
     * El apoderamiento puede esperar a la gestoría; el segundo factor no
     * depende de él. Que el paso 2 a medias no tape el 3.
     */
    autorizacionLuego: "Seguir con el paso 3 mientras tanto",
    queEsTitulo: "Qué es el apoderamiento",
    queEs:
      "Un trámite oficial y gratuito de la Agencia Tributaria. Entras en su Sede con tu Cl@ve, DNI electrónico o certificado —desde tu ordenador, sin entregárselo a nadie— y nos das permiso para enviar en tu nombre los registros de tus facturas. Queda anotado en su Registro de apoderamientos, y lo retiras allí cuando quieras.",
    /**
     * Lo que pidió el propietario el 2026-10-20: decir en voz alta por qué no
     * pedimos el certificado, que es lo que hacen otros. Es `OD-0039` D-04
     * contado para el cliente, no una decisión nueva.
     */
    certificadoTitulo: "Por qué no te pedimos tu certificado digital",
    certificadoEntradilla:
      "Otros programas de facturación te piden que les subas tu certificado digital o tu firma electrónica para enviar las facturas con ella. Nosotros no, y es a propósito.",
    certificadoRiesgo:
      "Tu certificado es tu identidad ante toda la Administración: con él se presentan impuestos, se firman documentos y se hacen trámites de todo tipo. Quien lo guarda puede actuar en tu nombre en todo eso, y si se filtra, el problema es tuyo.",
    comparativaAspecto: "Qué cambia",
    comparativaCertificado: "Subir tu certificado",
    comparativaApoderamiento: "Apoderamiento",
    comparativa: [
      {
        aspecto: "Qué entregas",
        certificado: "Tu certificado y su contraseña",
        apoderamiento: "Nada: el permiso lo das tú en la Sede",
      },
      {
        aspecto: "Para qué sirve",
        certificado: "Para cualquier trámite ante cualquier administración",
        apoderamiento: "Sólo para los trámites de facturación",
      },
      {
        aspecto: "Si hubiera una filtración",
        certificado: "Alguien podría firmar en tu nombre",
        apoderamiento: "No hay ningún certificado tuyo que robar",
      },
      {
        aspecto: "Para retirarlo",
        certificado: "Revocar el certificado y pedir otro",
        apoderamiento: "Lo retiras en la Sede cuando quieras",
      },
    ],
    certificadoCierre:
      "Con el apoderamiento enviamos tus facturas con nuestro propio certificado, y queda escrito en el registro oficial de la Agencia Tributaria quién actúa en tu nombre y para qué.",
    rechazadaTitulo: "No pudimos confirmarla",
    revocada: "Retiraste la autorización. Para volver a emitir facturas hay que autorizarnos otra vez.",
    nifCambiado: "Has cambiado el NIF de tu empresa. La autorización la dio el NIF anterior, así que hay que darla de nuevo.",
    // Al volver de la Sede, lo primero es la pregunta, no releer los pasos.
    yaEstaTitulo: "¿Ya lo has hecho?",
    yaEstaDetalle: "Si ya nos has apoderado en la Sede, dínoslo y lo comprobamos.",
    verPasos: "Ver los pasos otra vez",
    // Copiar los datos del apoderado: el error más común es teclear mal el NIF.
    datosTitulo: "Datos del apoderado",
    copiar: "Copiar",
    copiado: "Copiado",
    rutaComoAutorizar: "/como-autorizar",
    // Mandar los pasos por correo.
    enviarTitulo: "¿Prefieres hacerlo en otro momento o que lo haga tu gestoría?",
    enviarDetalle:
      "Te mandamos los cuatro pasos y los datos del apoderado por correo. Así lo haces desde el móvil, donde sueles tener la Cl@ve, o se lo reenvías a quien te lleva los impuestos.",
    destinoYo: "Enviármelo a mí",
    destinoGestoria: "Enviarlo a mi gestoría",
    correoEtiqueta: "Correo",
    correoAyuda: "No guardamos esta dirección: se usa para enviar el correo y se olvida.",
    enviar: "Enviar los pasos",
    enviando: "Enviando…",
    enviado: "Enviado. Revisa la bandeja de entrada (y la carpeta de spam).",
    yaEnviadoHoy: "Ya lo enviamos hoy a esa dirección. Mañana se puede volver a enviar.",
    sinClaveTitulo: "No tengo Cl@ve ni certificado",
    sinClaveDetalle:
      "Cl@ve se consigue gratis: puedes registrarte por internet con tu DNI y un dato de tu declaración de la renta, o presencialmente en una oficina. También sirve el DNI electrónico o cualquier certificado digital.",
    // La ENTRADA directa al registro por internet, elegida por el propietario
    // el 2026-09-28 y comprobada ese dia: identificarse con DNI/NIE y datos de
    // contraste, y de ahi a la pasarela de registro en Cl@ve. La anterior
    // (/Sede/todas-gestiones/.../registro-cl@ve.html) daba 404.
    //
    // El texto dice lo que se va a encontrar —un formulario que pide el DNI—,
    // no "como registrarse", que suena a pagina de ayuda y no lo es.
    sinClaveEnlace:
      "https://www2.agenciatributaria.gob.es/wlpl/BUCV-JDIT/AutenticaDniNieContrasteh?ref=%2Fwlpl%2FBUCV-JDIT%2FPasarelaC31h",
    sinClaveEnlaceTexto: "Registrarme en Cl@ve con mi DNI (Agencia Tributaria)",

    // 3) El segundo factor.
    mfaTitulo: "Activa la verificación en dos pasos",
    mfaDetalle:
      "Es un código que cambia cada 30 segundos en tu móvil. Se activa una vez y tarda un minuto: lo pedimos porque desde esta cuenta se emiten facturas con validez fiscal.",
    mfaTarda: "un minuto",
    mfaPorQueTitulo: "Por qué la pedimos",
    mfaPorQue: "Si alguien consiguiera tu contraseña, sin tu móvil no podría emitir ni una factura en tu nombre.",
    appTitulo: "Necesitas una app de autenticación",
    appDetalle:
      "Es gratis y pesa poco. Genera el código de 6 cifras que te pediremos al entrar. Si ya tienes una —Google Authenticator, Microsoft Authenticator u otra—, sirve igual.",
    recuperacionTitulo: "Ten papel a mano",
    recuperacion:
      "Al activarla te damos unos códigos de recuperación de un solo uso, para entrar si pierdes el móvil. Sólo se ven una vez: apúntalos antes de seguir.",

    // Al terminar los tres.
    listoTitulo: "Todo listo por tu parte",
    listoDetalle: "Has completado los tres pasos. Esto es lo que pasa ahora:",
    listoIrAlPanel: "Ir al panel",

    // E-14: el SII, en el paso 1, con salida clara si la respuesta es «Sí».
    siiBloque: "El SII",
    siiAvisoTitulo: "Con el SII, Faktelio no puede emitir tus facturas",
    siiAviso:
      "La ley obliga a quien lleva los libros en el SII a usar un sistema del SII, y Faktelio no lo es. Si guardas «Sí», podrás seguir usando presupuestos y clientes, pero no emitir facturas. Cambiarlo después solo se puede escribiéndonos a soporte.",
    siiTitulo: "Faktelio no puede emitir tus facturas",
    siiDetalle:
      "Has indicado que llevas los libros de IVA en el SII. Quien está en el SII tiene que usar un sistema del SII, y Faktelio no lo es, así que no te pedimos la autorización ni el resto de pasos: no te servirían para facturar.",
    siiSigue: [
      "Puedes seguir usando Faktelio para presupuestos, clientes y productos.",
      "No podrás emitir facturas desde Faktelio mientras estés en el SII.",
    ],

    // E-14: confirmar los datos fiscales DENTRO del asistente.
    confirmarTitulo: "Confirma tus datos fiscales",
    confirmarDetalle:
      "Es lo último. Saldrán tal cual como emisor en cada factura y quedan congelados en las que emitas, así que revísalos con calma.",
    confirmarCorregir: "Corregir los datos",
    confirmarAlFinal:
      "Confirmarás estos datos al final, después de activar la verificación en dos pasos: hasta entonces quedan guardados, pero no se puede emitir con ellos.",
    confirmarSinMfa: "Para confirmar los datos hace falta la verificación en dos pasos. Actívala en el paso 3 y vuelve aquí.",
  },
  autorizacion: {
    titulo: "Autorización AEAT",
    cargando: "Cargando…",
    reintentar: "Reintentar",
    paraQue:
      "Para enviar tus facturas a la Agencia Tributaria en tu nombre necesitamos que nos autorices. Es un trámite que haces una vez.",
    soloPropietario: "Solo la persona propietaria de la cuenta puede autorizar o retirar esto.",
    comoTitulo: "Cómo autorizarnos",
    ruta1Titulo: "Con tu certificado digital, DNI electrónico o Cl@ve",
    gratis: "Gratis",
    ruta1Detalle: "Lo haces tú en la Sede de la Agencia Tributaria. Te explicamos los pasos.",
    empezar: "Empezar",
    ruta3Titulo: "Firma online desde el móvil",
    ruta3Precio: "2,99 € + IVA",
    ruta3Detalle: "Sin certificado ni Cl@ve: firmas desde el móvil y nosotros hacemos el trámite.",
    masAdelante: "Disponible más adelante",
    pasosTitulo: "Pasos en la Sede de la Agencia Tributaria",
    paso1: "Entra en el Registro de apoderamientos con tu certificado, DNI electrónico o Cl@ve.",
    paso2:
      'Elige "Alta de poder para trámites tributarios específicos", escribe FACTU en «Búsqueda por nombre de trámite» y marca los CUATRO trámites de facturación que salen. Cuáles son exactamente, en la guía.',
    paso3: (apoderado: string) => `Como apoderado, pon ${apoderado}.`,
    paso4: "Confirma el trámite y vuelve aquí.",
    // La guia de /como-autorizar, que es donde estan los CUATRO tramites con
    // su codigo y el dibujo de cada pantalla de la Sede. Esta pestaña no los
    // repite a proposito: el mismo texto en dos sitios puede divergir, y el
    // que se quedara viejo seria el que nadie mira.
    guiaTexto: "Ver la guía, con los cuatro trámites y un dibujo de cada pantalla",
    // Enlace DIRECTO al alta de poder por identificacion electronica
    // (comprobado en la Sede el 2026-09-27). Aterrizar en el sitio exacto es
    // media friccion menos (OD-0039).
    enlaceSede:
      "https://sede.agenciatributaria.gob.es/static_files/common/html/selector_acceso/SelectorAccesos.html?ref=%2Fwlpl%2FITTI-APOD%2FEspecGenServlet&aut=CPE",
    enlaceSedeTexto: "Abrir el alta de poder en la Agencia Tributaria",
    avisoPoder: "También vale un poder general, si ya nos lo has dado.",
    yaLoHeHecho: "Ya lo he hecho",
    comprobandoTitulo: "Lo estamos comprobando",
    comprobando:
      "Comprobamos tu autorización en la Sede de la Agencia Tributaria. Normalmente antes del final del siguiente día laborable. Te avisamos en cuanto esté.",
    activaTitulo: "Todo listo",
    activa: (fecha: string) => `Comprobamos tu autorización el ${fecha}. Ya puedes emitir facturas.`,
    retirarAviso:
      "Si retiras la autorización dejarás de poder emitir facturas. Recuerda revocarla también en la Sede de la Agencia Tributaria.",
    retirar: "Retirar la autorización",
    retirada: "Retiraste la autorización. Las facturas que ya emitiste siguen siendo válidas.",
    nifCambiado:
      "Has cambiado el NIF de tu empresa. La autorización la dio el NIF anterior, así que hay que volver a autorizarnos.",
    rechazadaTitulo: "No hemos podido confirmarla",
    motivos: {
      NOT_FOUND: "No encontramos ningún poder a nuestro nombre con tu NIF. Revisa que el trámite se completara.",
      GRANTOR_MISMATCH: "El poder lo dio un NIF distinto al de tu empresa.",
      WRONG_PROCEDURE: "El poder es de otros trámites y no incluye el envío de registros VERI*FACTU.",
      WRONG_REPRESENTATIVE: "El apoderado que consta no es Jominicol Spain SL.",
      NOT_IN_FORCE: "El poder existe pero no está vigente.",
      OTHER: "No hemos podido confirmar la autorización. Escríbenos a soporte y lo vemos contigo.",
    } as Readonly<Record<string, string>>,
  },
  // Q-04 (RRSIF art. 13): la declaración responsable, visible en el sistema.
  acercaDe: {
    titulo: "Acerca de Faktelio",
    cargando: "Cargando…",
    errorCarga: "No hemos podido cargar estos datos. Inténtalo de nuevo en un momento.",
    sistema: "Sistema de facturación",
    version: "Versión",
    sinVersion: "sin configurar",
    productor: "Fabricante",
    declaracionTitulo: "Declaración responsable",
    verDeclaracion: (version: string) => `Declaración responsable de la versión ${version} (PDF)`,
    firmada: (fecha: string, lugar: string) => `firmada en ${lugar} el ${fecha}`,
    sinDeclaracion:
      "La declaración responsable de esta versión todavía no está firmada. Hasta que lo esté, Faktelio no emite facturas VERI*FACTU.",
    anterioresTitulo: "Todas las versiones",
    enlacePublico: "Declaración responsable",
  },
  /*
    OD-0057 (ACCEPTED 2026-10-24): importar desde Excel. Mínima en Free
    (clientes y productos, con la plantilla), avanzada en Factura+ y Business.
  */
  importar: {
    titulo: "Importar datos",
    pestana: "Importar datos",
    detalle: "Trae tus clientes, productos y facturas de otro programa desde un Excel o un CSV, sin escribirlos uno a uno.",
    tipos: {
      CUSTOMERS: "Clientes",
      PRODUCTS: "Productos",
      SUPPLIERS: "Proveedores",
      EXTERNAL_INVOICES: "Facturas de otro programa",
    },
    tiposSingular: {
      CUSTOMERS: "clientes",
      PRODUCTS: "productos",
      SUPPLIERS: "proveedores",
      EXTERNAL_INVOICES: "facturas",
    },
    queImportar: "¿Qué quieres importar?",
    minima: (filas: number, alMes: number) =>
      `Con tu plan puedes importar clientes y productos usando nuestra plantilla: hasta ${filas} filas cada vez y ${alMes} importaciones al mes.`,
    avanzada: (filas: number) =>
      `Puedes importar el Excel que saca tu programa anterior, hasta ${miles(filas)} filas cada vez. Si algo sale mal, la importación se puede deshacer.`,
    usadas: (usadas: number, alMes: number, vuelve: string) => `Este mes llevas ${usadas} de ${alMes}. Se renueva el ${vuelve}.`,
    sinCupo: (vuelve: string) => `Ya has hecho las importaciones de este mes. Podrás importar otra vez el ${vuelve}.`,
    masEnFacturaPlus: "Para importar el Excel de otro programa, proveedores o facturas antiguas, y poder deshacer, pasa a Factura+ o Business.",
    verPlanes: "Ver planes",
    plantilla: "Descargar la plantilla",
    plantillaAyuda: "Rellena la plantilla en Excel y súbela. La primera fila son los nombres de las columnas: no los cambies.",
    elegirFichero: "Elegir el fichero (.xlsx o .csv)",
    fichero: "Fichero",
    leyendo: "Leyendo el fichero…",
    errorLectura: {
      FORMATO_NO_ADMITIDO: "Ese fichero no es un Excel (.xlsx) ni un CSV. Si es un .xls antiguo, ábrelo en Excel y guárdalo como .xlsx.",
      FICHERO_ILEGIBLE: "No hemos podido leer el fichero. Ábrelo en Excel, guárdalo otra vez como .xlsx y vuelve a probar.",
      SIN_FILAS: "El fichero no tiene filas con datos debajo de la cabecera.",
    },
    demasiadoGrande: (mb: number) => `El fichero pasa de ${mb} MB. Divídelo en varios más pequeños.`,
    demasiadasFilas: (filas: number, max: number) => `El fichero tiene ${filas} filas y tu plan importa hasta ${max} de una vez. Divídelo en varios ficheros.`,
    filasLeidas: (n: number) => (n === 1 ? "1 fila con datos" : `${miles(n)} filas con datos`),
    emparejarTitulo: "Columnas",
    emparejarAyuda: "Hemos reconocido las columnas que hemos podido. Revisa las demás: elige qué dato de Faktelio es cada una, o «No importar».",
    columnaDelExcel: "Columna del Excel",
    campoFaktelio: "Dato en Faktelio",
    noImportar: "No importar",
    ejemplo: (v: string) => `Ejemplo: ${v}`,
    faltanColumnas: (lista: string) => `Faltan columnas obligatorias: ${lista}.`,
    faltanEnPlantilla: (lista: string) =>
      `Al fichero le faltan columnas de la plantilla: ${lista}. Descarga la plantilla y copia tus datos en ella.`,
    comprobar: "Comprobar el fichero",
    comprobando: (hechas: number, total: number) => `Comprobando… ${hechas} de ${total}`,
    resumenTitulo: "Antes de importar",
    // En facturas se cuentan FACTURAS (varias filas pueden ser una); en lo demás, filas.
    resumen: {
      bien: (n: number, facturas: boolean) =>
        n === 1 ? `1 ${facturas ? "factura está bien y se importará" : "fila está bien y se importará"}.` : `${miles(n)} ${facturas ? "facturas" : "filas"} están bien y se importarán.`,
      duplicadas: (n: number) => (n === 1 ? "1 ya existía y se saltará." : `${miles(n)} ya existían y se saltarán.`),
      errores: (n: number) => (n === 1 ? "1 fila tiene errores y no se importará." : `${miles(n)} filas tienen errores y no se importarán.`),
      noCaben: (n: number) => `Tu plan admite ${n} clientes más: las filas que pasen de ahí no se importarán.`,
    },
    erroresTitulo: "Filas con errores",
    masErrores: (n: number) => `Y ${n} más: descárgalas para verlas todas.`,
    fila: (n: number) => `Fila ${n}`,
    motivo: (campo: string, error: string) => `${campo}: ${error}`,
    avisoFacturas:
      "Las facturas que importes son una copia para consultar. Ya las registró y envió tu programa anterior: Faktelio no las envía a la AEAT, no les pone código QR y no forman parte de tus registros VERI*FACTU. Guarda también las originales: son las que valen ante Hacienda. Y desde ahora factura solo con Faktelio: no vuelvas a facturar con el programa anterior.",
    entendido: "Lo entiendo: son una copia para consultar",
    importar: (n: number, facturas: boolean) => (n === 1 ? `Importar 1 ${facturas ? "factura" : "fila"}` : `Importar ${miles(n)} ${facturas ? "facturas" : "filas"}`),
    importando: (hechas: number, total: number) => `Importando… ${hechas} de ${total}`,
    noCierres: "No cierres esta página hasta que termine.",
    hechoTitulo: "Importación terminada",
    hecho: {
      creadas: (n: number) => (n === 1 ? "1 creada" : `${miles(n)} creadas`),
      duplicadas: (n: number) => (n === 1 ? "1 ya existía" : `${miles(n)} ya existían`),
      rechazadas: (n: number) => (n === 1 ? "1 fila con errores" : `${miles(n)} filas con errores`),
    },
    descargarErrores: "Descargar las filas con errores",
    columnaMotivo: "Motivo",
    otra: "Importar otro fichero",
    cancelar: "Cancelar",
    volver: "Volver",
    error: "No se ha podido completar. Vuelve a intentarlo.",
    errorAMedias: (hechas: number) =>
      `Se ha cortado después de ${hechas} filas. Vuelve a pulsar «Importar»: lo que ya entró no se duplica.`,
    historialTitulo: "Importaciones anteriores",
    historialVacio: "Todavía no has importado nada.",
    estados: { OPEN: "Sin terminar", DONE: "Terminada", UNDONE: "Deshecha" },
    lineaHistorial: (tipo: string, fecha: string, creadas: number) => `${tipo} · ${fecha} · ${creadas} creadas`,
    deshacer: "Deshacer",
    deshaciendo: "Deshaciendo…",
    confirmarDeshacer:
      "Se quitará todo lo que creó esta importación. Lo que ya hayas usado en un presupuesto o una factura se queda, y los proveedores se archivan. ¿Seguimos?",
    deshecha: (quitadas: number, seQuedan: number) =>
      seQuedan === 0 ? `Deshecha: se han quitado ${quitadas}.` : `Deshecha: se han quitado ${quitadas}; ${seQuedan} se quedan porque ya se están usando.`,
    campos: {
      name: "Nombre",
      displayName: "Nombre",
      legalName: "Razón social",
      taxId: "NIF",
      taxIdValue: "NIF",
      taxIdType: "Tipo de NIF",
      country: "País",
      addressLine: "Dirección",
      postalCode: "Código postal",
      city: "Población",
      province: "Provincia",
      email: "Email",
      phone: "Teléfono",
      notes: "Notas",
      paymentTermsDays: "Plazo de pago (días)",
      code: "Referencia",
      description: "Descripción",
      unitPrice: "Precio",
      vatRate: "IVA %",
      vatTreatment: "IVA",
      number: "Número",
      issueDate: "Fecha",
      operationDate: "Fecha de operación",
      kind: "Tipo",
      customerName: "Cliente",
      customerTaxId: "NIF cliente",
      sourceProgram: "Programa de origen",
      base: "Base",
      vatAmount: "Cuota IVA",
      surchargeRate: "Recargo %",
      surchargeAmount: "Cuota recargo",
      withholdingAmount: "Retención",
      total: "Total",
      lines: "Líneas",
      _row: "La fila",
    } as Readonly<Record<string, string>>,
    errores: {
      REQUIRED: "falta",
      INVALID_NUMBER: "no es un número",
      TOO_MANY_DECIMALS: "tiene demasiados decimales",
      OUT_OF_RANGE: "está fuera de lo que se admite",
      INVALID_DATE: "no es una fecha (usa día/mes/año)",
      FUTURE_DATE: "es una fecha futura",
      FAKTELIO_NUMBER_SHAPE: "tiene la forma de los números de Faktelio (F2026-000001); cámbialo para que no se confunda con una factura emitida aquí",
      DOES_NOT_MATCH: "no cuadra con la base y los tipos",
      NEGATIVE_NOT_ALLOWED: "no puede ser negativo salvo en una rectificativa",
      INVALID_OPTION: "no es una opción válida",
      TOO_LONG: "es demasiado largo",
      FORMAT_INVALID: "no tiene un formato válido",
      INVALID_TAX_ID: "no es un NIF válido",
      INVALID_CHARACTERS: "tiene caracteres que no se admiten",
      TOO_MANY_LINES: "tiene más de 50 tipos de IVA distintos",
      QUOTA_EXCEEDED: "no cabe en tu plan",
      DUPLICATE_CODE: "esa referencia ya la tiene otro producto",
      EMAIL_INVALID: "no es un email válido",
      INVALID_EMAIL: "no es un email válido",
      PHONE_INVALID: "no es un teléfono válido",
      COUNTRY_INVALID: "no es un país válido (usa el código de dos letras, como ES)",
      POSTAL_CODE_INVALID: "no es un código postal español (5 cifras, de 01000 a 52999)",
      NEGATIVE: "no puede ser negativo",
      VAT_RATE_NOT_IN_CATALOG: "no es un tipo de IVA vigente",
      TOO_MANY: "son demasiados",
    } as Readonly<Record<string, string>>,
    errorGenerico: "no es válido",
  },
  facturasImportadas: {
    titulo: "Facturas de otro programa",
    boton: "Facturas de otro programa",
    detalle: "Copia de consulta de las facturas que emitiste con tu programa anterior. No se envían a la AEAT ni se pueden modificar.",
    volver: "Volver a facturas",
    buscar: "Buscar por número, cliente o programa",
    vacio: "No hay facturas importadas.",
    total: (n: number, total: string) => `${n === 1 ? "1 factura" : `${miles(n)} facturas`} · ${total} €`,
    importe: (total: string) => `${total} €`,
    numero: "Número",
    fecha: "Fecha",
    cliente: "Cliente",
    programa: "Programa",
    totalFactura: "Total",
    sinCliente: "Sin cliente",
    otroPrograma: "Otro programa",
    tipos: { ORDINARY: "Ordinaria", CORRECTIVE: "Rectificativa", SIMPLIFIED: "Simplificada" } as Readonly<Record<string, string>>,
    fichaVolver: "Volver a la lista",
    copia: "Copia de consulta: la registró y envió tu programa anterior.",
    base: "Base",
    iva: "IVA",
    tipoIva: "IVA %",
    cuota: "Cuota",
    recargo: "Recargo",
    retencion: "Retención",
    nif: "NIF",
    fechaOperacion: "Fecha de operación",
    importadaEl: (f: string) => `Importada el ${f}`,
    enCliente: "Facturas de otro programa",
    error: "No se han podido cargar las facturas importadas.",
  },
} as const;
